import React, { useEffect, useState, useMemo, useRef } from 'react';
import PaymentPage from './steps/PaymentPage';
import { useLocation, useNavigate } from 'react-router-dom';
import styled, { ThemeProvider } from 'styled-components';
import { theme } from '../../../styles/theme';
// Removed FontAwesome icons - using simple text/HTML elements instead

import { useBooking, BOOKING_ACTIONS } from '../../../contexts/BookingContext';
import { venposService } from '../../../services/venposService';
import { sendErrorNotification, logErrorDetails } from '../../../services/errorNotificationService';
import { 
  monitorPaymentTakenNoBooking, 
  monitorBookingCreatedNoPayment,
  monitorApiEndpointFailure,
  monitorVenposApiError,
  checkMaintenanceModeStatus
} from '../../../services/errorMonitoringService';
import { markBookingSystemDeprecated } from '../../../services/systemStatusService';
import { getPageConfigByPageId } from '../../../services/bookingPageConfigService';
import {
  filterTimeSlotsForPinnedVisit,
  isReptileDayHiddenTimeSlot,
  isToddlerRestrictedDate,
  isToddlerSpecialUpsell,
  parseVisitDateParam,
  visitDateFromYmdKey,
} from '../../../utils/visitDateUtils';
import { castleAndGardenPaidQtyForFreeAnimalParkPromotion } from '../../../utils/animalParkUpsell';
import { ticketNameQualifiesForCircusBundleDiscount } from '../../../utils/circusBundleDiscount';
import { shouldShowCarRegistration, normaliseVRM } from '../../../utils/membershipCarRegistration';
import { isChoirFreeAdultTicket } from './choirFreeAdultTicket';
import { trackPurchase } from '../../../utils/metaPixel';
import { trackConversion } from '../../../utils/firstPartyTracker';
import config from '../../../config/environment';
import Footer from '../../Footer/Footer';
import ParallaxBanner from '../../common/Parralax-banner';

// Import step components
import TicketSelection from './steps/TicketSelection';
import DateSelection from './steps/DateSelection';
import UpsellStep from './steps/UpsellStep';
import CustomerDetails from './steps/CustomerDetails';
import PaymentDetails from './steps/PaymentDetails';
import BookingConfirmation from './steps/BookingConfirmation';
import MembershipConfirmation from './steps/MembershipConfirmation';
import QueueScreen from './steps/QueueScreen';
import {
  isAdultProductCode,
  isChildProductCode,
  isUpgradeEligibleLineItem,
  partitionUpgradeItemsForMembershipPage,
  resolveUpgradeMembershipPageId,
  CASTLE_MEMBERSHIP_PAGE_ID,
  PARKLAND_MEMBERSHIP_PAGE_ID,
} from './membershipUpgradeProductCodes';

// Import images
import navyLogo from '../../../assets/navy_logo_stacked.png';
import safariHeaderImg from '../../../assets/safari/header.png';
import dogParkBannerImg from '../../../assets/dog2.jpg';

/* Bunny CDN — same hero banner as Day Tickets */
const BOOKING_JOURNEY_BANNER_CDN =
  'https://BelvoirCastlePull.b-cdn.net/Website/1.webp';

/* Safari booking page IDs */
const SAFARI_CASTLE_PAGE_ID = '94fbd17d-c40c-49f3-878e-d0bc415926b2';
const SAFARI_GARDENS_PAGE_ID = '6d614bac-7055-4932-b62b-c960e7ff213b';
const SAFARI_PAGE_IDS = [SAFARI_CASTLE_PAGE_ID, SAFARI_GARDENS_PAGE_ID];

/* Halloween Gardens & Playground booking */
const HALLOWEEN_GARDENS_PAGE_ID = '05c63d88-cb02-40ec-b6b5-08c3a8958db7';
const HALLOWEEN_BANNER_CDN =
  'https://BelvoirCastlePull.b-cdn.net/Website/5S5A1084-2%20(1).webp';

/* Belvoir Wellness — A Day of Discovery + Sensory Tree Trail Tours */
const WELLNESS_DAY_PAGE_ID = 'b90fafa0-440f-4c69-831e-1f7e434cb8c8';
const WELLNESS_TREE_TRAIL_PAGE_ID = '37b0d9d6-3ed8-420b-bf5f-04419a7b5eb6';
const WELLNESS_PAGE_IDS = [WELLNESS_DAY_PAGE_ID, WELLNESS_TREE_TRAIL_PAGE_ID];
const WELLNESS_BANNER_CDN =
  'https://BelvoirCastle.b-cdn.net/Wellness/Wellness%203%20(1).png';
const WELLNESS_TRAIL_BANNER_CDN =
  'https://BelvoirCastle.b-cdn.net/Wellness/Wellness%202.png';
const wellnessTheme = {
  ...theme,
  colors: {
    ...theme.colors,
    primary: '#302D2C',
    secondary: '#302D2C',
    accent: '#B29C8D',
    offWhite: '#F7F3EE',
    greyBackground: '#EFE6DC',
  },
};

const FLOWER_GARDEN_PAGE_ID = '1ced1e1a-b442-4dee-b1e1-ae8b5b7bee09';
const PIRATE_CIRCUS_QUEUE_PAGE_ID = '27d7f7cb-fd23-4c0c-bd71-8420c022ccf5';
/** Set to `true` to show the virtual queue overlay on the Pirate / Circus booking page. */
const ENABLE_PIRATE_CIRCUS_QUEUE_SCREEN = false;
const GROTTO_RETAIL_VILLAGE_PAGE_ID = 'd4314a7f-aa55-4e17-994e-f58655da7072';
/** Castle grotto only — for guests who already have Deck the Halls entry */
const CASTLE_GROTTO_ADD_LATER_PAGE_ID = '2f5da61b-0ace-44a0-8d52-51069a6c26df';
const GROTTO_STANDALONE_PAGE_IDS = [
  GROTTO_RETAIL_VILLAGE_PAGE_ID,
  '9afa8eb9-dd32-4529-b9af-316e13a7397e',
];
const GROTTO_NO_UPSELL_PAGE_IDS = [
  ...GROTTO_STANDALONE_PAGE_IDS,
  CASTLE_GROTTO_ADD_LATER_PAGE_ID,
];
const AFTERNOON_TEA_PAGE_ID = '8b64eaf5-431e-4a3e-9194-e243e5cbdbdc';
/** Direct standalone afternoon tea booking page (not as an upsell to an entry ticket) */
const DIRECT_AFTERNOON_TEA_PAGE_ID = '77637987-6d92-4c12-b571-f60d0f18fc69';
/** Festive afternoon tea only (no entry ticket) — same storage as year-round tea-only */
const FESTIVE_DIRECT_AFTERNOON_TEA_PAGE_ID = '99baec32-367a-4940-b189-f88060702a53';
const DIRECT_AFTERNOON_TEA_PAGE_IDS = [
  DIRECT_AFTERNOON_TEA_PAGE_ID,
  FESTIVE_DIRECT_AFTERNOON_TEA_PAGE_ID,
];
const VINEYARD_TOUR_PAGE_ID = '4effca7e-cbbe-4e9b-a37a-513a9e022bbb';
const NO_UPSELL_PAGE_IDS = [...GROTTO_NO_UPSELL_PAGE_IDS];

/** Dog Park — single product, auto-selected on load; user lands straight on Date & Time */
const DOG_PARK_PAGE_ID = 'db0a4790-c484-474b-b350-e7ac8209f9e7';

/** Festive Afternoon Tea journeys (?requireAfternoonTea=1) — tea sittings only in this window */
const FESTIVE_AFTERNOON_TEA_DATE_START = '2026-11-14';
const FESTIVE_AFTERNOON_TEA_DATE_END = '2026-12-30';

const applyFestiveAfternoonTeaDateWindow = (datesMap, requireTea) => {
  if (!requireTea || !datesMap) return datesMap || {};
  const next = {};
  Object.entries(datesMap).forEach(([date, availability]) => {
    if (date >= FESTIVE_AFTERNOON_TEA_DATE_START && date <= FESTIVE_AFTERNOON_TEA_DATE_END) {
      next[date] = availability;
    } else {
      next[date] = 4; // outside festive tea window — treat as unavailable
    }
  });
  return next;
};

const isWithinFestiveAfternoonTeaDateWindow = (dateLike) => {
  if (!dateLike) return false;
  let key = '';
  if (typeof dateLike === 'string') {
    key = dateLike.slice(0, 10);
  } else if (dateLike instanceof Date && !Number.isNaN(dateLike.getTime())) {
    const y = dateLike.getFullYear();
    const m = String(dateLike.getMonth() + 1).padStart(2, '0');
    const d = String(dateLike.getDate()).padStart(2, '0');
    key = `${y}-${m}-${d}`;
  }
  return key >= FESTIVE_AFTERNOON_TEA_DATE_START && key <= FESTIVE_AFTERNOON_TEA_DATE_END;
};

/** Castle & Gardens / Gardens journeys where we prompt if the guest skips Circus on the upsell step */
const CIRCUS_SKIP_REMINDER_PAGE_IDS = [
  'b0f1a62a-a34a-4c4e-a88d-6fda03a19279',
  '40ca044f-534f-4b7f-83ea-b4156f0633d3',
  'b17520e4-20c1-451c-84ea-38c476c5fad7',
  'ccb57ad1-9af9-4bf8-8192-0b9d08f0c746',
];

// Featured tour pages where a specific named upsell is mandatory before proceeding
const CROWN_TOUR_PAGE_ID = 'de28c2dc-c033-4de1-a18b-615b3c224cfa';
const BUTLER_SAW_PAGE_ID = '40c6aa85-2a28-49ee-b884-8839c71d3f4b';
const FEATURED_TOUR_PAGE_IDS = [CROWN_TOUR_PAGE_ID, BUTLER_SAW_PAGE_ID];

/** Basket includes Animal Park Free – Promotion lines (parent name may be on parentUpsellName) */
const basketHasFreeAnimalParkPromotion = (selectedTickets) =>
  (selectedTickets || []).some(t => {
    const qty = parseInt(t.quantity, 10) || 0;
    if (qty <= 0) return false;
    const label = `${t.name || ''} ${t.parentUpsellName || ''}`.toLowerCase();
    return label.includes('animal park free') && label.includes('promotion');
  });

/** Page offers an Animal Park upsell path (not paid-only “Animal Park Only” cards) */
const upsellsIncludeAnimalParkOffer = (upsells) =>
  (upsells || []).some(u => {
    const n = (u.name || '').toLowerCase();
    return n.includes('animal park') && !n.includes('animal park only');
  });

const upsellsIncludeAvailableCircusOffer = (upsells) =>
  (upsells || []).some(u => {
    if (u.quantityAvailable === 0 || u.isSoldOut === true) return false;
    return ticketNameQualifiesForCircusBundleDiscount(u.name);
  });

const basketHasCircusShowTicket = (selectedTickets) =>
  (selectedTickets || []).some(t => {
    const qty = parseInt(t.quantity, 10) || 0;
    if (qty <= 0) return false;
    const combined = `${t.name || ''} ${t.parentUpsellName || ''}`;
    return ticketNameQualifiesForCircusBundleDiscount(combined);
  });

// Map each featured tour page to the upsell name substring to match and a display name
const FEATURED_TOUR_UPSELL_MAP = {
  [CROWN_TOUR_PAGE_ID]: { pattern: 'crown tour', displayName: 'The Crown Tour' },
  [BUTLER_SAW_PAGE_ID]: { pattern: 'butler saw', displayName: 'What the Butler Saw Tour' },
};

const isAfternoonTeaProductName = (name) => {
  const n = (name || '').toLowerCase();
  if (!n) return false;
  return (
    n.includes('afternoon tea') ||
    n.includes('aviary tearoom') ||
    (n.includes('festive') && n.includes('tea'))
  );
};

const ticketIsAfternoonTea = (ticket) => {
  if (!ticket) return false;
  return (
    isAfternoonTeaProductName(ticket.name) ||
    isAfternoonTeaProductName(ticket.parentUpsellName) ||
    isAfternoonTeaProductName(ticket.parentUpsellName) ||
    isAfternoonTeaProductName(ticket.description)
  );
};

const basketHasAfternoonTea = (tickets) =>
  (tickets || []).some(t => (parseInt(t.quantity, 10) || 0) > 0 && ticketIsAfternoonTea(t));

const countEntryTickets = (tickets) =>
  (tickets || [])
    .filter(t => !t.isUpsellItem && t.pageType !== 'EventTemplate' && (t.quantity || 0) > 0)
    .reduce((sum, t) => sum + (parseInt(t.quantity, 10) || 0), 0);

const countAfternoonTeaTickets = (tickets) =>
  (tickets || [])
    .filter(t => ticketIsAfternoonTea(t) && (parseInt(t.quantity, 10) || 0) > 0)
    .reduce((sum, t) => sum + (parseInt(t.quantity, 10) || 0), 0);

const upsellsIncludeAvailableAfternoonTea = (upsells) =>
  (upsells || []).some(u => {
    if (
      u.quantityAvailable === 0 ||
      u.quantityAvailable === 0 ||
      u.isSoldOut === true ||
      u.isSoldOut === true
    ) return false;
    return isAfternoonTeaProductName(u.name);
  });

/**
 * Format a Date object as YYYY-MM-DD in local timezone
 * Avoids timezone conversion issues that occur with toISOString()
 * @param {Date} date - The date to format
 * @returns {string} Date string in YYYY-MM-DD format
 */
const formatDateLocal = (date) => {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Extract afternoon tea items from booking basket
 * @param {Object} venposBasket - The Venpos basket data
 * @param {Date} selectedDate - The selected visit date
 * @param {Object} customerDetails - Customer details
 * @param {number} totalAmount - Total booking amount
 * @param {string} bookingReference - Booking reference
 * @param {Array} selectedTickets - The selectedTickets state (contains modifier/question data)
 * @param {boolean} isDirectBooking - true when the whole booking IS afternoon tea (direct page, not upsell)
 * @returns {Object|null} Afternoon tea booking data or null if no tea items found
 */
const extractAfternoonTeaItems = (venposBasket, selectedDate, customerDetails, totalAmount, bookingReference, selectedTickets = [], isDirectBooking = false) => {
  try {
    // Check if basket has data
    if (!venposBasket || !venposBasket.data || !venposBasket.data.groups) {
      return null;
    }
    
    const basketGroups = venposBasket.data.groups || [];
    const basketItems = basketGroups.flatMap(g => g.items || []);
    
    const isAfternoonTeaBookingLabel = (name) => {
      const n = (name || '').toLowerCase();
      if (!n) return false;
      return (
        n.includes('afternoon tea') ||
        n.includes('aviary tearoom') ||
        (n.includes('festive') && n.includes('tea'))
      );
    };

    // Also match selectedTickets so Festive Afternoon Tea (and similar) still stores
    // if Venpos basket names omit the parent upsell title.
    const teaTicketIds = new Set(
      (selectedTickets || [])
        .filter(t =>
          isAfternoonTeaBookingLabel(t.name) ||
          isAfternoonTeaBookingLabel(t.parentUpsellName) ||
          isAfternoonTeaBookingLabel(t.description)
        )
        .map(t => t.webSaleItemId)
        .filter(Boolean)
    );

    // For direct AT bookings the entire basket is afternoon tea — no filtering needed.
    // For upsell flows filter by afternoon tea / Aviary / festive tea in the item or parent name.
    const afternoonTeaItems = isDirectBooking
      ? basketItems
      : basketItems.filter(item =>
          isAfternoonTeaBookingLabel(item.parentUpsellName) ||
          isAfternoonTeaBookingLabel(item.name) ||
          isAfternoonTeaBookingLabel(item.description) ||
          (item.webSaleItemId && teaTicketIds.has(item.webSaleItemId))
        );
    
    if (afternoonTeaItems.length === 0) {
      return null;
    }
    
    console.log('🍵 [AFTERNOON-TEA] Found afternoon tea items:', afternoonTeaItems.length);
    
    // Extract visit date and time - MUST use afternoon tea sitting time, NOT entry ticket time.
    // Prefer selectedTickets (user's choice in afternoon tea modal); basket may have entry time from API.
    let visitDate = null;
    let visitTime = null;
    const afternoonTeaItemIds = new Set(afternoonTeaItems.map(item => item.webSaleItemId).filter(Boolean));
    
    const formatLocalDate = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    const formatLocalTime = (d) => {
      const h = String(d.getHours()).padStart(2, '0');
      const min = String(d.getMinutes()).padStart(2, '0');
      const s = String(d.getSeconds()).padStart(2, '0');
      return `${h}:${min}:${s}`;
    };
    
    // 1) Prefer selectedTickets: these have the afternoon tea time from the modal (not entry time)
    const teaTicketWithTime = selectedTickets.find(t => 
      afternoonTeaItemIds.has(t.webSaleItemId) &&
      (t.selectedEventTime?.startDateTime || (t.selectedDate && String(t.selectedDate).includes('T')))
    );
    if (teaTicketWithTime) {
      const dateTimeStr = teaTicketWithTime.selectedEventTime?.startDateTime || teaTicketWithTime.selectedDate;
      const d = new Date(dateTimeStr);
      if (!Number.isNaN(d.getTime())) {
        visitDate = formatLocalDate(d);
        visitTime = formatLocalTime(d);
        console.log('🍵 [AFTERNOON-TEA] Using afternoon tea time from selectedTickets:', visitDate, visitTime);
      }
    }
    
    // 2) Fallback: basket item's selectedEventTime/selectedDate (only if clearly afternoon tea event time)
    if (!visitDate || !visitTime) {
      const firstItemWithTime = afternoonTeaItems.find(item => 
        item.selectedEventTime?.startDateTime || (item.selectedDate && String(item.selectedDate).includes('T'))
      );
      if (firstItemWithTime) {
        const dateTimeStr = firstItemWithTime.selectedEventTime?.startDateTime || firstItemWithTime.selectedDate;
        const d = new Date(dateTimeStr);
        if (!Number.isNaN(d.getTime())) {
          visitDate = visitDate || formatLocalDate(d);
          visitTime = visitTime || formatLocalTime(d);
          if (visitTime) console.log('🍵 [AFTERNOON-TEA] Using afternoon tea time from basket item:', visitDate, visitTime);
        }
      }
    }
    
    // 3) Date-only fallback: use main visit date only - NEVER use main selectedTimeSlot for time (that is entry time)
    if (!visitDate && selectedDate) {
      visitDate = formatLocalDate(selectedDate);
      console.log('🍵 [AFTERNOON-TEA] Using main visit date only (no afternoon tea time in basket/tickets):', visitDate);
    }
    
    // Create arrays of selectedTickets grouped by webSaleItemId
    // This handles multiple items with the same webSaleItemId (e.g., 2 afternoon teas)
    const selectedTicketsByItemId = {};
    selectedTickets.forEach(ticket => {
      if (ticket.webSaleItemId) {
        if (!selectedTicketsByItemId[ticket.webSaleItemId]) {
          selectedTicketsByItemId[ticket.webSaleItemId] = [];
        }
        selectedTicketsByItemId[ticket.webSaleItemId].push(ticket);
      }
    });
    
    console.log('🍵 [AFTERNOON-TEA] SelectedTickets grouped by itemId:', selectedTicketsByItemId);
    
    // Track which index we're at for each webSaleItemId
    const itemIndexCounters = {};
    
    // Process products with their modifiers and questions
    const products = afternoonTeaItems.map(item => {
      console.log('🍵 [AFTERNOON-TEA] Processing item:', item);
      console.log('🍵 [AFTERNOON-TEA] Item keys:', Object.keys(item));
      
      // Get the current index for this webSaleItemId and increment counter
      const itemId = item.webSaleItemId;
      if (!itemIndexCounters[itemId]) {
        itemIndexCounters[itemId] = 0;
      }
      const currentIndex = itemIndexCounters[itemId];
      itemIndexCounters[itemId]++;
      
      // Find matching ticket in selectedTickets by itemId and index
      const ticketsForThisItem = selectedTicketsByItemId[itemId] || [];
      // If there are fewer selectedTickets entries than Venpos basket items for this type
      // (e.g. selectedTickets has one entry with quantity:5, Venpos expands to 5 rows),
      // fall back to the first entry so modifier/allergen data propagates to every ticket.
      const matchingTicket = ticketsForThisItem[currentIndex] ?? ticketsForThisItem[0];
      console.log('🍵 [AFTERNOON-TEA] Matching ticket from selectedTickets (index ' + currentIndex + '):', matchingTicket);
      
      const product = {
        webSaleItemId: item.webSaleItemId || '',
        name: item.name || '',
        quantity: item.quantity || 1,
        price: item.unitPrice || item.price || 0,
        unitPrice: item.unitPrice || item.price || 0,
        totalPrice: item.totalPrice || (item.unitPrice || item.price || 0) * (item.quantity || 1),
        eventId: item.eventId || null,
        eventCapacityUsageId: item.eventCapacityUsageId || null
      };
      
      // Extract modifier selection - FIRST check selectedTickets (most reliable source)
      // This is where modifierName and modifierPrompt are stored
      let modifierSelection = null;
      
      if (matchingTicket && matchingTicket.modifierSelection) {
        modifierSelection = matchingTicket.modifierSelection;
        console.log('🍵 [AFTERNOON-TEA] Found modifierSelection in selectedTickets:', modifierSelection);
      }
      
      // Extract allergen notes from matching ticket
      let allergenNotes = null;
      if (matchingTicket && matchingTicket.allergenNotes) {
        allergenNotes = matchingTicket.allergenNotes;
        console.log('🍵 [AFTERNOON-TEA] Found allergenNotes in selectedTickets:', allergenNotes);
      }
      
      // Also check directly on item (fallback)
      if (!allergenNotes && item.allergenNotes) {
        allergenNotes = item.allergenNotes;
        console.log('🍵 [AFTERNOON-TEA] Found allergenNotes on item:', allergenNotes);
      }
      
      // Check direct property on item (fallback, but may not have text fields)
      if (!modifierSelection && item.modifierSelection) {
        modifierSelection = item.modifierSelection;
        console.log('🍵 [AFTERNOON-TEA] Found modifierSelection on item:', modifierSelection);
      }
      
      // Check modifierSelections array on item (fallback)
      if (!modifierSelection && item.modifierSelections && Array.isArray(item.modifierSelections) && item.modifierSelections.length > 0) {
        modifierSelection = item.modifierSelections[0]; // Take first modifier
        console.log('🍵 [AFTERNOON-TEA] Found modifierSelections array on item:', modifierSelection);
      }
      
      // Check basket-level modifierSelections array (fallback)
      if (!modifierSelection && venposBasket.data.modifierSelections && Array.isArray(venposBasket.data.modifierSelections)) {
        const itemModifiers = venposBasket.data.modifierSelections.filter(m => 
          m.webSaleItemId === item.webSaleItemId || m.eventId === item.eventId
        );
        if (itemModifiers.length > 0) {
          modifierSelection = itemModifiers[0];
          console.log('🍵 [AFTERNOON-TEA] Found modifierSelection in basket modifierSelections:', modifierSelection);
        }
      }
      
      // Check modifiers property (alternative name, fallback)
      if (!modifierSelection && item.modifiers && Array.isArray(item.modifiers) && item.modifiers.length > 0) {
        modifierSelection = item.modifiers[0];
        console.log('🍵 [AFTERNOON-TEA] Found modifiers array on item:', modifierSelection);
      }
      
      // Ensure modifierSelection has the required text fields
      // If it only has IDs, we can't get the text, but we should preserve what we have
      if (modifierSelection) {
        // Ensure we have modifierName and modifierPrompt if they're missing
        // (They should be present if coming from selectedTickets)
        if (!modifierSelection.modifierName && modifierSelection.modifierTextId) {
          console.warn('🍵 [AFTERNOON-TEA] Modifier selection missing modifierName, only has modifierTextId:', modifierSelection.modifierTextId);
        }
        if (!modifierSelection.modifierPrompt && modifierSelection.modifierId) {
          console.warn('🍵 [AFTERNOON-TEA] Modifier selection missing modifierPrompt, only has modifierId:', modifierSelection.modifierId);
        }
        product.modifierSelection = modifierSelection;
      } else {
        console.log('🍵 [AFTERNOON-TEA] No modifier selection found for item:', item.name);
      }
      
      // Add allergen notes if present
      if (allergenNotes) {
        product.allergenNotes = allergenNotes;
        console.log('🍵 [AFTERNOON-TEA] Added allergenNotes to product:', allergenNotes);
      }
      
      // Extract question answers - FIRST check selectedTickets (most reliable source)
      let questionAnswers = null;
      let questionData = null;
      
      if (matchingTicket) {
        if (matchingTicket.questionAnswers) {
          questionAnswers = matchingTicket.questionAnswers;
          console.log('🍵 [AFTERNOON-TEA] Found questionAnswers in selectedTickets:', questionAnswers);
        }
        if (matchingTicket.eventQuestions) {
          const questionDataObj = questionData || {}; // Preserve any existing questionData
          const eventQuestions = Array.isArray(matchingTicket.eventQuestions) 
            ? matchingTicket.eventQuestions 
            : (matchingTicket.eventQuestions.eventQuestions || []);
          eventQuestions.forEach(q => {
            const qId = q.questionId || q.id || `q_${eventQuestions.indexOf(q)}`;
            // Store with both the original ID and clean ID (without __0 suffix) for better matching
            const cleanQId = qId.replace(/__\d+$/, '');
            const questionText = q.questionText || q.text || q.label || q.question || 'Question';
            
            // Store with original ID
            questionDataObj[qId] = {
              text: questionText,
              type: q.questionType || q.type || 'text',
              required: q.required || false
            };
            // Also store with clean ID if different
            if (cleanQId !== qId) {
              questionDataObj[cleanQId] = {
                text: questionText,
                type: q.questionType || q.type || 'text',
                required: q.required || false
              };
            }
          });
          questionData = questionDataObj;
          console.log('🍵 [AFTERNOON-TEA] Found eventQuestions in selectedTickets:', questionData);
        }
      }
      
      // Check direct property on item (fallback)
      if (!questionAnswers && item.questionAnswers) {
        questionAnswers = item.questionAnswers;
        console.log('🍵 [AFTERNOON-TEA] Found questionAnswers on item:', questionAnswers);
      }
      
      // Check questions array on item
      if (!questionAnswers && item.questions && Array.isArray(item.questions) && item.questions.length > 0) {
        const qaObj = {};
        const qdObj = {};
        item.questions.forEach(q => {
          const qId = q.questionId || q.id || `q_${item.questions.indexOf(q)}`;
          qaObj[qId] = q.answer || q.value || '';
          qdObj[qId] = {
            text: q.questionText || q.text || q.question || qId,
            type: q.questionType || q.type || 'text'
          };
        });
        questionAnswers = qaObj;
        questionData = qdObj;
        console.log('🍵 [AFTERNOON-TEA] Found questions array on item:', questionAnswers);
      }
      
      // Extract eventQuestions metadata if present (contains question text and metadata) - fallback
      if (!questionData && item.eventQuestions && Array.isArray(item.eventQuestions)) {
        const questionDataObj = {};
        item.eventQuestions.forEach(q => {
          const qId = q.questionId || q.id || `q_${item.eventQuestions.indexOf(q)}`;
          questionDataObj[qId] = {
            text: q.questionText || q.text || q.label || qId,
            type: q.questionType || q.type || 'text',
            required: q.required || false
          };
        });
        questionData = questionDataObj;
        console.log('🍵 [AFTERNOON-TEA] Found eventQuestions on item:', questionData);
      }
      
      // Check basket-level questions array
      if (venposBasket.data.questions && Array.isArray(venposBasket.data.questions)) {
        // Try to match questions to this item by webSaleItemId or eventId
        const itemQuestions = venposBasket.data.questions.filter(q => 
          q.webSaleItemId === item.webSaleItemId || 
          q.eventId === item.eventId ||
          (q.productId && q.productId === item.webSaleItemId)
        );
        
        if (itemQuestions.length > 0) {
          console.log('🍵 [AFTERNOON-TEA] Found questions in basket.questions array:', itemQuestions);
          // Convert questions array to questionAnswers object format
          const questionAnswersObj = questionAnswers || {};
          const questionDataObj = questionData || {};
          itemQuestions.forEach(q => {
            const qId = q.questionId || q.id || `q_${itemQuestions.indexOf(q)}`;
            if (!questionAnswersObj[qId] && (q.answer || q.value)) {
              questionAnswersObj[qId] = q.answer || q.value || '';
            }
            if (!questionDataObj[qId]) {
              questionDataObj[qId] = {
                text: q.questionText || q.text || q.question || qId,
                type: q.questionType || q.type || 'text'
              };
            }
          });
          questionAnswers = questionAnswersObj;
          questionData = questionDataObj;
        }
      }
      
      // Check basket data.questions (alternative structure)
      if (venposBasket.data.questions && typeof venposBasket.data.questions === 'object' && !Array.isArray(venposBasket.data.questions)) {
        // It might be an object keyed by item ID
        const itemQuestions = venposBasket.data.questions[item.webSaleItemId] || venposBasket.data.questions[item.eventId];
        if (itemQuestions) {
          console.log('🍵 [AFTERNOON-TEA] Found questions in basket.questions object:', itemQuestions);
          if (Array.isArray(itemQuestions)) {
            const qaObj = questionAnswers || {};
            const qdObj = questionData || {};
            itemQuestions.forEach(q => {
              const qId = q.questionId || q.id || `q_${itemQuestions.indexOf(q)}`;
              qaObj[qId] = q.answer || q.value || '';
              qdObj[qId] = {
                text: q.questionText || q.text || q.question || qId,
                type: q.questionType || q.type || 'text'
              };
            });
            questionAnswers = qaObj;
            questionData = qdObj;
          }
        }
      }
      
      if (questionAnswers) {
        product.questionAnswers = questionAnswers;
      }
      if (questionData) {
        product.questionData = questionData;
      }
      
      if (!questionAnswers) {
        console.log('🍵 [AFTERNOON-TEA] No question answers found for item:', item.name);
      }
      
      console.log('🍵 [AFTERNOON-TEA] Final product data:', product);
      
      return product;
    });
    
    // Backend expects visitDate; if it contains 'T' it extracts both date and time. Send afternoon tea time, not entry time.
    const visitDateForApi = visitTime ? `${visitDate}T${visitTime}` : visitDate;
    
    return {
      bookingReference: bookingReference,
      visitDate: visitDateForApi,
      visitTime: visitTime || undefined,
      customerDetails: customerDetails || {},
      totalAmount: totalAmount || 0,
      products: products
    };
    
  } catch (error) {
    console.error('❌ [AFTERNOON-TEA] Error extracting afternoon tea items:', error);
    return null;
  }
};

const BookingContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 158px;

  @media (max-width: 768px) {
    padding-top: 100px;
  }
`;

/* Below hero — matches Day Tickets intro strip */
const BookingIntroSection = styled.section`
  padding: clamp(1.65rem, 4vw, 2.35rem) clamp(1.25rem, 4vw, 2rem)
    clamp(1rem, 2.5vw, 1.35rem);
  border-top: 1px solid ${props =>
    props.$wellness ? 'rgba(178, 156, 141, 0.28)' : 'rgba(16, 29, 64, 0.07)'};
  background: linear-gradient(
    180deg,
    ${props => props.theme.colors.offWhite} 0%,
    ${props =>
      props.$wellness
        ? 'rgba(239, 230, 220, 0.55)'
        : 'rgba(241, 239, 234, 0.35)'} 100%
  );
`;

const BookingIntroInner = styled.div`
  max-width: 36rem;
  margin: 0 auto;
  text-align: center;
`;

const BookingIntroEyebrow = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${props =>
    props.$wellness ? 'rgba(48, 45, 44, 0.48)' : 'rgba(16, 29, 64, 0.48)'};
  margin: 0 0 0.5rem;
`;

const BookingIntroSubtitle = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.92rem, 1.85vw, 1.02rem);
  line-height: 1.55;
  color: ${props =>
    props.$wellness ? 'rgba(48, 45, 44, 0.56)' : 'rgba(16, 29, 64, 0.56)'};
  margin: 0;
`;

const BookingContent = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  width: 100%;
  padding: clamp(0.85rem, 2.5vw, 1.35rem) clamp(1rem, 4vw, 1.5rem)
    clamp(2rem, 5vw, 3rem);

  @media (max-width: 480px) {
    padding-left: clamp(0.75rem, 4vw, 1rem);
    padding-right: clamp(0.75rem, 4vw, 1rem);
  }
`;

const ProgressContainer = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: clamp(1rem, 2.5vw, 1.35rem) clamp(1rem, 3vw, 1.5rem)
    clamp(0.85rem, 2vw, 1.1rem);
  margin-bottom: clamp(0.85rem, 2vw, 1.1rem);
  border: 1px solid ${props =>
    props.$wellness ? 'rgba(178, 156, 141, 0.22)' : 'rgba(16, 29, 64, 0.08)'};
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.92) inset,
    0 14px 42px ${props =>
      props.$wellness ? 'rgba(48, 45, 44, 0.06)' : 'rgba(16, 29, 64, 0.07)'};

  @media (max-width: 640px) {
    padding: 1rem 1.05rem;
    border-radius: 14px;
  }
`;

const ProgressHeader = styled.div`
  text-align: center;
  margin-bottom: clamp(0.85rem, 2vw, 1.1rem);

  h1 {
    font-family: ${props => props.theme.fonts.main};
    font-size: clamp(1.35rem, 3.2vw, 1.75rem);
    font-weight: 700;
    color: ${props => props.theme.colors.primary};
    margin: 0 0 0.3rem;
    line-height: 1.15;
    letter-spacing: -0.02em;
  }

  p {
    font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    margin: 0;
    color: ${props =>
      props.$wellness ? 'rgba(48, 45, 44, 0.52)' : 'rgba(16, 29, 64, 0.52)'};
    font-size: clamp(0.86rem, 1.7vw, 0.94rem);
    line-height: 1.45;
  }

  @media (max-width: 640px) {
    text-align: left;
    margin-bottom: 0.85rem;

    h1 {
      font-size: clamp(1.25rem, 5vw, 1.5rem);
    }
  }
`;

const ProgressSteps = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0;
  margin-bottom: 0;
  position: relative;

  @media (max-width: 640px) {
    display: none;
  }
`;

const ProgressTrack = styled.div`
  display: none;

  @media (max-width: 640px) {
    display: block;
    margin-bottom: 0;
  }
`;

const ProgressBarRail = styled.div`
  height: 3px;
  background: rgba(16, 29, 64, 0.1);
  border-radius: 99px;
  overflow: hidden;
  margin-bottom: 0.6rem;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  border-radius: 99px;
  background: ${props => props.$wellness
    ? 'linear-gradient(90deg, #B29C8D, #302D2C)'
    : props.$safari
    ? 'linear-gradient(90deg, #C8861A, #A83E18)'
    : `linear-gradient(90deg, ${props.theme.colors.accent}, ${props.theme.colors.primary})`};
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  width: ${props => props.$pct}%;
`;

const ProgressMobileLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.82rem;
`;

const ProgressMobileStep = styled.span`
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
`;

const ProgressMobileCount = styled.span`
  font-weight: 500;
  color: rgba(16, 29, 64, 0.42);
  font-size: 0.75rem;
`;

const ProgressStepNode = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  position: relative;

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 21px;
    left: 50%;
    right: -50%;
    height: 2px;
    background: ${props =>
      props.$isCompleted
        ? props.theme.colors.accent
        : 'rgba(16, 29, 64, 0.1)'};
    z-index: 0;
    transition: background 0.3s ease;
  }
`;

const ProgressStep = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
  padding: 0 0.5rem;
`;

const StepIcon = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.45rem;
  background: ${props => {
    if (props.$isCompleted) return props.theme.colors.accent;
    if (props.$isActive) return props.theme.colors.primary;
    return 'rgba(16, 29, 64, 0.07)';
  }};
  color: ${props => {
    if (props.$isCompleted || props.$isActive) return '#fff';
    return 'rgba(16, 29, 64, 0.42)';
  }};
  font-size: 1.05rem;
  font-weight: 700;
  transition: transform 0.22s ease, box-shadow 0.22s ease, background 0.22s ease;
  flex-shrink: 0;
  z-index: 1;
  box-shadow: ${props =>
    props.$isActive
      ? '0 0 0 3px rgba(157, 109, 73, 0.28)'
      : props.$isCompleted
        ? '0 4px 14px rgba(157, 109, 73, 0.25)'
        : 'none'};
`;

const StepLabel = styled.div`
  text-align: center;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.72rem, 1.5vw, 0.8rem);
  color: ${props => {
    if (props.$isCompleted) return props.theme.colors.accent;
    if (props.$isActive) return props.theme.colors.primary;
    return 'rgba(16, 29, 64, 0.42)';
  }};
  font-weight: ${props => (props.$isActive ? '700' : '500')};
  letter-spacing: ${props => (props.$isActive ? '0.01em' : '0')};
  white-space: nowrap;
  max-width: 72px;
  line-height: 1.2;
`;

const StepContent = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: clamp(1.1rem, 3vw, 1.5rem);
  border: 1px solid rgba(16, 29, 64, 0.08);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.92) inset,
    0 14px 42px rgba(16, 29, 64, 0.07);
  min-height: 400px;
  transition: opacity 0.2s ease;
  contain: layout;

  @media (max-width: 768px) {
    padding: clamp(0.95rem, 2.5vw, 1.15rem);
    min-height: 300px;
  }

  @media (max-width: 640px) {
    background: transparent;
    border: none;
    box-shadow: none;
    padding: 0;
    min-height: 0;
  }
`;

const NavigationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: clamp(0.85rem, 2vw, 1.1rem);
  padding-top: clamp(0.85rem, 2vw, 1.1rem);
  border-top: 1px solid rgba(16, 29, 64, 0.07);
  gap: 0.75rem;

  @media (max-width: 640px) {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 200;
    margin-top: 0;
    padding: 0.85rem 1rem
      calc(0.85rem + env(safe-area-inset-bottom, 0px));
    border-top: 1px solid rgba(16, 29, 64, 0.1);
    background: rgba(255, 255, 255, 0.96);
    backdrop-filter: blur(12px) saturate(1.4);
    -webkit-backdrop-filter: blur(12px) saturate(1.4);
    box-shadow: 0 -4px 24px rgba(16, 29, 64, 0.1);
    gap: 0.6rem;
  }
`;

const NavSpacer = styled.div`
  display: none;

  @media (max-width: 640px) {
    display: block;
    height: calc(72px + env(safe-area-inset-bottom, 0px));
  }
`;

const NavigationButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  padding: 0.72rem 1.65rem;
  border-radius: 999px;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.92rem, 2vw, 1rem);
  font-weight: 600;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease,
    transform 0.2s ease;
  border: 2px solid transparent;
  cursor: pointer;
  min-height: 48px;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  @media (max-width: 640px) {
    flex: 1;
    padding: 0.82rem 1rem;
    min-height: 50px;
    font-size: 0.96rem;
  }
`;

const BackButton = styled(NavigationButton)`
  background: transparent;
  color: rgba(16, 29, 64, 0.62);
  border-color: rgba(16, 29, 64, 0.14);
  flex-shrink: 1;
  min-width: 0;

  &:hover:not(:disabled) {
    background: rgba(16, 29, 64, 0.05);
    color: ${props => props.theme.colors.primary};
    border-color: rgba(16, 29, 64, 0.22);
  }

  @media (max-width: 640px) {
    flex: 0 0 auto;
    width: auto;
    padding: 0.82rem 1.15rem;
  }
`;

const NextButton = styled(NavigationButton)`
  background: ${props => props.theme.colors.accent};
  color: #fff;
  border-color: ${props => props.theme.colors.accent};
  min-width: 158px;
  flex-shrink: 0;
  box-shadow: 0 6px 20px rgba(157, 109, 73, 0.28);

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.primary};
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 10px 28px rgba(16, 29, 64, 0.18);
  }

  @media (max-width: 640px) {
    flex: 1;
    min-width: 0;
    box-shadow: 0 4px 16px rgba(157, 109, 73, 0.3);
  }
`;

const PartnersFooter = styled.div`
  text-align: center;
  padding: clamp(1.25rem, 3vw, 1.6rem) clamp(1rem, 3vw, 1.35rem);
  margin-top: clamp(1.5rem, 4vw, 2rem);
  border: 1px solid rgba(16, 29, 64, 0.08);
  border-radius: 16px;
  color: rgba(16, 29, 64, 0.58);
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.82rem, 1.65vw, 0.9rem);
  background: #fff;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.92) inset,
    0 12px 36px rgba(16, 29, 64, 0.06);
  line-height: 1.55;

  p {
    margin: 0;

    &:first-child {
      margin-bottom: 0.45rem;
    }
  }

  strong {
    color: ${props => props.theme.colors.primary};
    font-weight: 600;
  }

  .strapline {
    font-style: italic;
    font-size: 0.88em;
    color: rgba(16, 29, 64, 0.48);
  }
`;

const ErrorMessage = styled.div`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background: rgba(220, 53, 69, 0.08);
  color: #a71d2a;
  padding: 0.95rem 1.1rem;
  border-radius: 12px;
  margin-bottom: 1rem;
  text-align: center;
  border: 1px solid rgba(220, 53, 69, 0.22);
  font-size: 0.92rem;
  line-height: 1.45;
`;

const LoadingScreen = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 3rem 1rem;
  background: ${props => props.theme.colors.offWhite};
`;

const LoadingLogoContainer = styled.div`
  margin-bottom: 2.25rem;
  opacity: 0;
  animation: lcFadeUp 0.65s cubic-bezier(0.22, 1, 0.36, 1) 0.1s forwards;

  @keyframes lcFadeUp {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

const LoadingLogo = styled.img`
  width: 160px;
  height: auto;
  display: block;
`;

const LoadingText = styled.div`
  font-family: ${props => props.theme.fonts.main};
  color: ${props => props.theme.colors.primary};
  font-size: clamp(1.1rem, 2.5vw, 1.3rem);
  font-weight: 600;
  letter-spacing: -0.01em;
  margin-bottom: 0.4rem;
  opacity: 0;
  animation: lcFadeUp 0.55s cubic-bezier(0.22, 1, 0.36, 1) 0.35s forwards;
`;

const LoadingStrapline = styled.div`
  font-family: system-ui, -apple-system, sans-serif;
  color: rgba(16, 29, 64, 0.48);
  font-size: 0.875rem;
  letter-spacing: 0.01em;
  opacity: 0;
  animation: lcFadeUp 0.55s cubic-bezier(0.22, 1, 0.36, 1) 0.5s forwards;
`;

const LoadingTrack = styled.div`
  width: 160px;
  height: 2px;
  background: rgba(16, 29, 64, 0.08);
  border-radius: 999px;
  margin-top: 1.75rem;
  overflow: hidden;
  opacity: 0;
  animation: lcFadeUp 0.45s ease 0.65s forwards;
`;

const LoadingBar = styled.div`
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(
    90deg,
    ${props => props.theme.colors.accent},
    ${props => props.theme.colors.primary}
  );
  animation: lcBar 1.8s cubic-bezier(0.4, 0, 0.2, 1) infinite;

  @keyframes lcBar {
    0%   { width: 0%;   margin-left: 0%; }
    50%  { width: 60%;  margin-left: 20%; }
    100% { width: 0%;   margin-left: 100%; }
  }
`;


const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(16, 29, 64, 0.55);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  backdrop-filter: blur(6px) saturate(1.2);
  -webkit-backdrop-filter: blur(6px) saturate(1.2);

  @media (max-width: 640px) {
    align-items: flex-end;
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 20px;
  padding: 2rem 2rem calc(2rem + env(safe-area-inset-bottom, 0px));
  max-width: 500px;
  width: 92%;
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.12) inset,
    0 24px 64px rgba(16, 29, 64, 0.3);
  animation: modalSlideIn 0.28s cubic-bezier(0.34, 1.56, 0.64, 1);
  max-height: 92vh;
  overflow-y: auto;
  
  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: translateY(-18px) scale(0.97);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
  
  @media (max-width: 640px) {
    width: 100%;
    max-width: 100%;
    border-radius: 24px 24px 0 0;
    padding: 1.5rem 1.25rem calc(1.25rem + env(safe-area-inset-bottom, 12px));
    max-height: 88vh;
    animation: sheetSlideUp 0.32s cubic-bezier(0.32, 0.72, 0, 1);

    @keyframes sheetSlideUp {
      from {
        opacity: 0;
        transform: translateY(80px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    /* Drag handle pill */
    &::before {
      content: '';
      display: block;
      width: 40px;
      height: 4px;
      background: rgba(16, 29, 64, 0.14);
      border-radius: 99px;
      margin: 0 auto 1.25rem;
    }
  }
`;

const ModalIcon = styled.div`
  width: 72px;
  height: 72px;
  margin: 0 auto 1.35rem;
  background: linear-gradient(135deg, rgba(241, 239, 234, 0.95), rgba(255, 250, 240, 0.98));
  border: 1px solid rgba(157, 109, 73, 0.22);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  
  @media (max-width: 640px) {
    width: 60px;
    height: 60px;
    font-size: 2rem;
    margin-bottom: 1rem;
  }
`;

const ModalTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.3rem, 3.5vw, 1.65rem);
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.75rem;
  text-align: center;
  font-weight: 700;
  line-height: 1.2;
`;

const ModalText = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.9rem, 1.85vw, 1rem);
  color: rgba(16, 29, 64, 0.62);
  line-height: 1.6;
  margin-bottom: 1.15rem;
  text-align: center;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;

  @media (max-width: 640px) {
    flex-direction: column-reverse;
    gap: 0.6rem;
  }
`;

const ModalButton = styled.button`
  flex: 1;
  padding: 0.9rem 1rem;
  border-radius: 999px;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.92rem, 2vw, 1rem);
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
  border: 2px solid;
  min-height: 50px;
  
  &:hover {
    transform: translateY(-1px);
  }
`;

const CancelModalButton = styled(ModalButton)`
  background: transparent;
  color: ${props => props.theme.colors.secondary};
  border-color: ${props => props.theme.colors.greyBackground};
  
  &:hover {
    background: ${props => props.theme.colors.greyBackground};
  }
`;

const ConfirmModalButton = styled(ModalButton)`
  background: ${props => props.theme.colors.accent};
  color: white;
  border-color: ${props => props.theme.colors.accent};
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    border-color: ${props => props.theme.colors.primary};
  }
`;

// Technical Difficulties Overlay
const TechnicalDifficultiesOverlay = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f8f7f4 0%, #e5e3de 100%);
  padding: 2rem;
`;

const TechnicalDifficultiesBox = styled.div`
  max-width: 600px;
  background: white;
  border-radius: 16px;
  padding: 3rem 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  text-align: center;
`;

const TechnicalDifficultiesIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1.5rem;
  color: #f39c12;
`;

const TechnicalDifficultiesTitle = styled.h1`
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
  font-weight: 600;
`;

const TechnicalDifficultiesMessage = styled.p`
  font-size: 1.1rem;
  color: ${props => props.theme.colors.secondary};
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const BookingJourney = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const requireAfternoonTea = useMemo(
    () => new URLSearchParams(location.search).get('requireAfternoonTea') === '1',
    [location.search]
  );
  const choirBookingToken = useMemo(
    () => (new URLSearchParams(location.search).get('choir') || '').trim(),
    [location.search]
  );
  const {
    currentStep,
    locationId,
    pageId,
    selectedTickets,
    selectedDate,
    selectedTimeSlot,
    availableUpsells,
    selectedUpsells,
    customerDetails,
    paymentDetails,
    bookingSummary,
    loading,
    error,
    dispatch,
    canProceedToNextStep: canProceedToNextStepContext,
    calculateTotal,
    getFamilySaverCode,
    hasCircusTicketsForDiscount,
    calculateCircusDiscountAmount,
    modifierSelections,
    vineyardDietaryRequirements,
    directAfternoonTeaAllergens,
    santasGrottoChildren,
    searchForSantaChildren
  } = useBooking();

  // 🔒 Authentication state for page protection
  // Check maintenance mode status on mount
  const [maintenanceMode, setMaintenanceMode] = useState({ enabled: false, reason: '' });

  const [tickets, setTickets] = useState([]);
  const [choirFreeAdultAllowed, setChoirFreeAdultAllowed] = useState(false);
  const [choirDiscountCode, setChoirDiscountCode] = useState(null);
  const [choirLinkMessage, setChoirLinkMessage] = useState('');
  const [availableDates, setAvailableDates] = useState({});
  const [datesLoading, setDatesLoading] = useState(false);
  /** When set (YYYY-MM-DD from ?date=), calendar shows only this visit date. */
  const [pinnedVisitDate, setPinnedVisitDate] = useState(null);
  /** When set (YYYY-MM from ?filterMonth=), calendar restricts to that month only. */
  const [filterMonth, setFilterMonth] = useState(null);
  const [timeSlots, setTimeSlots] = useState([]);
  const [basketId, setBasketId] = useState(null);
  const [bookingReference, setBookingReference] = useState(null);
  const [venposBasket, setVenposBasket] = useState(null);
  const [appliedSaverCode, setAppliedSaverCode] = useState(null);
  const [registeredCustomerId, setRegisteredCustomerId] = useState(null);
  const [formattedEventQuestions, setFormattedEventQuestions] = useState([]); // Store event questions for CreateFreeOrder
  const [basketCustomerId, setBasketCustomerId] = useState(null);
  const [basketOrderId, setBasketOrderId] = useState(null);
  const [preflightDone, setPreflightDone] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [upsellsLoading, setUpsellsLoading] = useState(false);
  const [showRestartModal, setShowRestartModal] = useState(false);
  const [showDateTimeChangeModal, setShowDateTimeChangeModal] = useState(false);
  const [showFreeAnimalParkReminderModal, setShowFreeAnimalParkReminderModal] = useState(false);
  /** Increment to tell UpsellStep to open the Animal Park detail modal (from reminder dialog). */
  const [openAnimalParkUpsellNonce, setOpenAnimalParkUpsellNonce] = useState(0);
  const [showCircusSkipReminderModal, setShowCircusSkipReminderModal] = useState(false);
  /** Increment to tell UpsellStep to open the Circus upsell detail modal (from reminder dialog). */
  const [openCircusUpsellNonce, setOpenCircusUpsellNonce] = useState(0);
  const [pendingDateTimeChange, setPendingDateTimeChange] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  // Queue screen: show for high-demand events to build urgency on first load
  const [showQueue, setShowQueue] = useState(false);
  const queueShownRef = useRef(false);
  const [membershipDetails, setMembershipDetails] = useState(null);
  const [membershipCustomerDetails, setMembershipCustomerDetails] = useState(null);
  
  // Upgrade from booking state
  const [upgradeFromBooking, setUpgradeFromBooking] = useState(null);
  const [originalBookingData, setOriginalBookingData] = useState(null);
  const [upgradeCreditAmount, setUpgradeCreditAmount] = useState(0);
  const [isLoadingUpgradeBooking, setIsLoadingUpgradeBooking] = useState(false);
  const [sortedUpgradeItems, setSortedUpgradeItems] = useState({ child: [], adult: [] }); // Sorted items by type (lowest price first) for discount application
  const [usedUpgradeItems, setUsedUpgradeItems] = useState({ child: [], adult: [] }); // Items already redeemed in tracker (display only)
  const [upgradeDiscountApplied, setUpgradeDiscountApplied] = useState(false);
  const [upgradeDiscountCodeState, setUpgradeDiscountCodeState] = useState(null);
  const [circusDiscountCodeState, setCircusDiscountCodeState] = useState(null);
  const [childrenReturnDiscountCode, setChildrenReturnDiscountCode] = useState(null);
  const [upgradeDiscountMessage, setUpgradeDiscountMessage] = useState('');
  const [upgradeDiscountValue, setUpgradeDiscountValue] = useState(0);
  const [upgradeLinkExpired, setUpgradeLinkExpired] = useState(false);
  const [upgradeLinkErrorMessage, setUpgradeLinkErrorMessage] = useState('');
  
  // Throttling and undo state for discount creation
  const [isCreatingDiscount, setIsCreatingDiscount] = useState(false);
  const [lastDiscountCreationTime, setLastDiscountCreationTime] = useState(0);
  const [createdDiscountCodes, setCreatedDiscountCodes] = useState([]); // Track codes created in this session for undo
  const [isUndoingDiscounts, setIsUndoingDiscounts] = useState(false);

  // Ref used to scroll to the top of the booking content on step change
  const topRef = useRef(null);

  const isFlowerGardenShow = pageId === '1ced1e1a-b442-4dee-b1e1-ae8b5b7bee09';
  const isSafariBooking = SAFARI_PAGE_IDS.includes(pageId);
  const isHalloweenBooking = pageId === HALLOWEEN_GARDENS_PAGE_ID;
  const isDogParkBooking = pageId === DOG_PARK_PAGE_ID;
  const urlPageId = useMemo(
    () => (new URLSearchParams(location.search).get('pageId') || '').trim(),
    [location.search]
  );
  const brandingPageId = urlPageId || pageId;
  const isWellnessBooking = WELLNESS_PAGE_IDS.includes(brandingPageId);
  const isWellnessTrailBooking = brandingPageId === WELLNESS_TREE_TRAIL_PAGE_ID;
  const withWellnessTheme = (node) =>
    isWellnessBooking ? <ThemeProvider theme={wellnessTheme}>{node}</ThemeProvider> : node;
  const isVineyardTourBooking = pageId === VINEYARD_TOUR_PAGE_ID;
  const isDirectAfternoonTeaBooking = DIRECT_AFTERNOON_TEA_PAGE_IDS.includes(pageId);
  const isFestiveDirectAfternoonTeaBooking = pageId === FESTIVE_DIRECT_AFTERNOON_TEA_PAGE_ID;
  const restrictToFestiveTeaDates = requireAfternoonTea || isFestiveDirectAfternoonTeaBooking;

  // Guest count for vineyard dietary forms — prefer Venpos basket (expanded lines), else ticket quantities
  const vineyardGuestCount = useMemo(() => {
    if (!isVineyardTourBooking) return 0;
    const basketItems = venposBasket?.data?.groups?.flatMap(g => g.items || []) || [];
    if (basketItems.length > 0) {
      return basketItems.reduce((sum, item) => sum + (parseInt(item.quantity, 10) || 1), 0);
    }
    return (selectedTickets || [])
      .filter(t => (Number(t.quantity) || 0) > 0)
      .reduce((sum, t) => sum + (Number(t.quantity) || 0), 0);
  }, [isVineyardTourBooking, venposBasket, selectedTickets]);
  const isSafariCastle = pageId === SAFARI_CASTLE_PAGE_ID;

  // Helper to format dates in local time (avoid timezone shifting to previous day)
  const formatLocalDate = (date) => {
    if (!date) return null;
    const tzOffset = date.getTimezoneOffset() * 60000; // offset in ms
    const localISO = new Date(date.getTime() - tzOffset).toISOString();
    return localISO.split('T')[0];
  };

  // Detect if this is a membership journey
  const isMembershipJourney = useMemo(() => {
    // Check if pageId matches a membership page ID
    if (pageId === '37bd858f-4f04-4e83-a469-05274db0d546' || pageId === '9d7c060b-6088-4bc1-8def-7bddb9b87ef9') {
      return true;
    }
    // Check if any selected tickets have webSaleItemTypeID === 4 (membership type)
    if (selectedTickets && selectedTickets.length > 0) {
      return selectedTickets.some(ticket => ticket.webSaleItemTypeID === 4);
    }
    // Check if any tickets in the list have webSaleItemTypeID === 4
    if (tickets && tickets.length > 0) {
      return tickets.some(ticket => ticket.webSaleItemTypeID === 4);
    }
    return false;
  }, [pageId, selectedTickets, tickets]);

  // Redirect membership journeys away from steps 2 and 3
  useEffect(() => {
    if (isMembershipJourney && (currentStep === 2 || currentStep === 3)) {
      dispatch({ type: BOOKING_ACTIONS.SET_CURRENT_STEP, payload: 4 });
    }
  }, [isMembershipJourney, currentStep, dispatch]);
  
  // Redirect grotto retail village page away from step 3 (upsells)
  useEffect(() => {
    if (NO_UPSELL_PAGE_IDS.includes(pageId) && currentStep === 3) {
      dispatch({ type: BOOKING_ACTIONS.SET_CURRENT_STEP, payload: 4 });
    }
  }, [pageId, currentStep, dispatch]);

  // Show queue screen once when the Pirate / Circus page ID is resolved (when enabled)
  useEffect(() => {
    if (!ENABLE_PIRATE_CIRCUS_QUEUE_SCREEN) return;
    if (pageId === PIRATE_CIRCUS_QUEUE_PAGE_ID && !queueShownRef.current) {
      queueShownRef.current = true;
      setShowQueue(true);
    }
  }, [pageId]);
  
  // Check maintenance mode status on mount and periodically
  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const status = await checkMaintenanceModeStatus();
        setMaintenanceMode(status);
        
        if (status.enabled) {
          console.warn('🚨 [MAINTENANCE] Maintenance mode is ACTIVE:', status.reason);
        }
      } catch (error) {
        console.error('❌ [MAINTENANCE] Error checking maintenance mode:', error);
      }
    };
    
    checkMaintenance();
    
    // Check every 30 seconds
    const interval = setInterval(checkMaintenance, 30000);
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const validateChoirLink = async () => {
      if (!choirBookingToken) {
        setChoirFreeAdultAllowed(false);
        setChoirDiscountCode(null);
        setChoirLinkMessage('');
        return;
      }
      try {
        const response = await fetch(
          `${config.apiEndpoint}/belvoir/v1/choir-booking-link?token=${encodeURIComponent(choirBookingToken)}`,
          { headers: { Accept: 'application/json' }, credentials: 'omit' }
        );
        const data = await response.json();
        if (cancelled) return;
        if (data?.valid) {
          setChoirFreeAdultAllowed(true);
          setChoirDiscountCode(data.discountCode || null);
          setChoirLinkMessage('Choir family offer: 20% off plus one free Parent / Carer ticket.');
        } else {
          setChoirFreeAdultAllowed(false);
          setChoirDiscountCode(null);
          setChoirLinkMessage(data?.message || 'This choir booking link is not valid.');
        }
      } catch (err) {
        if (cancelled) return;
        setChoirFreeAdultAllowed(false);
        setChoirDiscountCode(null);
        setChoirLinkMessage('Unable to verify this choir booking link. Please try again or email info@belvoircastle.com.');
      }
    };
    validateChoirLink();
    return () => {
      cancelled = true;
    };
  }, [choirBookingToken]);
  
  // Handle return from GoCardless direct debit setup
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const mandateId = searchParams.get('mandate');
    const sessionToken = searchParams.get('session_token');
    const redirectFlowId = searchParams.get('redirect_flow_id');
    
    // Check if we're returning from GoCardless
    if (mandateId || sessionToken || redirectFlowId) {
      console.log('💳 [Direct Debit] Returned from GoCardless:', { mandateId, sessionToken, redirectFlowId });
      
      // If we're on step 5 (payment) and have a mandate, complete the booking
      if (currentStep === 5 && (mandateId || sessionToken) && basketId) {
        // Create a payment result object for direct debit
        const directDebitPaymentResult = {
          paymentToken: sessionToken || mandateId || 'DIRECT_DEBIT',
          orderId: basketId,
          transactionId: mandateId || sessionToken,
          paymentMethod: 'DirectDebit',
          success: true,
          isDirectDebit: true
        };
        
        setPaymentResult(directDebitPaymentResult);
        
        // Process the booking with the direct debit payment result
        processBooking(directDebitPaymentResult).catch(async (error) => {
          console.error('❌ [Direct Debit] Error processing booking:', error);
          
          // Monitor critical error: payment taken but booking failed
          await monitorPaymentTakenNoBooking(
            directDebitPaymentResult,
            basketId,
            error
          );
          
          dispatch({ type: BOOKING_ACTIONS.SET_ERROR, payload: 'Failed to complete direct debit payment. Please contact support.' });
        });
      }
    }
  }, [location.search, currentStep, basketId, dispatch]);

  // Smoothly scroll to top whenever the step changes (improves UX across steps)
  useEffect(() => {
    if (currentStep === 1) return; // No scroll on initial Select Tickets screen
    // Use requestAnimationFrame to ensure the next step has rendered before scrolling
    requestAnimationFrame(() => {
      const el = topRef?.current;
      if (el && typeof el.getBoundingClientRect === 'function') {
        const rect = el.getBoundingClientRect();
        const currentY = window.scrollY || window.pageYOffset || 0;
        const offset = 140; // show section title without jumping to full banner
        const targetY = Math.max(0, currentY + rect.top - offset);
        window.scrollTo({ top: targetY, behavior: 'smooth' });
        return;
      }
      // Fallback
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }, [currentStep]);

  // Helper function to log discount events
  const logDiscountEvent = async (eventType, discountCode, amount, details = '', status = 'SUCCESS') => {
    try {
      const hostname = window.location.hostname;
      let apiBaseUrl;
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        apiBaseUrl = 'http://localhost:8888/Website/front-end/public/api';
      } else if (hostname.includes('staging.belvoircastle.com')) {
        apiBaseUrl = 'https://staging.belvoircastle.com/api';
      } else {
        apiBaseUrl = 'https://www.belvoircastle.com/api';
      }
      
      await fetch(`${apiBaseUrl}/log-discount-event.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventType,
          discountCode,
          amount,
          bookingRef: upgradeFromBooking || 'N/A',
          locationId,
          details,
          status
        })
      });
    } catch (error) {
      console.error('❌ [LOGGING] Failed to log discount event:', error);
      // Don't throw - logging is non-critical
    }
  };

  // Helper function to undo all created discount codes
  /**
   * Create Circus discount code dynamically
   * Called when the basket includes a circus show ticket — calculates 20% off eligible entry lines
   */
  const createCircusDiscountIfNeeded = async () => {
    // Check if circus discount is needed
    const circusData = calculateCircusDiscountAmount();
    const circusAmountRaw = circusData.amount || circusData; // Support old format (number) and new format (object)
    const circusAmount = parseFloat(circusAmountRaw) || 0; // Ensure it's a number
    const eligibleItemsCount = circusData.eligibleItemsCount || 1;
    
    if (circusAmount <= 0) {
      console.log('🎪 No circus discount needed (no qualifying circus ticket or eligible amount is 0)');
      return null;
    }

    // If we already created a circus code for this session, reuse it
    if (circusDiscountCodeState) {
      console.log('🎪 Reusing existing circus discount code:', circusDiscountCodeState);
      return circusDiscountCodeState;
    }

    console.log('🎪 Creating circus discount code for £' + circusAmount.toFixed(2), 'across', eligibleItemsCount, 'eligible items');

    try {
      // Determine API base URL
      // Use production server for API calls (even when testing locally)
      let apiBaseUrl = 'https://www.belvoircastle.com/api';
      if (typeof window !== 'undefined') {
        const hostname = window.location.hostname;
        if (hostname.includes('staging')) {
          apiBaseUrl = 'https://staging.belvoircastle.com/api';
        }
        // For localhost/local dev, use production API
      }

      const createDiscountUrl = `${apiBaseUrl}/create-circus-discount.php`;
      
      console.log('🎪 [CIRCUS] API URL:', createDiscountUrl);
      console.log('🎪 [CIRCUS] Hostname:', typeof window !== 'undefined' ? window.location.hostname : 'N/A');
      
      // Generate session ID (use basketId or create one)
      const sessionId = basketId || `circus-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      const apiKey = process.env.REACT_APP_DISCOUNT_API_KEY || '';

      const response = await fetch(createDiscountUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(apiKey ? { 'X-API-Key': apiKey } : {})
        },
        body: JSON.stringify({
          locationId: locationId,
          amount: circusAmount,
          sessionId: sessionId,
          eligibleItemsCount: eligibleItemsCount, // Total quantity of eligible items for per-item discount calculation
          expiresInDays: 1 // Expires same day
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create circus discount code');
      }

      const data = await response.json();

      if (data.success && data.discountCode) {
        console.log('✅ Circus discount code created:', data.discountCode, '£' + parseFloat(data.amount || 0).toFixed(2));
        setCircusDiscountCodeState(data.discountCode);
        setCreatedDiscountCodes(prev => [...prev, data.discountCode]);
        
        // Log the creation
        await logDiscountEvent(
          'CREATED',
          data.discountCode,
          data.amount,
          'Circus 20% off eligible items (fixed amount discount)',
          'SUCCESS'
        );

        return data.discountCode;
      } else {
        throw new Error('Invalid response from circus discount API');
      }
    } catch (error) {
      console.error('❌ Failed to create circus discount code:', error);
      console.warn('⚠️ Continuing without circus discount - booking will proceed with Family Saver only');
      
      // Log the failure (but don't let logging failure stop the booking)
      try {
        await logDiscountEvent(
          'CREATION_FAILED',
          'CIRCUS-XXXXX',
          circusAmount,
          `Failed to create circus discount: ${error.message}`,
          'FAILURE'
        );
      } catch (logError) {
        console.warn('⚠️ Failed to log circus discount error (non-critical):', logError);
      }

      // Return null so booking continues without circus discount
      return null;
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // CHILDREN'S SUMMER RETURN DISCOUNT
  // Triggered after a successful June booking when the basket contains child
  // admission tickets (ADMISS00004 or ADMISS00008). Creates a Venpos discount
  // code worth the full value of those tickets, redeemable during summer
  // holidays (1 Jul – 1 Sep 2026), then emails it to the customer.
  //
  // ⚡ FEATURE FLAG — set to true to re-enable the offer:
  const CHILDREN_RETURN_OFFER_ENABLED = true;
  // ─────────────────────────────────────────────────────────────────────────

  /** Returns true when the selected visit date falls in June. */
  const isJuneVisit = () => {
    if (!selectedDate) return false;
    const d = selectedDate instanceof Date ? selectedDate : new Date(selectedDate);
    return d.getMonth() === 5; // 0-indexed: 5 = June
  };

  /**
   * Only Castle & Gardens or Garden & Playground child admission (ADMISS00004 / ADMISS00008).
   * Excludes Afternoon Tea Child, Animal Park Child, guided tours, and other child upsells.
   */
  const isEligibleChildrenReturnTicket = (name, productCode) => {
    const code = (productCode || '').trim().toUpperCase();
    if (code === 'ADMISS00004' || code === 'ADMISS00008') return true;

    const n = (name || '').toLowerCase().trim();
    if (!n.includes('child')) return false;

    // Safety net — never count known non-admission child products
    const excluded = [
      'afternoon tea',
      'animal park',
      'guided tour',
      'circus',
      'membership',
      'afternoon',
    ];
    if (excluded.some((term) => n.includes(term))) return false;

    const isCastleGardenChild =
      n.includes('castle and garden entry') && n.includes('child');
    const isGardenPlaygroundChild =
      (n.includes('garden and playground only entry') ||
        n.includes('garden and playground only')) &&
      n.includes('child');

    return isCastleGardenChild || isGardenPlaygroundChild;
  };

  /**
   * Sums the total price of child admission tickets in the current basket.
   * Detects child tickets by name (contains "child", case-insensitive) since
   * Venpos does not return a productCode on web sale items.
   */
  const calculateChildTicketsTotalValue = () => {
    // Prefer venposBasket item prices — these are the correct checkout prices (e.g. £10/£6).
    // selectedTickets uses the raw Venpos catalog price which can be £1 lower than the
    // actual selling price, resulting in a discount code that leaves the customer £1 short.
    const basketGroups = venposBasket?.data?.groups || [];
    const basketItems  = basketGroups.flatMap(g => g.items || []);

    if (basketItems.length > 0) {
      return basketItems.reduce((total, item) => {
        if (!isEligibleChildrenReturnTicket(item.name, item.productCode)) return total;
        const qty       = parseInt(item.quantity, 10) || 1;
        const unitPrice = parseFloat(item.unitPrice || item.price || 0);
        return total + unitPrice * qty;
      }, 0);
    }

    // Fallback to selectedTickets if basket isn't available yet
    return (selectedTickets || []).reduce((total, ticket) => {
      const qty  = parseInt(ticket.quantity, 10) || 0;
      if (qty <= 0) return total;
      if (!isEligibleChildrenReturnTicket(ticket.name, ticket.productCode)) return total;
      const unitPrice = parseFloat(ticket.price || ticket.unitPrice || 0);
      return total + unitPrice * qty;
    }, 0);
  };

  /**
   * Creates a children's summer return discount code in Venpos (if this is a
   * June booking with child admission tickets). Returns the code string on
   * success, or null if not applicable / on error.
   */
  const createChildrenReturnDiscount = async (sessionId) => {
    if (!isJuneVisit()) {
      return null;
    }

    const childTotal = calculateChildTicketsTotalValue();
    if (childTotal <= 0) {
      console.log('🎒 No child admission tickets found — skipping children\'s summer return discount');
      return null;
    }

    // Reuse code if already created in this session
    if (childrenReturnDiscountCode) {
      console.log('🎒 Reusing existing children\'s return discount code:', childrenReturnDiscountCode);
      return childrenReturnDiscountCode;
    }

    console.log(`🎒 Creating children's summer return discount for £${childTotal.toFixed(2)}`);

    try {
      let apiBaseUrl = 'https://www.belvoircastle.com/api';
      if (typeof window !== 'undefined' && window.location.hostname.includes('staging')) {
        apiBaseUrl = 'https://staging.belvoircastle.com/api';
      }

      const apiKey = process.env.REACT_APP_DISCOUNT_API_KEY || '';
      const sid    = sessionId || `child-return-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      const response = await fetch(`${apiBaseUrl}/create-children-return-discount.php`, {
        method:  'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(apiKey ? { 'X-API-Key': apiKey } : {})
        },
        body: JSON.stringify({
          locationId:    locationId,
          amount:        childTotal,
          sessionId:     sid,
          expiresInDays: 90 // Valid through the whole summer
        })
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      if (data.success && data.discountCode) {
        console.log('✅ Children\'s return discount created:', data.discountCode);
        setChildrenReturnDiscountCode(data.discountCode);
        return data.discountCode;
      }
      throw new Error('Invalid response from children return discount API');
    } catch (error) {
      console.error('❌ Failed to create children\'s return discount (non-fatal):', error);
      return null;
    }
  };

  /**
   * Sends the promotional "children return this summer holiday" email via
   * the PHP Mailgun endpoint. Called after the booking is confirmed.
   */
  const sendChildrenReturnEmail = async ({ code, amount, bookingReference, visitDate }) => {
    if (!code || !customerDetails?.email) return;

    // Count eligible child admission tickets only (excludes Afternoon Tea Child)
    const basketItems = (venposBasket?.data?.groups || []).flatMap(g => g.items || []);
    const childCount = basketItems.length > 0
      ? basketItems.reduce((sum, item) => {
          if (!isEligibleChildrenReturnTicket(item.name, item.productCode)) return sum;
          return sum + (parseInt(item.quantity, 10) || 1);
        }, 0)
      : (selectedTickets || []).reduce((sum, t) => {
          const qty = parseInt(t.quantity, 10) || 0;
          if (qty > 0 && isEligibleChildrenReturnTicket(t.name, t.productCode)) return sum + qty;
          return sum;
        }, 0);

    try {
      let apiBaseUrl = 'https://www.belvoircastle.com/api';
      if (typeof window !== 'undefined' && window.location.hostname.includes('staging')) {
        apiBaseUrl = 'https://staging.belvoircastle.com/api';
      }

      await fetch(`${apiBaseUrl}/send-children-return-email.php`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerEmail:    customerDetails.email,
          customerName:     `${customerDetails.firstName} ${customerDetails.lastName}`,
          discountCode:     code,
          discountAmount:   amount,
          childCount:       childCount,
          visitDate:        visitDate || '',
          bookingReference: bookingReference || '',
        })
      });

      console.log('✅ Children\'s summer return email sent to', customerDetails.email);
    } catch (error) {
      console.error('❌ Failed to send children\'s return email (non-fatal):', error);
    }
  };

  /**
   * Marks a children's summer return code as redeemed in our tracker.
   * Called after a successful summer return booking that used one of these codes.
   * Non-fatal — never blocks booking completion.
   */
  const markChildReturnCodeRedeemed = async (discountCode, redeemedBookingReference) => {
    if (!discountCode) return;
    try {
      let apiBaseUrl = 'https://www.belvoircastle.com/api';
      if (typeof window !== 'undefined' && window.location.hostname.includes('staging')) {
        apiBaseUrl = 'https://staging.belvoircastle.com/api';
      }
      await fetch(`${apiBaseUrl}/children-return-code-tracker.php`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:                    'redeem',
          discountCode:              discountCode,
          redeemedBookingReference:  redeemedBookingReference || '',
        })
      });
      console.log('✅ [CHILD-RETURN] Code marked redeemed:', discountCode);
    } catch (error) {
      console.warn('⚠️ [CHILD-RETURN] Failed to mark code redeemed (non-fatal):', error);
    }
  };

  const undoAllDiscountCodes = async () => {
    if (createdDiscountCodes.length === 0) {
      alert('No discount codes to undo');
      return;
    }
    
    const confirmUndo = window.confirm(
      `Are you sure you want to delete ${createdDiscountCodes.length} discount code(s)?\n\n` +
      `Codes: ${createdDiscountCodes.join(', ')}\n\n` +
      `This action cannot be undone.`
    );
    
    if (!confirmUndo) return;
    
    setIsUndoingDiscounts(true);
    setUpgradeDiscountMessage('Deleting discount codes...');
    
    try {
      const hostname = window.location.hostname;
      let apiBaseUrl;
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        apiBaseUrl = 'http://localhost:8888/Website/front-end/public/api';
      } else if (hostname.includes('staging.belvoircastle.com')) {
        apiBaseUrl = 'https://staging.belvoircastle.com/api';
      } else {
        apiBaseUrl = 'https://www.belvoircastle.com/api';
      }
      
      let deletedCount = 0;
      let failedCodes = [];
      
      for (const code of createdDiscountCodes) {
        try {
          const response = await fetch(`${apiBaseUrl}/delete-upgrade-discount.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              locationId,
              discountCode: code,
              reason: 'User requested undo/rollback'
            })
          });
          
          const result = await response.json();
          
          if (result.success) {
            deletedCount++;
            await logDiscountEvent('UNDO', code, upgradeDiscountValue, 'User requested undo', 'SUCCESS');
          } else {
            failedCodes.push(code);
            await logDiscountEvent('UNDO', code, upgradeDiscountValue, `Failed: ${result.error}`, 'ERROR');
          }
        } catch (error) {
          console.error(`❌ [UNDO] Failed to delete code ${code}:`, error);
          failedCodes.push(code);
          await logDiscountEvent('UNDO', code, upgradeDiscountValue, `Error: ${error.message}`, 'ERROR');
        }
      }
      
      // Clear the created codes list
      setCreatedDiscountCodes([]);
      setUpgradeDiscountCodeState(null);
      setCircusDiscountCodeState(null);
      setUpgradeDiscountApplied(false);
      setUpgradeDiscountValue(0);
      
      if (failedCodes.length === 0) {
        setUpgradeDiscountMessage(`✅ Successfully deleted ${deletedCount} discount code(s)`);
        setTimeout(() => setUpgradeDiscountMessage(''), 3000);
      } else {
        setUpgradeDiscountMessage(
          `⚠️ Deleted ${deletedCount} code(s), but ${failedCodes.length} failed: ${failedCodes.join(', ')}`
        );
      }
    } catch (error) {
      console.error('❌ [UNDO] Error during undo operation:', error);
      setUpgradeDiscountMessage(`❌ Error during undo: ${error.message}`);
    } finally {
      setIsUndoingDiscounts(false);
    }
  };

  // Create a stable string key from ticket quantities to use as dependency
  const ticketQuantitiesKey = useMemo(() => {
    return selectedTickets.map(t => `${t.webSaleItemId}:${t.quantity}`).join(',');
  }, [selectedTickets]);
  
  // Track which ticket types are selected (regardless of quantity) for detecting significant changes
  const selectedTicketTypes = useMemo(() => {
    return selectedTickets
      .filter(t => t.quantity > 0)
      .map(t => t.webSaleItemId)
      .sort()
      .join(',');
  }, [selectedTickets]);

  const upsellCriteriaKey = useMemo(() => {
    if (!selectedDate || !selectedTimeSlot) return null;
    const dateKey = selectedDate.toISOString();
    const timeKey = selectedTimeSlot.eventCapacityUsageId || selectedTimeSlot.time || selectedTimeSlot.startDateTime || '';
    return `${dateKey}|${timeKey}`;
  }, [selectedDate, selectedTimeSlot]);
  
  // Previous ticket types (to detect when user changes ticket selection after going back)
  const prevTicketTypesRef = useRef(selectedTicketTypes);
  const lastUpsellCriteriaRef = useRef(null);
  
  // Detect significant ticket changes and clear dependent state
  useEffect(() => {
    // Only monitor when on step 1 (ticket selection)
    if (currentStep !== 1) {
      // Update ref when not on step 1
      prevTicketTypesRef.current = selectedTicketTypes;
      return;
    }
    
    // Check if ticket types have changed (not just quantities)
    if (prevTicketTypesRef.current && prevTicketTypesRef.current !== selectedTicketTypes) {

      // Clear date/time selection as it might not be valid for new ticket types
      if (selectedDate) {

        dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_DATE, payload: null });
        dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_TIME_SLOT, payload: null });
      }
      
      // Clear upsells as they depend on ticket selection
      if (selectedUpsells.length > 0) {

        dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_UPSELLS, payload: [] });
        // Also remove upsell tickets from selectedTickets
        const nonUpsellTickets = selectedTickets.filter(t => !t.isUpsellItem && t.pageType !== 'EventTemplate');
        dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_TICKETS, payload: nonUpsellTickets });
      }
      
      // Clear basket
      if (basketId) {

        dispatch({ type: BOOKING_ACTIONS.SET_BASKET_ID, payload: null });
        dispatch({ type: BOOKING_ACTIONS.SET_VENPOS_BASKET, payload: null });
      }
    }
    
    // Update ref for next comparison
    prevTicketTypesRef.current = selectedTicketTypes;
  }, [currentStep, selectedTicketTypes, selectedDate, selectedUpsells.length, basketId, dispatch, selectedTickets]);
  
  // Helper function to clear upsells and basket after date/time change confirmation
  const clearUpsellsAndBasket = () => {

    // Clear any existing errors
    dispatch({ type: BOOKING_ACTIONS.CLEAR_ERROR });
    
    // Clear selected upsells and remove upsell tickets
    dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_UPSELLS, payload: [] });
    const nonUpsellTickets = selectedTickets.filter(t => !t.isUpsellItem && t.pageType !== 'EventTemplate');
    if (nonUpsellTickets.length !== selectedTickets.length) {
      dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_TICKETS, payload: nonUpsellTickets });
    }

    // Clear available upsells so step 3 fetches fresh upsells
    dispatch({ type: BOOKING_ACTIONS.SET_AVAILABLE_UPSELLS, payload: [] });

    // Clear basket to force recreation
    dispatch({ type: BOOKING_ACTIONS.SET_BASKET_ID, payload: null });
    dispatch({ type: BOOKING_ACTIONS.SET_VENPOS_BASKET, payload: null });
    dispatch({ type: BOOKING_ACTIONS.SET_PREFLIGHT_DONE, payload: false });
  };
  
  const handleConfirmDateTimeChange = async () => {
    if (pendingDateTimeChange) {
      // Clear upsells and apply the pending change
      clearUpsellsAndBasket();
      
      if (pendingDateTimeChange.type === 'date') {
        dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_DATE, payload: pendingDateTimeChange.value });
      } else if (pendingDateTimeChange.type === 'time') {
        // For time changes, we need to process capacity reservation
        const timeSlot = pendingDateTimeChange.value;
        
        try {
          const items = selectedTickets
            .filter(ticket => (ticket.quantity || 0) > 0)
            .map(ticket => ({
              pageType: 'WebPage',
              pageId: pageId,
              webSaleItemId: ticket.webSaleItemId,
              webSaleItemTypeID: ticket.webSaleItemTypeID,
              quantity: ticket.quantity,
              selectedDate: formatDateLocal(selectedDate) + 'T' + (timeSlot.time || timeSlot) + ':00',
              fixedDonationAmount: 0,
              name: ticket.name,
              giftAidEligible: false,
              isSysGift: false,
              isSysMemberCard: false
            }));

          const capacityResponse = await venposService.addEventCapacityUsages(
            locationId,
            pageId,
            items
          );

          if (!capacityResponse.isError && capacityResponse.data) {
            const responseItems = capacityResponse.data.items || capacityResponse.data || [];
            if (responseItems.length > 0 && responseItems[0].eventCapacityUsageId) {
              const enrichedTimeSlot = {
                ...timeSlot,
                eventCapacityUsageId: responseItems[0].eventCapacityUsageId,
                eventId: responseItems[0].eventId || timeSlot.eventId
              };
              dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_TIME_SLOT, payload: enrichedTimeSlot });
              
              // Update tickets with capacity IDs
              const updatedTickets = selectedTickets.map(ticket => {
                if (ticket.quantity > 0) {
                  const matchingResponse = responseItems.find(item => 
                    item.webSaleItemId === ticket.webSaleItemId
                  );
                  if (matchingResponse) {
                    return {
                      ...ticket,
                      eventCapacityUsageId: matchingResponse.eventCapacityUsageId,
                      eventId: matchingResponse.eventId
                    };
                  }
                }
                return ticket;
              });
              dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_TICKETS, payload: updatedTickets });
            } else {
              dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_TIME_SLOT, payload: timeSlot });
            }
          } else {
            dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_TIME_SLOT, payload: timeSlot });
          }
        } catch (e) {

          dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_TIME_SLOT, payload: timeSlot });
        }
      }
    }
    
    setShowDateTimeChangeModal(false);
    setPendingDateTimeChange(null);
  };

  // Initialize booking with location and page ID from URL params or state
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const locationIdParam = urlParams.get('locationId') || location.state?.locationId;
    const pageIdParam = urlParams.get('pageId') || location.state?.pageId;
    const upgradeFromBookingParam = urlParams.get('upgradeFromBooking');
    const pinnedDateParam = parseVisitDateParam(urlParams.get('date'));
    setPinnedVisitDate(pinnedDateParam);
    // filterMonth: restrict calendar to a specific month, e.g. ?filterMonth=2026-06
    const filterMonthParam = urlParams.get('filterMonth');
    if (filterMonthParam && /^\d{4}-\d{2}$/.test(filterMonthParam)) {
      setFilterMonth(filterMonthParam);
    }

    if (locationIdParam) {
      dispatch({ type: BOOKING_ACTIONS.SET_LOCATION_ID, payload: locationIdParam });
    }
    if (pageIdParam) {
      dispatch({ type: BOOKING_ACTIONS.SET_PAGE_ID, payload: pageIdParam });
    }
    
    // Store upgrade booking reference if present
    if (upgradeFromBookingParam) {
      setUpgradeFromBooking(upgradeFromBookingParam);
    }
  }, [location, dispatch]);

  // Fetch page config (Family Saver enabled/disabled) from Security Center when pageId is set
  useEffect(() => {
    if (!pageId) return;
    let cancelled = false;
    const apply = async () => {
      try {
        const configData = await getPageConfigByPageId(pageId);
        if (cancelled) return;
        const enabled = configData?.family_saver_enabled !== false;
        dispatch({ type: BOOKING_ACTIONS.SET_FAMILY_SAVER_ENABLED, payload: enabled });
        if (process.env.NODE_ENV === 'development' && configData != null) {
          console.log('🎟️ [BookingJourney] Page config:', { pageId, family_saver_enabled: configData.family_saver_enabled, applied: enabled });
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('⚠️ [BookingJourney] Failed to fetch page config, defaulting Family Saver enabled:', err);
          dispatch({ type: BOOKING_ACTIONS.SET_FAMILY_SAVER_ENABLED, payload: true });
        }
      }
    };
    apply();
    return () => { cancelled = true; };
  }, [pageId, dispatch]);

  // Fetch original booking data when upgradeFromBooking is set
  useEffect(() => {
    const fetchOriginalBooking = async () => {
      if (!upgradeFromBooking) return;
      
      // Reset expiry flags for new link
      setUpgradeLinkExpired(false);
      setUpgradeLinkErrorMessage('');

      setIsLoadingUpgradeBooking(true);
      try {
        console.log('🔄 [UPGRADE] Fetching original booking:', upgradeFromBooking);
        
        // Validate link freshness (sent within 24 hours)
        try {
          const validationUrl = `https://api.belvoircastle.com/api/visitor-bookings/membership-upgrade-status?transRef=${encodeURIComponent(upgradeFromBooking)}`;
          const validationResponse = await fetch(validationUrl, { method: 'GET' });
          console.log('[UPGRADE] Link validation response status:', validationResponse.status);
          if (validationResponse.ok) {
            const validationResult = await validationResponse.json();
            console.log('[UPGRADE] Link validation payload:', validationResult);
            const sentAtStr = validationResult.sentAt || validationResult.sent_at || validationResult.data?.sentAt;
            if (sentAtStr) {
              const sentAt = new Date(sentAtStr);
              const ageHours = (Date.now() - sentAt.getTime()) / (1000 * 60 * 60);
              console.log('[UPGRADE] Link age (hours):', ageHours.toFixed(2));
              if (ageHours > 24) {
                const msg = 'This upgrade link has expired (over 24 hours old). Please email info@belvoircastle.com for assistance.';
                setUpgradeLinkExpired(true);
                setUpgradeLinkErrorMessage(msg);
                setIsLoadingUpgradeBooking(false);
                return;
              }
            }
          } else {
            console.warn('[UPGRADE] Link validation request failed with status:', validationResponse.status);
          }
        } catch (validationErr) {
          console.warn('⚠️ [UPGRADE] Link validation skipped (validation fetch failed):', validationErr);
        }

        // Note: We'll check for used items after we fetch the booking data
        // so we can filter out already-used items
        
        const apiUrl = `https://api.belvoircastle.com/api/visitor-bookings/booking-by-transref?upgradeFromBooking=${encodeURIComponent(upgradeFromBooking)}`;
        
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch booking: ${response.status} ${response.statusText}`);
        }
        
        const result = await response.json();
        
        if (result.success && result.booking) {
          const booking = result.booking;
          console.log('✅ [UPGRADE] Original booking fetched:', {
            transRef: booking.transRef,
            totalValue: booking.totalValue,
            items: booking.items?.length || 0,
            isRedeemed: booking.isRedeemed
          });
          
          setOriginalBookingData(booking);
          
          // Pre-fill customer details from original booking if available
          if (booking.customerName || booking.email || booking.mobile || booking.address) {
            // Parse customer name into first and last name
            // Handle titles (Mr, Mrs, Miss, Ms, Dr, etc.) that might be included in the name
            let firstName = '';
            let lastName = '';
            if (booking.customerName) {
              const nameParts = booking.customerName.trim().split(/\s+/);
              
              // Common titles that might appear at the start of the name
              const titles = ['mr', 'mrs', 'miss', 'ms', 'dr', 'prof', 'professor', 'sir', 'dame', 'lord', 'lady', 'rev', 'reverend'];
              
              let startIndex = 0;
              // Check if first part is a title (case-insensitive)
              if (nameParts.length > 0 && titles.includes(nameParts[0].toLowerCase())) {
                startIndex = 1; // Skip the title
              }
              
              if (nameParts.length > startIndex) {
                // First name is the first non-title part
                firstName = nameParts[startIndex] || '';
                // Last name is everything after the first name
                lastName = nameParts.slice(startIndex + 1).join(' ') || '';
              }
            }
            
            // Fetch titles to match title text to ID
            let titleId = null;
            if (booking.title && !customerDetails.selectedTitle) {
              try {
                const titlesResponse = await venposService.getTitles(locationId);
                if (!titlesResponse.isError && titlesResponse.data && Array.isArray(titlesResponse.data)) {
                  // Match title text (case-insensitive) to find the ID
                  const matchedTitle = titlesResponse.data.find(t => 
                    t.title && t.title.toLowerCase() === booking.title.toLowerCase()
                  );
                  if (matchedTitle && matchedTitle.lookupTitleID) {
                    titleId = matchedTitle.lookupTitleID;
                    console.log(`👤 [UPGRADE] Matched title "${booking.title}" to ID: ${titleId}`);
                  } else {
                    console.log(`⚠️ [UPGRADE] Could not match title "${booking.title}" to Venpos title IDs`);
                  }
                }
              } catch (error) {
                console.error('❌ [UPGRADE] Error fetching titles for matching:', error);
              }
            }
            
            // Only update if customer details are not already filled
            // This prevents overwriting user-entered data
            const updatedDetails = {};
            const updatedAddress = {};
            
            // Personal information
            if (firstName && !customerDetails.firstName) {
              updatedDetails.firstName = firstName;
            }
            if (lastName && !customerDetails.lastName) {
              updatedDetails.lastName = lastName;
            }
            if (titleId && !customerDetails.selectedTitle) {
              updatedDetails.selectedTitle = titleId;
            }
            if (booking.email && !customerDetails.email) {
              updatedDetails.email = booking.email;
              updatedDetails.confirmEmail = booking.email; // Pre-fill confirm email too
            }
            if (booking.mobile && !customerDetails.phone) {
              updatedDetails.phone = booking.mobile;
            }
            
            // Address information
            if (booking.address) {
              const addr = booking.address;
              
              if (addr.nameNumber && !customerDetails.address?.houseNumber) {
                updatedAddress.houseNumber = addr.nameNumber;
              }
              if (addr.line1 && !customerDetails.address?.line1) {
                updatedAddress.line1 = addr.line1;
              }
              // Map line2, and if line3 exists and line2 is empty, use line3
              if (addr.line2 && !customerDetails.address?.line2) {
                updatedAddress.line2 = addr.line2;
              } else if (addr.line3 && !customerDetails.address?.line2 && !addr.line2) {
                updatedAddress.line2 = addr.line3;
              }
              if (addr.town && !customerDetails.address?.city) {
                updatedAddress.city = addr.town;
              }
              if (addr.postcode && !customerDetails.address?.postcode) {
                updatedAddress.postcode = addr.postcode;
              }
              if (addr.country && !customerDetails.address?.country) {
                // Map country name to country code if needed
                const countryCode = addr.country === 'United Kingdom' ? 'GB' : addr.country;
                updatedAddress.country = countryCode;
              }
            }
            
            // Update customer details if we have any updates
            if (Object.keys(updatedDetails).length > 0) {
              console.log('👤 [UPGRADE] Pre-filling customer details from original booking:', updatedDetails);
              dispatch({ 
                type: BOOKING_ACTIONS.UPDATE_CUSTOMER_DETAILS, 
                payload: updatedDetails 
              });
            }
            
            // Update address if we have any updates
            if (Object.keys(updatedAddress).length > 0) {
              console.log('🏠 [UPGRADE] Pre-filling address from original booking:', updatedAddress);
              dispatch({ 
                type: BOOKING_ACTIONS.UPDATE_CUSTOMER_ADDRESS, 
                payload: updatedAddress 
              });
            }
          }
          
          // Process and filter items - separate adult and child product codes.
          // Prefer product-code routing (e.g. Flower & Garden → Castle membership) over a
          // membership pageId already in the URL — wrong email links were wiping credit by
          // forcing Parkland, which only accepts garden & playground admissions.
          if (booking.items && Array.isArray(booking.items)) {
            const KNOWN_MEMBERSHIP_PAGE_IDS = [CASTLE_MEMBERSHIP_PAGE_ID, PARKLAND_MEMBERSHIP_PAGE_ID];
            const resolvedFromItems = resolveUpgradeMembershipPageId(booking.items);
            const urlPageIdIsKnown = KNOWN_MEMBERSHIP_PAGE_IDS.includes(pageId);

            const resolvedMembershipPageId =
              resolvedFromItems || (urlPageIdIsKnown ? pageId : null);

            const membershipPageId = resolvedMembershipPageId || pageId;

            if (resolvedFromItems && urlPageIdIsKnown && resolvedFromItems !== pageId) {
              console.log(
                '🔄 [UPGRADE] URL membership page does not match booking products; correcting:',
                { from: pageId, to: resolvedFromItems }
              );
            }

            if (resolvedMembershipPageId && resolvedMembershipPageId !== pageId) {
              console.log('🔄 [UPGRADE] Routing to membership page:', resolvedMembershipPageId);
              dispatch({
                type: BOOKING_ACTIONS.SET_PAGE_ID,
                payload: resolvedMembershipPageId,
              });
              const newParams = new URLSearchParams(location.search);
              newParams.set('pageId', resolvedMembershipPageId);
              if (locationId) {
                newParams.set('locationId', String(locationId));
              }
              navigate(
                { pathname: location.pathname, search: `?${newParams.toString()}` },
                { replace: true }
              );
            }

            const { childItems: sortedChildItems, adultItems: sortedAdultItems } =
              partitionUpgradeItemsForMembershipPage(booking.items, membershipPageId);
            
            // These track the items that are actually available after filtering used ones.
            // Defaulting to the full lists; updated below if the tracker finds used items.
            let finalChildItems = sortedChildItems;
            let finalAdultItems = sortedAdultItems;

            // Check which items have already been used (for both child and adult)
            const allItemsToCheck = [...sortedChildItems, ...sortedAdultItems];
            
            if (allItemsToCheck.length > 0) {
              try {
                const hostname = window.location.hostname;
                let trackerBaseUrl;
                if (hostname === 'localhost' || hostname === '127.0.0.1') {
                  trackerBaseUrl = 'http://localhost:8888/Website/front-end/public/api';
                } else if (hostname.includes('staging.belvoircastle.com')) {
                  trackerBaseUrl = 'https://staging.belvoircastle.com/api';
                } else {
                  trackerBaseUrl = 'https://www.belvoircastle.com/api';
                }
                
                // Create item identifiers for checking (both child and adult items)
                // Use webTransItemId if available (unique per item instance), otherwise use index
                const itemIdentifiers = allItemsToCheck.map((item, index) => {
                  const uniqueId = item.webTransItemId || `index_${index}`;
                  return `${upgradeFromBooking}|${item.productCode}|${parseFloat(item.value).toFixed(2)}|${uniqueId}`;
                });
                
                const trackerUrl = `${trackerBaseUrl}/upgrade-booking-tracker.php?bookingRef=${encodeURIComponent(upgradeFromBooking)}&itemIdentifiers=${encodeURIComponent(JSON.stringify(itemIdentifiers))}`;
                
                const trackerResponse = await fetch(trackerUrl, {
                  method: 'GET',
                  headers: {
                    'Content-Type': 'application/json'
                  }
                });
                
                if (trackerResponse.ok) {
                  const contentType = trackerResponse.headers.get('content-type');
                  if (contentType && contentType.includes('application/json')) {
                    const trackerResult = await trackerResponse.json();
                    if (trackerResult.success && trackerResult.used_items) {
                      const usedItemIdentifiers = Object.keys(trackerResult.used_items);
                      
                      // Separate already-used vs available items (child and adult)
                      const availableChildItems = [];
                      const usedChildItems = [];
                      
                      sortedChildItems.forEach((item, index) => {
                        const uniqueId = item.webTransItemId || `child_index_${index}`;
                        const itemIdentifier = `${upgradeFromBooking}|${item.productCode}|${parseFloat(item.value).toFixed(2)}|${uniqueId}`;
                        const isUsed = usedItemIdentifiers.includes(itemIdentifier);
                        if (isUsed) {
                          console.log(`🚫 [UPGRADE] Child item already used: ${item.productDescription} (${item.productCode}) - £${item.value} [ID: ${uniqueId}]`);
                          usedChildItems.push(item);
                        } else {
                          availableChildItems.push(item);
                        }
                      });
                      
                      const availableAdultItems = [];
                      const usedAdultItems = [];
                      
                      sortedAdultItems.forEach((item, index) => {
                        const uniqueId = item.webTransItemId || `adult_index_${index}`;
                        const itemIdentifier = `${upgradeFromBooking}|${item.productCode}|${parseFloat(item.value).toFixed(2)}|${uniqueId}`;
                        const isUsed = usedItemIdentifiers.includes(itemIdentifier);
                        if (isUsed) {
                          console.log(`🚫 [UPGRADE] Adult item already used: ${item.productDescription} (${item.productCode}) - £${item.value} [ID: ${uniqueId}]`);
                          usedAdultItems.push(item);
                        } else {
                          availableAdultItems.push(item);
                        }
                      });
                      
                      if (availableChildItems.length === 0 && availableAdultItems.length === 0) {
                        console.warn('⚠️ [UPGRADE] All items from this booking have already been used');
                        dispatch({ 
                          type: BOOKING_ACTIONS.SET_ERROR, 
                          payload: `All upgrade credits from this booking (${upgradeFromBooking}) have already been used. Please contact support if you need assistance.` 
                        });
                        setIsLoadingUpgradeBooking(false);
                        setUpgradeCreditAmount(0);
                        setSortedUpgradeItems({ child: [], adult: [] });
                        return;
                      }
                      
                      setSortedUpgradeItems({
                        child: availableChildItems,
                        adult: availableAdultItems
                      });
                      setUsedUpgradeItems({
                        child: usedChildItems,
                        adult: usedAdultItems
                      });
                      // Update final items so the credit total only reflects available credits
                      finalChildItems = availableChildItems;
                      finalAdultItems = availableAdultItems;
                    } else {
                      // No items used yet, use all items
                      setSortedUpgradeItems({
                        child: sortedChildItems,
                        adult: sortedAdultItems
                      });
                      setUsedUpgradeItems({ child: [], adult: [] });
                    }
                  } else {
                    // API returned non-JSON, use all items
                    setSortedUpgradeItems({
                      child: sortedChildItems,
                      adult: sortedAdultItems
                    });
                    setUsedUpgradeItems({ child: [], adult: [] });
                  }
                } else {
                  // Tracker check failed, use all items (graceful degradation)
                  console.warn('⚠️ [UPGRADE] Could not check item status, using all items');
                  setSortedUpgradeItems({
                    child: sortedChildItems,
                    adult: sortedAdultItems
                  });
                  setUsedUpgradeItems({ child: [], adult: [] });
                }
              } catch (trackerError) {
                // If tracker check fails, use all items (graceful degradation)
                console.warn('⚠️ [UPGRADE] Could not check item status:', trackerError);
                setSortedUpgradeItems({
                  child: sortedChildItems,
                  adult: sortedAdultItems
                });
                setUsedUpgradeItems({ child: [], adult: [] });
              }
            } else {
              setSortedUpgradeItems({
                child: sortedChildItems,
                adult: sortedAdultItems
              });
              setUsedUpgradeItems({ child: [], adult: [] });
            }
            
            // Recalculate credit amount from only the items still available (not already used)
            const childCreditAmount = finalChildItems.reduce((total, item) => {
              return total + (parseFloat(item.value) || 0);
            }, 0);
            
            const adultCreditAmount = finalAdultItems.reduce((total, item) => {
              return total + (parseFloat(item.value) || 0);
            }, 0);
            
            setUpgradeCreditAmount(childCreditAmount + adultCreditAmount);
            
            console.log('📋 [UPGRADE] Filtered items for discount (lowest first):', {
              child: finalChildItems.map((item, index) => ({
                order: index + 1,
                productCode: item.productCode,
                description: item.productDescription,
                value: item.value,
                quantity: item.quantity
              })),
              adult: finalAdultItems.map((item, index) => ({
                order: index + 1,
                productCode: item.productCode,
                description: item.productDescription,
                value: item.value,
                quantity: item.quantity
              }))
            });
            
            console.log(`✅ [UPGRADE] Child items: ${finalChildItems.length} (sorted from lowest £${finalChildItems[0]?.value || 0} to highest £${finalChildItems[finalChildItems.length - 1]?.value || 0})`);
            console.log(`✅ [UPGRADE] Adult items: ${finalAdultItems.length} (sorted from lowest £${finalAdultItems[0]?.value || 0} to highest £${finalAdultItems[finalAdultItems.length - 1]?.value || 0})`);
            console.log(`💰 [UPGRADE] Total credit amount: £${(childCreditAmount + adultCreditAmount).toFixed(2)} (Child: £${childCreditAmount.toFixed(2)}, Adult: £${adultCreditAmount.toFixed(2)})`);
            
            // Log excluded items for debugging
            const excludedItems = booking.items.filter(
              (item) => !isUpgradeEligibleLineItem(item)
            );
            
            if (excludedItems.length > 0) {
              console.log('🚫 [UPGRADE] Excluded items (not admission codes):', excludedItems.map(item => ({
                productCode: item.productCode,
                description: item.productDescription,
                value: item.value
              })));
            }
          } else {
            setSortedUpgradeItems({ child: [], adult: [] });
            setUpgradeCreditAmount(0);
          }
        } else {
          throw new Error('Booking not found or invalid response');
        }
      } catch (error) {
        console.error('❌ [UPGRADE] Error fetching original booking:', error);
        // Don't block the user - they can still proceed without the upgrade credit
        setOriginalBookingData(null);
        setUpgradeCreditAmount(0);
        setSortedUpgradeItems({ child: [], adult: [] });
        setUsedUpgradeItems({ child: [], adult: [] });
      } finally {
        setIsLoadingUpgradeBooking(false);
      }
    };
    
    fetchOriginalBooking();
  }, [upgradeFromBooking]);

  useEffect(() => {
    if (!upgradeFromBooking) {
      setUpgradeDiscountApplied(false);
      setUpgradeDiscountCodeState(null);
      setUpgradeDiscountMessage('');
      setUpgradeDiscountValue(0);
    }
  }, [upgradeFromBooking]);

  // Load tickets when component mounts
  useEffect(() => {
    const loadTickets = async () => {
      if (!pageId) return;

      try {
        dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: true });

        const response = await venposService.getWebSaleItems(
          locationId,
          pageId,
          formatDateLocal(selectedDate) || formatDateLocal(new Date())
        );

        // Normalize various possible response shapes
        const items = (response?.data?.webSaleItems)
          || (response?.webSaleItems)
          || (response?.data?.items)
          || (response?.items)
          || [];

        // Ensure required fields exist; provide safe defaults
        // Preserve any quantities already chosen
        const existingQtyById = new Map((selectedTickets || []).map(t => [String(t.webSaleItemId), Number(t.quantity || 0)]));
        const normalized = items.map((it) => {
          const id = it.webSaleItemId || it.id || it.itemId || it.WebSaleItemId || it.WebSaleItemID;
          return {
            webSaleItemId: id,
            webSaleItemTypeID: it.webSaleItemTypeID || it.itemTypeId || it.WebSaleItemTypeID,
            name: it.name || it.title || 'Ticket',
            description: it.description || it.DESC || '',
            price: typeof it.price === 'number' ? it.price : (parseFloat(it.price) || parseFloat(it.unitPrice) || 0),
            quantity: existingQtyById.get(String(id)) || 0,
            // Preserve product code so child-ticket detection works for the summer return promo
            productCode: it.productCode || it.ProductCode || it.code || it.sku || '',
            maxPurchaseQty: isChoirFreeAdultTicket(it) ? 1 : (it.maxPurchaseQty || it.maxQty || 50),
          };
        }).filter(it => it.webSaleItemId);

        setTickets(normalized);
        // Initialize selected tickets in context so quantity updates work
        dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_TICKETS, payload: normalized });
      } catch (err) {
        dispatch({ type: BOOKING_ACTIONS.SET_ERROR, payload: 'Failed to load tickets. Please try again.' });

      } finally {
        dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: false });
      }
    };

    loadTickets();
  }, [pageId, locationId, dispatch]); // Removed selectedDate and selectedTickets to prevent infinite loop

  // Dog Park: auto-select the single product with qty 1 and jump to Date & Time step
  useEffect(() => {
    if (!isDogParkBooking) return;
    if (tickets.length === 0) return;
    if (currentStep !== 1) return; // Only trigger from step 1

    // Set quantity = 1 on the first (only) ticket; leave others at 0
    const autoSelected = tickets.map((ticket, index) => ({
      ...ticket,
      quantity: index === 0 ? 1 : 0,
    }));
    dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_TICKETS, payload: autoSelected });

    // Calendar fetch starts after this qty change — show a spinner immediately
    setDatesLoading(true);

    // Advance directly to the Date & Time step
    dispatch({ type: BOOKING_ACTIONS.SET_CURRENT_STEP, payload: 2 });
  }, [isDogParkBooking, tickets.length, currentStep, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load available dates when tickets are selected - only when user changes quantities
  // SKIP for membership journeys (no date selection needed)
  useEffect(() => {
    const loadAvailableDates = async () => {
      // Skip for membership journeys
      if (isMembershipJourney) return;
      
      if (!pageId || selectedTickets.length === 0) return;
      
      // Only load dates if we have tickets with quantities
      const hasTicketsWithQuantity = selectedTickets.some(ticket => ticket.quantity > 0);
      if (!hasTicketsWithQuantity) return;

      setDatesLoading(true);
      try {
        const selectedItems = selectedTickets
          .filter(ticket => ticket.quantity > 0)
          .map(ticket => ({
            pageType: 'WebPage',
            pageId: pageId,
            webSaleItemId: ticket.webSaleItemId,
            quantity: ticket.quantity,
            webSaleItemTypeID: ticket.webSaleItemTypeID
          }));

        const response = await venposService.getAvailableDates(
          locationId,
          pageId,
          {
            pageType: 'WebPage',
            selectedDate: formatDateLocal(new Date()),
            dateSelectorType: 'OnScreenCalendar'
          },
          selectedItems
        );

        if (!response.isError && response.data?.calendar) {
          const datesMap = {};
          response.data.calendar.forEach(dateInfo => {
            // Use the date string directly from VenPos (already in YYYY-MM-DD format)
            // Don't parse through new Date() to avoid timezone conversion
            const key = dateInfo.date; // Already in "YYYY-MM-DD" format
            datesMap[key] = Number(dateInfo.availability);
          });
          setAvailableDates(applyFestiveAfternoonTeaDateWindow(datesMap, restrictToFestiveTeaDates));
        }
      } catch (err) {

      } finally {
        setDatesLoading(false);
      }
    };

    loadAvailableDates();
  }, [pageId, locationId, ticketQuantitiesKey, isMembershipJourney, restrictToFestiveTeaDates]); // Only re-run when ticket quantities actually change

  // Auto-select visit date when ?date= is in the booking URL (e.g. Reptile Day 27 May)
  useEffect(() => {
    if (!pinnedVisitDate || isMembershipJourney || currentStep !== 2 || selectedDate) {
      return;
    }
    const availability = availableDates[pinnedVisitDate];
    if (availability !== 1) return;

    const date = visitDateFromYmdKey(pinnedVisitDate);
    if (!date) return;

    dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_DATE, payload: date });
  }, [
    pinnedVisitDate,
    availableDates,
    currentStep,
    selectedDate,
    isMembershipJourney,
    dispatch,
  ]);

  // Load time slots when date is selected
  // SKIP for membership journeys (no time selection needed)
  useEffect(() => {
    const loadTimeSlots = async () => {
      // Skip for membership journeys
      if (isMembershipJourney) return;
      
      if (!pageId || !selectedDate) return;
      
      // Only load if we have tickets with quantities
      const hasTicketsWithQuantity = selectedTickets.some(ticket => ticket.quantity > 0);
      if (!hasTicketsWithQuantity) return;

      try {
        const selectedItems = selectedTickets
          .filter(ticket => ticket.quantity > 0)
          .map(ticket => ({
            pageType: 'WebPage',
            pageId: pageId,
            webSaleItemId: ticket.webSaleItemId,
            quantity: ticket.quantity,
            webSaleItemTypeID: ticket.webSaleItemTypeID
          }));

        const response = await venposService.getTimeSlots(
          locationId,
          pageId,
          formatLocalDate(selectedDate),
          selectedItems
        );

        const slots = filterTimeSlotsForPinnedVisit(
          response?.data?.timeSlots || [],
          pinnedVisitDate
        );

        setTimeSlots(slots);

        if (selectedTimeSlot && isReptileDayHiddenTimeSlot(selectedTimeSlot, pinnedVisitDate)) {
          dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_TIME_SLOT, payload: null });
        }

        // For Flower and Garden Show 2026 (all-day), auto-select a time slot
        if (pageId === FLOWER_GARDEN_PAGE_ID && selectedDate) {
          const selectedDateKey = formatLocalDate(selectedDate);
          
          // CRITICAL: Use the actual slot from API if available, which includes eventId
          let autoSlot;
          if (slots && slots.length > 0) {
            autoSlot = slots[0];
            console.log('🌸 [Flower & Garden Show] Auto-selecting slot from API:', {
              time: autoSlot.time,
              eventId: autoSlot.eventId,
              eventCapacityUsageId: autoSlot.eventCapacityUsageId,
              startDateTime: autoSlot.startDateTime
            });
          } else {
            // FALLBACK ONLY - This should NOT happen if API is configured correctly
            console.error('❌ [Flower & Garden Show] No time slots returned from API! Using fallback (will cause basket issues)');
            autoSlot = { time: '10:00', label: '10:00', eventId: null, eventCapacityUsageId: null };
          }

          const currentSelectedDate = selectedTimeSlot?.selectedDate || selectedTimeSlot?.date || null;
          if (!selectedTimeSlot || currentSelectedDate !== selectedDateKey) {
            // CRITICAL FIX: Call addEventCapacityUsages to get eventCapacityUsageId
            // This is normally done in onTimeSlotSelect, but auto-selection bypasses that
            if (autoSlot.eventId && selectedTickets.some(t => t.quantity > 0)) {
              console.log('🌸 [Flower & Garden Show] Reserving capacity for auto-selected time slot');
              
              try {
                const items = selectedTickets
                  .filter(ticket => (ticket.quantity || 0) > 0)
                  .map(ticket => ({
                    pageType: 'WebPage',
                    pageId: pageId,
                    webSaleItemId: ticket.webSaleItemId,
                    webSaleItemTypeID: ticket.webSaleItemTypeID,
                    quantity: ticket.quantity,
                    selectedDate: autoSlot.startDateTime || `${selectedDateKey}T${autoSlot.time}:00`,
                    fixedDonationAmount: 0,
                    name: ticket.name,
                    giftAidEligible: false,
                    isSysGift: false,
                    isSysMemberCard: false,
                    reservationExpiryDateTime: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
                    eventId: autoSlot.eventId
                  }));

                const capacityResponse = await venposService.addEventCapacityUsages(
                  locationId,
                  pageId,
                  items,
                  null,
                  []
                );

                console.log('🌸 [Flower & Garden Show] Capacity response:', {
                  isError: capacityResponse.isError,
                  hasData: !!capacityResponse.data,
                  itemsCount: (capacityResponse.data?.items || capacityResponse.data || []).length
                });

                if (!capacityResponse.isError && capacityResponse.data) {
                  const responseItems = capacityResponse.data.items || capacityResponse.data || [];
                  
                  if (responseItems.length > 0 && responseItems[0].eventCapacityUsageId) {
                    const enrichedTimeSlot = {
                      ...autoSlot,
                      eventCapacityUsageId: responseItems[0].eventCapacityUsageId,
                      eventId: responseItems[0].eventId || autoSlot.eventId,
                      selectedDate: selectedDateKey
                    };
                    
                    console.log('🌸 [Flower & Garden Show] Enriched time slot with capacity ID:', {
                      time: enrichedTimeSlot.time,
                      eventId: enrichedTimeSlot.eventId,
                      eventCapacityUsageId: enrichedTimeSlot.eventCapacityUsageId
                    });

                    dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_TIME_SLOT, payload: enrichedTimeSlot });
                    
                    // Update tickets with capacity IDs
                    const updatedTickets = selectedTickets.map(ticket => {
                      if (ticket.quantity > 0) {
                        const matchingResponse = responseItems.find(item => 
                          item.webSaleItemId === ticket.webSaleItemId
                        );
                        if (matchingResponse) {
                          return {
                            ...ticket,
                            eventCapacityUsageId: matchingResponse.eventCapacityUsageId,
                            eventId: matchingResponse.eventId
                          };
                        }
                      }
                      return ticket;
                    });
                    dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_TICKETS, payload: updatedTickets });
                    return; // Skip the normal dispatch below
                  }
                }
              } catch (error) {
                console.error('❌ [Flower & Garden Show] Failed to reserve capacity:', error);
              }
            }
            
            // Fallback: set time slot without capacity ID (will cause payment issues)
            dispatch({
              type: BOOKING_ACTIONS.SET_SELECTED_TIME_SLOT,
              payload: { ...autoSlot, selectedDate: selectedDateKey }
            });
          }
        }
      } catch (err) {

      }
    };

    loadTimeSlots();
  }, [pageId, locationId, selectedDate, ticketQuantitiesKey, isMembershipJourney, pinnedVisitDate, dispatch]);


  // Enhanced basket management following API documentation
  const createAndValidateBasket = async (forceNew = false) => {
    try {

      // If we already have a valid basket and not forcing new, just refresh it
      if (basketId && !forceNew) {

        return await refreshExistingBasket();
      }

      // If forcing new or no existing basket, create fresh one but preserve existing customer/order IDs if available

      // If we already have valid basket IDs and not forcing new, skip creation
      if (!forceNew && basketOrderId && basketCustomerId) {

        return {
          isError: false,
          data: {
            basketId,
            basketDetails: null,
            loadResponse: null
          }
        };
      }

      // Use registered customer ID if available
      const actualCustomerId = registeredCustomerId;

      // DEBUG: Check what selectedTickets contains before processing

      // CRITICAL FIX: Expand tickets with quantity > 1 into individual tickets for event participants
      // Venpos events need individual participants, not consolidated quantities
      // NOTE: We expand tickets LOCALLY for the basket creation only, WITHOUT updating the global selectedTickets context
      // This prevents issues with price calculations when the basket is refreshed later
      const expandedTickets = [];
      selectedTickets
        .filter(ticket => (ticket.quantity || 0) > 0)
        .forEach(ticket => {
          const quantity = ticket.quantity || 1;
          
          if (quantity === 1) {
            // Single ticket - add as-is
            expandedTickets.push({ ...ticket, quantity: 1 });
          } else {
            // Multiple quantity - expand into individual tickets for each person

            for (let i = 0; i < quantity; i++) {
              expandedTickets.push({
                ...ticket,
                quantity: 1,  // Each person gets quantity 1
                personIndex: i,  // Track which person this is (0, 1, 2...)
                originalQuantity: quantity  // Remember original quantity for debugging
              });
            }
          }
        });

      // REMOVED: Do NOT update the booking context with expanded tickets
      // This was causing issues where refreshExistingBasket would use already-expanded tickets
      // and the Venpos backend would incorrectly group/calculate prices

      // Prepare basket items according to API specification with event metadata  
      const basketItems = expandedTickets
        .map((ticket, index) => {
          const basketItem = {
            pageType: 'WebPage',
            pageId: pageId,
            webSaleItemId: ticket.webSaleItemId,
            webSaleItemTypeID: ticket.webSaleItemTypeID,
            quantity: ticket.quantity,
            selectedDate: selectedTimeSlot?.time ?
              `${formatDateLocal(selectedDate || new Date())}T${selectedTimeSlot.time}:00` :
              formatDateLocal(selectedDate || new Date()),
            reservationExpiryDateTime: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
            name: ticket.name,
            giftAidEligible: false,
            isSysGift: false,
            isSysMemberCard: false,
            // Include event metadata - CRITICAL: Use ticket's own capacity ID if assigned, otherwise fallback to selectedTimeSlot
            eventId: ticket.eventId || selectedTimeSlot?.eventId || null,
            eventCapacityUsageId: ticket.eventCapacityUsageId || selectedTimeSlot?.eventCapacityUsageId || null
          };
          
          // DEBUG: Log each main booking item to track eventId assignment

          return basketItem;
        });

      if (basketItems.length === 0) {
        throw new Error('No valid basket items');
      }

      // Create basket with validation, using registered customer ID
      const basketResponse = await venposService.createBasketWithValidation(
        locationId,
        basketItems,
        customerDetails,
        basketOrderId || undefined,    // Pass existing order ID if available
        actualCustomerId || basketCustomerId || undefined  // Use registered customer ID
      );

      if (basketResponse.isError) {
        throw new Error(basketResponse.error || 'Failed to create basket');
      }

      const responseBasketId = basketResponse.data.basketId;
      const responseOrderId = basketResponse.data.orderId;

      // Update state with response IDs if we don't already have them
      if (!basketId && responseBasketId) {
        setBasketId(responseBasketId);

      }
      if (!basketOrderId && responseOrderId) {
        setBasketOrderId(responseOrderId);

      }
      // Note: basketCustomerId is now managed by registeredCustomerId

      // Apply family saver and circus discounts if applicable
      // NOTE: Discounts are now applied via Basket/Load endpoint (not ApplyVoucher)
      // The discounts are included in the discounts array when calling loadBasket below
      const saverCode = getFamilySaverCode();
      const circusCode = await createCircusDiscountIfNeeded();
      const allDiscounts = [saverCode, circusCode].filter(Boolean);
      if (saverCode) {
        setAppliedSaverCode(saverCode);
      }

      console.log('🎪🎟️ [BASKET LOAD] Discounts to apply:', {
        saverCode,
        circusCode,
        allDiscounts,
        discountCount: allDiscounts.length
      });

      // Load final basket with all discounts applied to get totals
      const loadPayload = {
        pageId,
        items: basketItems,
        giftAidSelected: false,
        discounts: allDiscounts, // Both Family Saver (percentage) and Circus (fixed amount) codes
        discountsFromMembership: [],
        // Use existing basket IDs to prevent creating new basket
        customerId: registeredCustomerId, // Only use registered customer ID (will be omitted if null)
        orderId: basketOrderId || responseOrderId || basketId
      };

      // Don't include customer object in basket payload - only customerId is needed

      // IMPORTANT: Pass null as 3rd param since both discounts already in payload.discounts (no duplication)
      const finalBasket = await venposService.loadBasket(locationId, loadPayload, null);
      
      console.log('🎪🎟️ [BASKET RESPONSE] Discounts applied by Venpos:', {
        discounts: finalBasket?.data?.discounts || [],
        totalPrice: finalBasket?.data?.totalPrice,
        finalPrice: finalBasket?.data?.finalPrice,
        discountAmount: (finalBasket?.data?.totalPrice || 0) - (finalBasket?.data?.finalPrice || 0)
      });
      setVenposBasket(finalBasket);

      // Update stored basket IDs from the response if available
      if (finalBasket?.data?.customerId) {
        if (!basketCustomerId && !registeredCustomerId) {
        setBasketCustomerId(finalBasket.data.customerId);

      }
      }
      if (finalBasket?.data?.orderId) {
        if (!basketOrderId) {
        setBasketOrderId(finalBasket.data.orderId);

        }
      }

      return {
        basketId: basketId || responseBasketId,
        basketDetails: finalBasket,
        success: true
      };

    } catch (error) {

      throw error;
    }
  };

  // Helper function to refresh existing basket with explicit customer ID
  const refreshExistingBasketWithCustomerId = async (customerId) => {
    try {

      if (!basketId) {
        throw new Error('Cannot refresh basket: missing basketId');
      }

      // CRITICAL FIX: Expand tickets with quantity > 1 into individual tickets
      const expandedTickets = [];
      selectedTickets
        .filter(ticket => (ticket.quantity || 0) > 0)
        .forEach(ticket => {
          const ticketQty = ticket.quantity || 1;
          
          if (ticketQty === 1) {
            expandedTickets.push({ ...ticket, quantity: 1 });
          } else {
            // Expand into individual items
            for (let i = 0; i < ticketQty; i++) {
              expandedTickets.push({
                ...ticket,
                quantity: 1,
                personIndex: i
              });
            }
          }
        });

      const basketItems = expandedTickets
        .map(ticket => ({
          pageType: 'WebPage',
          pageId: pageId,
          webSaleItemId: ticket.webSaleItemId,
          webSaleItemTypeID: ticket.webSaleItemTypeID,
          quantity: ticket.quantity,
          selectedDate: selectedTimeSlot?.time ?
            `${formatDateLocal(selectedDate || new Date())}T${selectedTimeSlot.time}:00` :
            formatDateLocal(selectedDate || new Date()),
          reservationExpiryDateTime: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
          name: ticket.name,
          giftAidEligible: false,
          isSysGift: false,
          isSysMemberCard: false,
          // CRITICAL: Use ticket's own event metadata if assigned, otherwise fallback to selectedTimeSlot
          eventId: ticket.eventId || selectedTimeSlot?.eventId || null,
          eventCapacityUsageId: ticket.eventCapacityUsageId || selectedTimeSlot?.eventCapacityUsageId || null
        }));

      if (basketItems.length === 0) {
        throw new Error('No valid basket items');
      }

      // Apply family saver and circus discounts if applicable
      const saverCode = getFamilySaverCode();
      const circusCode = await createCircusDiscountIfNeeded();
      const allDiscounts = [saverCode, circusCode].filter(Boolean);

      console.log('🎪🎟️ [refreshExistingBasketWithCustomerId] Discounts to apply:', {
        saverCode,
        circusCode,
        allDiscounts,
        discountCount: allDiscounts.length
      });

      const loadPayload = {
        pageId,
        items: basketItems,
        giftAidSelected: false,
        discounts: allDiscounts, // Both Family Saver (percentage) and Circus (fixed amount) codes
        discountsFromMembership: [],
        customerId: customerId, // Use the provided customer ID
        orderId: basketOrderId || basketId
      };

      // Don't include customer object - only customerId is needed

      // IMPORTANT: Pass null as 3rd param since both discounts already in payload.discounts (no duplication)
      const refreshedBasket = await venposService.loadBasket(locationId, loadPayload, null);
      
      console.log('🎪🎟️ [refreshExistingBasketWithCustomerId] Discounts applied by Venpos:', {
        discounts: refreshedBasket?.data?.discounts || [],
        totalPrice: refreshedBasket?.data?.totalPrice,
        finalPrice: refreshedBasket?.data?.finalPrice
      });
      setVenposBasket(refreshedBasket);

      return {
        basketId,
        basketDetails: refreshedBasket,
        success: true
      };

    } catch (error) {

      throw error;
    }
  };

  // Helper function to refresh existing basket without creating new one
  const refreshExistingBasket = async () => {
    try {

      // If we don't have valid basket IDs, we can't refresh
      if (!basketId) {
        throw new Error('Cannot refresh basket: missing basketId');
      }

      // Log what IDs we have vs what's missing

      // CRITICAL FIX: Expand tickets with quantity > 1 into individual tickets BEFORE creating basketItems
      // This matches what we do in createAndValidateBasket
      const expandedTickets = [];
      selectedTickets
        .filter(ticket => (ticket.quantity || 0) > 0)
        .forEach(ticket => {
          const ticketQty = ticket.quantity || 1;
          const isUpsell = ticket.isUpsellItem || ticket.pageType === 'EventTemplate' || ticket.pageType === 'Merchandise';
          
          // Don't expand upsell items - they can have quantities > 1
          if (isUpsell || ticketQty === 1) {
            expandedTickets.push({ ...ticket, quantity: ticketQty });
          } else {
            // Expand main tickets into individual items
            for (let i = 0; i < ticketQty; i++) {
              expandedTickets.push({
                ...ticket,
                quantity: 1,
                personIndex: i
              });
            }
          }
        });

      const basketItems = expandedTickets
        .map(ticket => {
          // Check if this is an upsell item (EventTemplate, Merchandise, or flagged as upsell)
          const isUpsell = ticket.isUpsellItem || ticket.pageType === 'EventTemplate' || ticket.pageType === 'Merchandise';
          
          const item = {
            // Preserve the upsell's own pageType (EventTemplate, Merchandise, etc.); main tickets use WebPage
            pageType: isUpsell ? (ticket.pageType || 'EventTemplate') : 'WebPage',
            // Use upsell's own pageId, or main event pageId
            pageId: isUpsell ? (ticket.pageId || ticket.parentUpsellId) : pageId,
          webSaleItemId: ticket.webSaleItemId,
          webSaleItemTypeID: ticket.webSaleItemTypeID,
          quantity: ticket.quantity,
            // Upsells have their own selectedDate, main tickets use selectedTimeSlot
            selectedDate: isUpsell ? 
              (ticket.selectedDate || ticket.selectedEventTime?.startDateTime) :
              (selectedTimeSlot?.time ?
            `${formatDateLocal(selectedDate || new Date())}T${selectedTimeSlot.time}:00` :
                formatDateLocal(selectedDate || new Date())),
            reservationExpiryDateTime: ticket.reservationExpiryDateTime || new Date(Date.now() + 30 * 60 * 1000).toISOString(),
          name: ticket.name,
          giftAidEligible: false,
          isSysGift: false,
          isSysMemberCard: false,
            // Upsells have their own eventId and capacity, main tickets use selectedTimeSlot
            eventId: isUpsell ? 
              (ticket.eventId || ticket.selectedEventTime?.eventId) : 
              (selectedTimeSlot?.eventId || null),
            eventCapacityUsageId: isUpsell ? 
              (ticket.eventCapacityUsageId || ticket.selectedEventTime?.eventCapacityUsageId) : 
              (selectedTimeSlot?.eventCapacityUsageId || null),
            // Include parentWebSaleItemId for upsells
            ...(isUpsell && ticket.parentWebSaleItemId && { parentWebSaleItemId: ticket.parentWebSaleItemId }),
            // CRITICAL: Preserve question answers, modifiers, and participant details
            questionAnswers: ticket.questionAnswers || {},
            modifierSelection: ticket.modifierSelection || null,
            participantDetails: ticket.participantDetails || {}
          };
          
          // Debug log for upsells
          if (isUpsell) {

          }
          
          return item;
        });

      if (basketItems.length === 0) {
        throw new Error('No valid basket items');
      }

      // Apply family saver and circus discounts if applicable
      // NOTE: Discounts are now applied via Basket/Load endpoint (not ApplyVoucher)
      // The discounts are included in the discounts array when calling loadBasket below
      const saverCode = getFamilySaverCode();
      const circusCode = await createCircusDiscountIfNeeded();
      const allDiscounts = [saverCode, circusCode].filter(Boolean);
      if (saverCode) {
        setAppliedSaverCode(saverCode);
      }

      console.log('🎪🎟️ [refreshExistingBasket] Discounts to apply:', {
        saverCode,
        circusCode,
        allDiscounts,
        discountCount: allDiscounts.length
      });

      // Load final basket with all discounts applied to get totals
      // IMPORTANT: Include existing basket's customer and order IDs to prevent creating new basket
      const loadPayload = {
        pageId,
        items: basketItems,
        giftAidSelected: false,
        discounts: allDiscounts, // Both Family Saver (percentage) and Circus (fixed amount) codes
        discountsFromMembership: [],
        // Use stored basket's IDs to prevent creating new basket
        customerId: registeredCustomerId, // Only use registered customer ID (will be omitted if null)
        orderId: basketOrderId || basketId // Use stored orderId or fallback to basketId
      };

      // Don't include customer object - only customerId is needed

      // IMPORTANT: Pass null as 3rd param since both discounts already in payload.discounts (no duplication)
      const refreshedBasket = await venposService.loadBasket(locationId, loadPayload, null);
      
      console.log('🎪🎟️ [refreshExistingBasket] Discounts applied by Venpos:', {
        discounts: refreshedBasket?.data?.discounts || [],
        totalPrice: refreshedBasket?.data?.totalPrice,
        finalPrice: refreshedBasket?.data?.finalPrice
      });
      
      setVenposBasket(refreshedBasket);

      // Update stored basket IDs from the response if they're missing
      if (refreshedBasket?.data?.customerId && !basketCustomerId) {
        setBasketCustomerId(refreshedBasket.data.customerId);
      }
      if (refreshedBasket?.data?.orderId && !basketOrderId) {
        setBasketOrderId(refreshedBasket.data.orderId);
      }

      return {
        basketId,
        basketDetails: refreshedBasket,
        success: true
      };

    } catch (error) {

      throw error;
    }
  };

  // Preflight util for step 4 preparation
  const runPreflight = async () => {
    try {

      // If we don't have a basket yet, create one
      if (!basketId) {

        await createAndValidateBasket();
        return;
      }

      // Otherwise, just refresh the existing basket to get updated totals

      await refreshExistingBasket();

    } catch (e) {

    } finally {
      setPreflightDone(true);
    }
  };

  // Preflight with explicit customer ID (used after registration)
  const runPreflightWithCustomerId = async (customerId) => {
    try {

      // If we don't have a basket yet, create one
      if (!basketId) {

        await createAndValidateBasket();
        return;
      }

      // Otherwise, refresh the existing basket with the registered customer ID

      await refreshExistingBasketWithCustomerId(customerId);

    } catch (e) {

    } finally {
      setPreflightDone(true);
    }
  };

  // Create basket early after time slot selection (for upsell timing) - but reuse existing if available
  useEffect(() => {
    const createBasketEarly = async () => {
      // Only create basket early if we need it for upsells and don't already have one
      if (selectedTimeSlot && selectedTickets?.length > 0 && !basketId && currentStep === 3) {

        try {

          await createAndValidateBasket();

        } catch (error) {


        }
      }
    };

    createBasketEarly();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTimeSlot, basketId, currentStep]); // Removed selectedTickets, customerDetails, registeredCustomerId to prevent infinite loops


  // Trigger preflight on entering step 5 or when we have all required data
  // BUT skip for membership journeys (they handle preflight in handleNext)
  useEffect(() => {
    if (currentStep === 5 && !preflightDone && selectedTickets?.length > 0 && selectedDate && selectedTimeSlot && !basketId) {
      // Don't run preflight for membership journeys - they handle it in handleNext
      const isMembershipJourney = selectedTickets.some(ticket => ticket.webSaleItemTypeID === 4);
      if (!isMembershipJourney) {
      runPreflight();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, preflightDone, selectedDate, selectedTimeSlot, basketId]); // Removed runPreflight and selectedTickets to prevent infinite loops

  // Hide processing overlay when confirmation page is reached
  useEffect(() => {
    if (currentStep === 7) {

      setIsProcessingPayment(false);
    }
  }, [currentStep]);

  // Load upsells when moving to step 3 (after date/time selection)
  useEffect(() => {
    const loadUpsells = async () => {
      if (
        currentStep === 3 &&
        selectedTickets?.length > 0 &&
        selectedDate &&
        selectedTimeSlot &&
        upsellCriteriaKey
      ) {
        const criteriaChanged = lastUpsellCriteriaRef.current !== upsellCriteriaKey;
        if (availableUpsells.length > 0 && !criteriaChanged) {
          return;
        }
        
        // If upsells are already loaded but empty, auto-skip to customer details
        if (availableUpsells.length === 0 && !criteriaChanged && !upsellsLoading) {
          dispatch({ type: BOOKING_ACTIONS.SET_CURRENT_STEP, payload: 4 });
          return;
        }

        if (criteriaChanged) {
          dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_UPSELLS, payload: [] });
        }

        lastUpsellCriteriaRef.current = upsellCriteriaKey;
        setUpsellsLoading(true);
        try {

          // Prepare items for upsell request - match exact structure from working example
          const items = selectedTickets
            .filter(ticket => ticket.quantity > 0)
            .map(ticket => ({
              pageType: 'WebPage',
              pageId: pageId,
              webSaleItemId: ticket.webSaleItemId,
              quantity: ticket.quantity,
              webSaleItemTypeID: ticket.webSaleItemTypeID,
              name: ticket.name,
              selectedDate: formatDateLocal(selectedDate) + 'T' + (selectedTimeSlot.time || '10:00') + ':00',
              reservationExpiryDateTime: new Date(Date.now() + 30 * 60 * 1000).toISOString(), // 30 minutes from now
              // CRITICAL: Use ticket's own capacity ID if assigned during time slot selection
              eventCapacityUsageId: ticket.eventCapacityUsageId || selectedTimeSlot.eventCapacityUsageId || null,
              eventId: ticket.eventId || selectedTimeSlot.eventId || null,
              fixedDonationAmount: 0,
              giftAidEligible: false,
              isSysGift: false,
              isSysMemberCard: false
            }));

          const response = await venposService.getUpsells(
            locationId,
            pageId,
            'Event',
            'OnScreenCalendar',
            items
          );

          console.log('[BookingJourney] getUpsells raw response:', JSON.stringify(response, null, 2));

          // Merchandise upsells — always fetched for Dog Park ("Additional dog" product);
          // for all other pages only fetched within the Explorer Lunch Pack date window (before 1 Sep 2026)
          let merchandiseUpsells = [];
          const visitDateForLunchPacks = selectedDate ? new Date(selectedDate) : null;
          const lunchPackEndDate = new Date('2026-09-01');
          const showLunchPackUpsells =
            visitDateForLunchPacks && visitDateForLunchPacks <= lunchPackEndDate;
          if (isDogParkBooking || showLunchPackUpsells) {
            try {
              const merchandiseResponse = await venposService.getUpsells(
                locationId,
                pageId,
                'Merchandise',
                'OnScreenCalendar',
                items
              );
              console.log('[BookingJourney] getUpsells Merchandise response:', JSON.stringify(merchandiseResponse, null, 2));
              if (merchandiseResponse?.data?.upsells) {
                merchandiseUpsells = merchandiseResponse.data.upsells;
              }
            } catch (e) {
              // Non-blocking — if merchandise upsells fail just continue without them
            }
          }

          // No separate Product-type fetch needed — dog park upsell ("Additional dog") is Merchandise type
          const productUpsells = [];

          if (response?.data?.upsells || merchandiseUpsells.length > 0 || productUpsells.length > 0) {
            const combinedUpsells = [
              // Dog Park only uses Product-type upsells — skip the Event results to avoid showing unrelated upsells
              ...(isDogParkBooking ? [] : (response?.data?.upsells || [])),
              ...productUpsells,
              ...merchandiseUpsells
            ];
            const shouldHideToddlerSpecial = isToddlerRestrictedDate(selectedDate);
            let sanitizedUpsells = shouldHideToddlerSpecial
              ? combinedUpsells.filter(upsell => !isToddlerSpecialUpsell(upsell.name))
              : combinedUpsells;
            // Exclude Castle and Garden Entry + Garden and Playground (Only) Entry from upsells
            const isExcludedUpsell = (name) => {
              const n = (name || '').toLowerCase().trim();
              return (
                n.includes('castle and garden entry - adult - entry') ||
                n.includes('castle and garden entry - child - entry') ||
                n.includes('castle and garden entry - concession - entry') ||
                n.includes('garden and playground only entry - adult - entry') ||
                n.includes('garden and playground only entry - child - entry') ||
                n.includes('garden and playground entry - concession - entry') ||
                n.includes('garden and playground only entry - concession - entry') ||
                n.includes('garden and playground only entry - concession')
              );
            };
            sanitizedUpsells = sanitizedUpsells.filter(upsell => !isExcludedUpsell(upsell.name));
            sanitizedUpsells = sanitizedUpsells.filter(upsell => !isChoirFreeAdultTicket(upsell));

            if (process.env.NODE_ENV === 'development') {
              console.log(
                '[BookingJourney] Upsells loaded',
                {
                  selectedDate: selectedDate?.toISOString(),
                  shouldHideToddlerSpecial,
                  originalUpsells: combinedUpsells.map(u => u.name),
                  sanitizedUpsells: sanitizedUpsells.map(u => u.name)
                }
              );
            }

            dispatch({ type: BOOKING_ACTIONS.SET_AVAILABLE_UPSELLS, payload: sanitizedUpsells });
            lastUpsellCriteriaRef.current = upsellCriteriaKey;
            
            // Auto-skip to step 4 if no upsells are available
            if (sanitizedUpsells.length === 0) {
              // Auto-advance to step 4 (customer details) since there are no upsells to show
              dispatch({ type: BOOKING_ACTIONS.SET_CURRENT_STEP, payload: 4 });
            }
          } else {
            // No upsells returned - auto-skip to customer details
            dispatch({ type: BOOKING_ACTIONS.SET_CURRENT_STEP, payload: 4 });
          }
        } catch (error) {

          // Don't block the flow if upsells fail to load
          dispatch({ type: BOOKING_ACTIONS.SET_AVAILABLE_UPSELLS, payload: [] });
          
          // Auto-skip to step 4 if upsells fail to load (treat as no upsells available)
          dispatch({ type: BOOKING_ACTIONS.SET_CURRENT_STEP, payload: 4 });
        } finally {
          setUpsellsLoading(false);
        }
      }
    };

    loadUpsells();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, selectedDate, selectedTimeSlot, pageId, locationId, availableUpsells.length, dispatch, upsellCriteriaKey, ticketQuantitiesKey]);

  useEffect(() => {
    if (!upsellCriteriaKey) {
      lastUpsellCriteriaRef.current = null;
    }
  }, [upsellCriteriaKey]);

  // Load questions and participant details for grotto retail village page after time slot selection
  useEffect(() => {
    const loadGrottoQuestionsAndParticipants = async () => {
      // Only for grotto retail village page, after time slot selection
      // Load on step 2 (after date/time selection) or step 4 (customer details) if not already loaded
      if (
        NO_UPSELL_PAGE_IDS.includes(pageId) &&
        selectedTimeSlot &&
        selectedTickets?.length > 0 &&
        selectedDate &&
        (currentStep === 2 || currentStep === 4) // After date/time selection or on customer details step
      ) {
        // Check if questions are already loaded
        const hasQuestions = selectedTickets.some(t => t.eventQuestions && t.eventQuestions.length > 0);
        if (hasQuestions && currentStep === 4) {
          // Questions already loaded, skip
          return;
        }
        try {
          // Prepare webSaleItems for questions call
          const webSaleItems = {};
          selectedTickets
            .filter(ticket => ticket.quantity > 0)
            .forEach(ticket => {
              webSaleItems[ticket.webSaleItemId] = ticket.quantity;
            });

          // Prepare eventIds array
          const eventIds = selectedTimeSlot.eventId ? [selectedTimeSlot.eventId] : [];

          // Load event questions
          if (Object.keys(webSaleItems).length > 0 && eventIds.length > 0) {
            const questionsResponse = await venposService.getEventQuestions(
              locationId,
              webSaleItems,
              eventIds
            );

            if (questionsResponse?.data?.eventQuestions && questionsResponse.data.eventQuestions.length > 0) {
              // Update tickets with questions
              // For grotto retail village, include ALL questions (both event questions and product questions)
              // Product questions should be shown for all tickets in the booking
              const allQuestions = questionsResponse.data.eventQuestions;
              
              // Debug: Log questions to see what we're getting
              console.log('🎯 [GROTTO] Loaded questions:', {
                totalQuestions: allQuestions.length,
                questions: allQuestions.map(q => ({
                  questionId: q.questionId,
                  question: q.question,
                  productId: q.productId,
                  isRequired: q.isRequired
                }))
              });
              
              const updatedTickets = selectedTickets.map(ticket => ({
                ...ticket,
                eventQuestions: allQuestions, // Include all questions for grotto retail village
                questionAnswers: ticket.questionAnswers || {}
              }));
              dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_TICKETS, payload: updatedTickets });
            } else {
              console.log('⚠️ [GROTTO] No questions found in response:', questionsResponse?.data);
            }

            // Load participant details
            const participantItems = selectedTickets
              .filter(ticket => ticket.quantity > 0)
              .map(ticket => ({
                pageType: 'WebPage',
                pageId: pageId,
                webSaleItemId: ticket.webSaleItemId,
                quantity: ticket.quantity,
                webSaleItemTypeID: ticket.webSaleItemTypeID,
                selectedDate: formatDateLocal(selectedDate) + 'T' + (selectedTimeSlot.time || '10:00') + ':00',
                eventId: ticket.eventId || selectedTimeSlot.eventId || null,
                eventCapacityUsageId: ticket.eventCapacityUsageId || selectedTimeSlot.eventCapacityUsageId || null
              }));

            if (participantItems.length > 0) {
              const participantResponse = await venposService.getEventParticipantDetails(
                locationId,
                participantItems
              );

              if (participantResponse?.data && participantResponse.data.length > 0) {
                // Update tickets with participant details
                const updatedTickets = selectedTickets.map(ticket => ({
                  ...ticket,
                  participantDetails: participantResponse.data,
                  participantAnswers: ticket.participantAnswers || {}
                }));
                dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_TICKETS, payload: updatedTickets });
              }
            }
          }
        } catch (error) {
          console.error('Error loading questions/participant details for grotto:', error);
          // Don't block the flow if this fails
        }
      }
    };

    loadGrottoQuestionsAndParticipants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, selectedTimeSlot, selectedTickets, selectedDate, currentStep]);

  // Enhanced validation that includes membership details
  const canProceedToNextStep = () => {
    const baseValidation = canProceedToNextStepContext();

    // Featured tour pages: require the named tour upsell on step 3
    if (currentStep === 3 && FEATURED_TOUR_PAGE_IDS.includes(pageId)) {
      const tourConfig = FEATURED_TOUR_UPSELL_MAP[pageId];
      const totalEntryTickets = selectedTickets
        .filter(t => !t.isUpsellItem && t.pageType !== 'EventTemplate' && (t.quantity || 0) > 0)
        .reduce((sum, t) => sum + (parseInt(t.quantity, 10) || 0), 0);

      const tourUpsellQty = selectedTickets
        .filter(t => (t.isUpsellItem || t.pageType === 'EventTemplate') &&
          (t.name || t.parentUpsellName || '').toLowerCase().includes(tourConfig.pattern))
        .reduce((sum, t) => sum + (parseInt(t.quantity, 10) || 1), 0);

      return baseValidation && totalEntryTickets > 0 && tourUpsellQty >= totalEntryTickets;
    }

    // Arrived from Festive Afternoon Tea: must add tea, but not more than Castle entry tickets
    if (currentStep === 3 && requireAfternoonTea) {
      if (upsellsLoading) return false;
      if (upsellsIncludeAvailableAfternoonTea(availableUpsells)) {
        const entryQty = countEntryTickets(selectedTickets);
        const teaQty = countAfternoonTeaTickets(selectedTickets);
        if (teaQty < 1) return false;
        if (entryQty > 0 && teaQty > entryQty) return false;
      }
    }

    // Additional validation for membership journeys on step 4
    if (currentStep === 4 && isMembershipJourney && membershipDetails) {
      // Validate all membership details are filled
      const allMembershipDetailsValid = membershipDetails.every((membership, membershipIndex) => {
        if (!membership.memberDetails || membership.memberDetails.length === 0) {
          return false;
        }
        
        return membership.memberDetails.every((member, memberIndex) => {
          // Check required fields
          if (!member.firstName?.trim()) return false;
          if (!member.lastName?.trim()) return false;
          if (membership.emailRequired && !member.email?.trim()) return false;
          if (membership.mobilePhoneRequired && !member.mobile?.trim()) return false;
          if (shouldShowCarRegistration(membership, memberIndex, selectedTickets) && membership.displayTelephone) {
            if (!member.telephone?.trim()) return false;
            const vrm = normaliseVRM(member.telephone);
            if (!member.vehicleRegistrationVerified || member.vehicleRegistrationVerifiedVrm !== vrm) {
              return false;
            }
          }
          return true;
        });
      });
      
      return baseValidation && allMembershipDetailsValid;
    }
    
    return baseValidation;
  };

  const handleNext = async (options = {}) => {
    const skipAnimalParkReminder = options.skipAnimalParkReminder === true;
    const skipCircusReminder = options.skipCircusReminder === true;

    if (!canProceedToNextStep()) {
      // Show error message for membership journeys
      if (currentStep === 4 && isMembershipJourney && membershipDetails) {
        dispatch({ 
          type: BOOKING_ACTIONS.SET_ERROR, 
          payload: 'Please complete all required membership information before continuing.' 
        });
      }
      if (
        currentStep === 3 &&
        requireAfternoonTea &&
        upsellsIncludeAvailableAfternoonTea(availableUpsells)
      ) {
        const entryQty = countEntryTickets(selectedTickets);
        const teaQty = countAfternoonTeaTickets(selectedTickets);
        if (teaQty < 1) {
          dispatch({
            type: BOOKING_ACTIONS.SET_ERROR,
            payload: 'Festive Afternoon Tea is not included with Castle entry. Please add it below before continuing.'
          });
        } else if (entryQty > 0 && teaQty > entryQty) {
          dispatch({
            type: BOOKING_ACTIONS.SET_ERROR,
            payload: `You can add at most ${entryQty} afternoon ${entryQty === 1 ? 'tea' : 'teas'} — one per Castle entry ticket.`
          });
        }
      }
      return;
    }

    // Step 3: castle added but free Animal Park not — in-journey modal (not window.confirm)
    if (currentStep === 3 && !skipAnimalParkReminder) {
      const visitDate = selectedDate ? new Date(selectedDate) : null;
      const safariStart = new Date('2026-07-10');
      const safariEnd = new Date('2026-09-01');
      const isInSafariPeriod = visitDate && visitDate >= safariStart && visitDate <= safariEnd;
      const hasAnyNonUpsellTicket = selectedTickets.some(
        t => (parseInt(t.quantity, 10) || 0) > 0 && !t.isUpsellItem
      );
      const hasPaidCastleForFreeAnimalParkPromo =
        castleAndGardenPaidQtyForFreeAnimalParkPromotion(selectedTickets) > 0 ||
        (isSafariBooking && hasAnyNonUpsellTicket) ||
        (isInSafariPeriod && hasAnyNonUpsellTicket);
      if (
        hasPaidCastleForFreeAnimalParkPromo &&
        !basketHasFreeAnimalParkPromotion(selectedTickets) &&
        upsellsIncludeAnimalParkOffer(availableUpsells)
      ) {
        setShowFreeAnimalParkReminderModal(true);
        return;
      }
    }

    // Step 3: Castle & Gardens / Gardens pages — confirm if leaving upsells without a Circus ticket when one is offered
    if (currentStep === 3 && !skipCircusReminder && !requireAfternoonTea) {
      if (
        CIRCUS_SKIP_REMINDER_PAGE_IDS.includes(pageId) &&
        !basketHasCircusShowTicket(selectedTickets) &&
        upsellsIncludeAvailableCircusOffer(availableUpsells)
      ) {
        setShowCircusSkipReminderModal(true);
        return;
      }
    }

    try {
      // Prevent rapid state changes during transition
      dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: true });
      
      // Validate state consistency before proceeding

      // Step-specific validation
      switch (currentStep) {
        case 1: // Moving from Tickets to Date/Time or Customer Details (for memberships)
          // For membership journeys, skip to customer details (step 4)
          if (isMembershipJourney) {
            // Load membership details
            const membershipItems = selectedTickets
              .filter(ticket => ticket.quantity > 0)
              .map(ticket => ({
                pageType: 'WebPage',
                pageId: pageId,
                webSaleItemId: ticket.webSaleItemId,
                quantity: ticket.quantity,
                webSaleItemTypeID: ticket.webSaleItemTypeID || 4,
                name: ticket.name,
                fixedDonationAmount: 0,
                giftAidEligible: false,
                isSysGift: false,
                isSysMemberCard: false
              }));

            if (membershipItems.length > 0) {
              const membershipResponse = await venposService.getMembershipDetails(locationId, membershipItems);
              if (!membershipResponse.isError && membershipResponse.data) {
                setMembershipDetails(membershipResponse.data);
              }
            }
            
            dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: false });
            dispatch({ type: BOOKING_ACTIONS.SET_CURRENT_STEP, payload: 4 }); // Skip to customer details
            return;
          }
          
          // CRITICAL: Check capacity availability before proceeding to prevent overbooking
          // This validates that there are available dates for the selected ticket quantities
          try {
            const selectedItems = selectedTickets
              .filter(ticket => ticket.quantity > 0)
              .map(ticket => ({
                pageType: 'WebPage',
                pageId: pageId,
                webSaleItemId: ticket.webSaleItemId,
                quantity: ticket.quantity,
                webSaleItemTypeID: ticket.webSaleItemTypeID
              }));

            if (selectedItems.length === 0) {
              dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: false });
              dispatch({ 
                type: BOOKING_ACTIONS.SET_ERROR, 
                payload: 'Please select at least one ticket before continuing.' 
              });
              return;
            }

            // Check if there are any available dates for the selected ticket quantities
            const capacityCheckResponse = await venposService.getAvailableDates(
              locationId,
              pageId,
              {
                pageType: 'WebPage',
                selectedDate: formatDateLocal(new Date()),
                dateSelectorType: 'OnScreenCalendar'
              },
              selectedItems
            );

            if (capacityCheckResponse.isError) {
              dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: false });
              dispatch({ 
                type: BOOKING_ACTIONS.SET_ERROR, 
                payload: 'Unable to check availability. Please try again.' 
              });
              return;
            }

            // Check if any dates are available (availability === 1 means available)
            const availableDatesList = capacityCheckResponse.data?.calendar || [];
            const hasAvailableDates = availableDatesList.some(dateInfo => {
              const availability = Number(dateInfo.availability);
              // Check if date is in the future and available
              const date = new Date(dateInfo.date);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              date.setHours(0, 0, 0, 0);
              if (date < today || availability !== 1) return false;
              if (restrictToFestiveTeaDates && !isWithinFestiveAfternoonTeaDateWindow(dateInfo.date)) {
                return false;
              }
              return true;
            });

            if (!hasAvailableDates) {
              dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: false });
              dispatch({ 
                type: BOOKING_ACTIONS.SET_ERROR, 
                payload: 'Sorry, there are no available dates for the selected number of tickets. Please reduce the quantity or try again later.' 
              });
              return;
            }

            // Update available dates in state for the date selection screen
            if (capacityCheckResponse.data?.calendar) {
              const datesMap = {};
              capacityCheckResponse.data.calendar.forEach(dateInfo => {
                // Use the date string directly from VenPos (already in YYYY-MM-DD format)
                // Don't parse through new Date() to avoid timezone conversion
                const key = dateInfo.date; // Already in "YYYY-MM-DD" format
                datesMap[key] = Number(dateInfo.availability);
              });
              setAvailableDates(applyFestiveAfternoonTeaDateWindow(datesMap, restrictToFestiveTeaDates));
            }
          } catch (error) {
            console.error('Error checking capacity availability:', error);
            dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: false });
            dispatch({ 
              type: BOOKING_ACTIONS.SET_ERROR, 
              payload: 'Unable to verify availability. Please try again.' 
            });
            return;
          }
          
          // For grotto retail village, still need date/time selection (step 2), only skip upsells (step 3)
          // So we just break and proceed to step 2 normally
          break;
          
        case 2: // Moving from Date/Time to Upsells
          // Validate that time slot has required capacity information
          if (!selectedTimeSlot?.eventCapacityUsageId) {

          }
          
          // CRITICAL: Re-check date availability before proceeding to prevent booking on fully booked dates
          if (selectedDate) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const selectedDateNormalized = new Date(selectedDate);
            selectedDateNormalized.setHours(0, 0, 0, 0);
            
            // Check if date is in the past
            if (selectedDateNormalized < today) {
              dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: false });
              dispatch({ 
                type: BOOKING_ACTIONS.SET_ERROR, 
                payload: 'The selected date is in the past. Please select a future date.' 
              });
              return;
            }
            
            // Re-fetch availability to ensure date is still available
            try {
              const selectedItems = selectedTickets
                .filter(ticket => ticket.quantity > 0)
                .map(ticket => ({
                  pageType: 'WebPage',
                  pageId: pageId,
                  webSaleItemId: ticket.webSaleItemId,
                  quantity: ticket.quantity,
                  webSaleItemTypeID: ticket.webSaleItemTypeID
                }));

              const response = await venposService.getAvailableDates(
                locationId,
                pageId,
                {
                  pageType: 'WebPage',
                  selectedDate: formatDateLocal(new Date()),
                  dateSelectorType: 'OnScreenCalendar'
                },
                selectedItems
              );

              if (!response.isError && response.data?.calendar) {
                const datesMap = {};
                response.data.calendar.forEach(dateInfo => {
                  // Use the date string directly from VenPos (already in YYYY-MM-DD format)
                  // Don't parse through new Date() to avoid timezone conversion
                  const key = dateInfo.date; // Already in "YYYY-MM-DD" format
                  datesMap[key] = Number(dateInfo.availability);
                });
                
                // Update available dates
                const filteredDatesMap = applyFestiveAfternoonTeaDateWindow(datesMap, restrictToFestiveTeaDates);
                setAvailableDates(filteredDatesMap);
                
                // Check if the selected date is still available
                const selectedDateString = formatDateLocal(selectedDate);
                const currentAvailability = filteredDatesMap[selectedDateString];
                
                // If date is not available (availability === 4) or unavailable, block proceeding
                if (
                  (restrictToFestiveTeaDates && !isWithinFestiveAfternoonTeaDateWindow(selectedDateString)) ||
                  currentAvailability === undefined ||
                  currentAvailability === 4 ||
                  currentAvailability !== 1
                ) {
                  dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: false });
                  dispatch({ 
                    type: BOOKING_ACTIONS.SET_ERROR, 
                    payload: restrictToFestiveTeaDates && !isWithinFestiveAfternoonTeaDateWindow(selectedDateString)
                      ? 'Festive Afternoon Tea is available 14 November – 30 December 2026. Please select a date in that range.'
                      : 'The selected date is no longer available or is fully booked. Please select a different date.' 
                  });
                  // Clear the selected date to force user to reselect
                  dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_DATE, payload: null });
                  dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_TIME_SLOT, payload: null });
                  return;
                }
              } else {
                // If we can't fetch availability, err on the side of caution and block
                dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: false });
                dispatch({ 
                  type: BOOKING_ACTIONS.SET_ERROR, 
                  payload: 'Unable to verify date availability. Please try again.' 
                });
                return;
              }
            } catch (error) {
              console.error('Error checking date availability:', error);
              dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: false });
              dispatch({ 
                type: BOOKING_ACTIONS.SET_ERROR, 
                payload: 'Unable to verify date availability. Please try again.' 
              });
              return;
            }
          }
          
          // Skip upsells for grotto retail village page - go directly to customer details
          if (NO_UPSELL_PAGE_IDS.includes(pageId)) {
            dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: false });
            dispatch({ type: BOOKING_ACTIONS.SET_CURRENT_STEP, payload: 4 }); // Skip to customer details
            return;
          }
          break;
          
        case 3: // Moving from Upsells to Customer Details

          // Featured tour pages: enforce the named tour upsell before proceeding
          if (FEATURED_TOUR_PAGE_IDS.includes(pageId)) {
            const tourConfig = FEATURED_TOUR_UPSELL_MAP[pageId];
            const totalEntryTickets = selectedTickets
              .filter(t => !t.isUpsellItem && t.pageType !== 'EventTemplate' && (t.quantity || 0) > 0)
              .reduce((sum, t) => sum + (parseInt(t.quantity, 10) || 0), 0);

            const tourUpsellQtyCheck = selectedTickets
              .filter(t => (t.isUpsellItem || t.pageType === 'EventTemplate') &&
                (t.name || t.parentUpsellName || '').toLowerCase().includes(tourConfig.pattern))
              .reduce((sum, t) => sum + (parseInt(t.quantity, 10) || 1), 0);

            if (tourUpsellQtyCheck < totalEntryTickets) {
              dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: false });
              dispatch({
                type: BOOKING_ACTIONS.SET_ERROR,
                payload: `Please add ${tourConfig.displayName} for all ${totalEntryTickets} ${totalEntryTickets === 1 ? 'person' : 'people'} in your party before continuing.`
              });
              return;
            }
          }

          if (
            requireAfternoonTea &&
            upsellsIncludeAvailableAfternoonTea(availableUpsells)
          ) {
            const entryQty = countEntryTickets(selectedTickets);
            const teaQty = countAfternoonTeaTickets(selectedTickets);
            if (teaQty < 1) {
              dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: false });
              dispatch({
                type: BOOKING_ACTIONS.SET_ERROR,
                payload: 'Festive Afternoon Tea is not included with Castle entry. Please add it below before continuing.'
              });
              return;
            }
            if (entryQty > 0 && teaQty > entryQty) {
              dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: false });
              dispatch({
                type: BOOKING_ACTIONS.SET_ERROR,
                payload: `You can add at most ${entryQty} afternoon ${entryQty === 1 ? 'tea' : 'teas'} — one per Castle entry ticket.`
              });
              return;
            }
          }

          // Validate upsell times are after main entry time
          const upsellTickets = selectedTickets.filter(t => t.isUpsellItem || t.pageType === 'EventTemplate');
          if (upsellTickets.length > 0 && selectedTimeSlot?.time) {
            const parseTime = (timeStr) => {
              if (!timeStr) return 0;
              const [hours, minutes] = timeStr.split(':').map(Number);
              return hours * 60 + minutes;
            };
            const mainTimeMinutes = parseTime(selectedTimeSlot.time);
            const invalidUpsells = upsellTickets.filter(t => {
              if (!t.selectedEventTime?.time) return false;
              return parseTime(t.selectedEventTime.time) < mainTimeMinutes;
            });
            if (invalidUpsells.length > 0) {

              dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: false });
              dispatch({ 
                type: BOOKING_ACTIONS.SET_ERROR, 
                payload: 'Some activities have invalid times. Please review your selections.' 
              });
              return;
            }
          }
          break;
          
        case 4: // Moving from Customer Details to Review
          // For membership journeys, skip preflight (no date/time needed)
          if (isMembershipJourney) {
            // Get membership customer details if customer is registered
            if (registeredCustomerId) {
              const customerDetailsResponse = await venposService.getMembershipCustomerDetails(locationId, registeredCustomerId);
              if (!customerDetailsResponse.isError && customerDetailsResponse.data) {
                setMembershipCustomerDetails(customerDetailsResponse.data);
              }
            }
          }
          break;
      }

      // Handle step-specific logic
      if (currentStep === 5) {
        // Review step: if amount > 0, go to dedicated payment step; if free, complete booking
        // ✅ CRITICAL FIX: Calculate amount properly - don't use finalPrice if it's 0 when we have a valid calculated amount
        const basketFinalPrice = venposBasket?.data?.finalPrice ?? venposBasket?.finalPrice;
        const calculatedTotal = calculateTotal();
        
        // Calculate discount amount from basket if available
        const basketDiscountAmount = venposBasket?.data?.discounts?.reduce((sum, discount) => {
          return sum + (discount.discountedAmount || 0);
        }, 0) || 0;
        
        const basketSubtotal = venposBasket?.data?.totalPrice;
        const calculatedFromBasket = basketSubtotal ? Math.max(0, basketSubtotal - basketDiscountAmount) : null;
        
        // Use basket finalPrice if it's explicitly set (including 0), otherwise use calculated amount
        // CRITICAL: If basketFinalPrice is 0, that means it's a free booking - use 0
        const amountNow = (basketFinalPrice !== null && basketFinalPrice !== undefined)
          ? basketFinalPrice  // Use basket finalPrice (even if 0 - that means free booking)
          : (calculatedFromBasket !== null && calculatedFromBasket !== undefined)
            ? Math.max(0, calculatedFromBasket)  // Use calculated from basket, ensure non-negative
            : Math.max(0, calculatedTotal);  // Fallback to calculated total, ensure non-negative
        
        console.log('💰 [BookingJourney] Step 5 Amount Calculation:', {
          basketFinalPrice,
          basketSubtotal,
          basketDiscountAmount,
          calculatedFromBasket,
          calculatedTotal,
          amountNow,
          willProceedToPayment: amountNow > 0
        });
        
        if (amountNow > 0) {

          // CRITICAL: Ensure basket exists and is fresh before payment
          // This handles the case where user cancelled payment and went back (basket was cleared)
          if (!basketId || !venposBasket || !preflightDone) {

            // Run preflight to create fresh basket with current prices
            try {
              await runPreflight();

            } catch (error) {

              dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: false });
              dispatch({ 
                type: BOOKING_ACTIONS.SET_ERROR, 
                payload: 'Failed to prepare payment. Please try again.' 
              });
              return;
            }
          } else {

          }

          dispatch({ type: BOOKING_ACTIONS.SET_CURRENT_STEP, payload: 6 });
          dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: false });
          return;
        }
        // Free booking: process immediately
        await processBooking();
      } else {
        // If moving from 4 -> 5, register customer and run preflight before navigating
        if (currentStep === 4) {
          // For membership journeys, create basket without date/time
          if (isMembershipJourney) {
            // Create basket for membership (no date/time needed)
            // Declare variables outside try block so they're accessible in catch block
            const membershipBasketItems = selectedTickets
              .filter(ticket => ticket.quantity > 0)
              .map(ticket => ({
                pageType: 'WebPage',
                pageId: pageId,
                webSaleItemId: ticket.webSaleItemId,
                webSaleItemTypeID: ticket.webSaleItemTypeID || 4,
                quantity: ticket.quantity,
                name: ticket.name,
                fixedDonationAmount: 0,
                giftAidEligible: false,
                isSysGift: false,
                isSysMemberCard: false
                // No selectedDate, eventId, or eventCapacityUsageId for memberships
              }));

            let responseBasketId = null;
            let responseOrderId = null;

            // Wrap everything in a try-catch to prevent any unhandled errors from resetting the component
            try {
            // Register customer if needed
            if (customerDetails?.firstName && customerDetails?.lastName && customerDetails?.email && !registeredCustomerId) {
              const registrationDetails = {
                ...customerDetails,
                address: {
                  line1: customerDetails.address?.line1 || '',
                  line2: customerDetails.address?.line2 || '',
                  city: customerDetails.address?.city || '',
                  postcode: customerDetails.address?.postcode || '',
                  country: customerDetails.address?.country || 'GB',
                  houseNumber: customerDetails.address?.houseNumber || ''
                }
              };

              try {
                const registrationResponse = await venposService.registerGuestUser(locationId, registrationDetails);
                if (!registrationResponse.isError && registrationResponse.data.webCustomerId) {
                  const newCustomerId = registrationResponse.data.webCustomerId;
                  setRegisteredCustomerId(newCustomerId);
                  
                  // Get membership customer details
                  const customerDetailsResponse = await venposService.getMembershipCustomerDetails(locationId, newCustomerId);
                  if (!customerDetailsResponse.isError && customerDetailsResponse.data) {
                    setMembershipCustomerDetails(customerDetailsResponse.data);
                  }
                }
              } catch (error) {
                console.error('Error registering customer for membership:', error);
                  // Don't throw - continue anyway
                }
              }

              if (membershipBasketItems.length > 0) {
                try {
                const basketResponse = await venposService.createBasketWithValidation(
                  locationId,
                  membershipBasketItems,
                  customerDetails,
                  basketOrderId || undefined,
                  registeredCustomerId || basketCustomerId || undefined
                );

                if (!basketResponse.isError) {
                  responseBasketId = basketResponse.data.basketId;
                  responseOrderId = basketResponse.data.orderId;
                  
                  if (responseBasketId) setBasketId(responseBasketId);
                  if (responseOrderId) setBasketOrderId(responseOrderId);

                  // Calculate total membership quantity (1 discount per membership)
                  const totalMembershipQuantity = membershipBasketItems.reduce((total, item) => {
                    return total + (item.quantity || 0);
                  }, 0);
                  
                  // ✅ NEW APPROACH: Create a single dynamic discount code for the total upgrade credit
                  // This is much more practical than pre-creating 112+ static codes
                  let upgradeDiscountCode = null;
                  
                  if (upgradeFromBooking && upgradeCreditAmount > 0 && totalMembershipQuantity > 0) {
                    try {
                      console.log('🔄 [UPGRADE] Creating dynamic discount code for upgrade credit');
                      setUpgradeDiscountApplied(false);
                      setUpgradeDiscountCodeState(null);
                      setUpgradeDiscountMessage('Generating upgrade discount code…');
                      setUpgradeDiscountValue(0);
                      
                      // Calculate the actual credit amount that will be applied
                      // Helper function to determine if a membership is adult or child
                      const isChildMembership = (ticket) => {
                        const name = (ticket.name || '').toLowerCase();
                        const description = (ticket.description || '').toLowerCase();
                        
                        // Exclude free child tickets (under 3, under 2, infant) - these don't count as child memberships
                        if (name.includes('under 3') || name.includes('under 2') || name.includes('infant') ||
                            description.includes('under 3') || description.includes('under 2') || description.includes('infant')) {
                          return false;
                        }
                        
                        return name.includes('child') || 
                               name.includes('junior') || 
                               description.includes('child') || 
                               description.includes('junior');
                      };
                      
                      // Helper function to check if ticket is a free "under 3" ticket (should not count for any discounts)
                      const isFreeUnder3Ticket = (ticket) => {
                        const name = (ticket.name || '').toLowerCase();
                        const description = (ticket.description || '').toLowerCase();
                        return name.includes('under 3') || name.includes('under 2') || name.includes('infant') ||
                               description.includes('under 3') || description.includes('under 2') || description.includes('infant');
                      };
                      
                      // Helper to check if membership is family type
                      const isFamilyMembership = (ticket) => {
                        const name = (ticket.name || '').toLowerCase();
                        const description = (ticket.description || '').toLowerCase();
                        return name.includes('family') || description.includes('family');
                      };
                      
                      // Separate memberships by type
                      const childMemberships = membershipBasketItems.filter((item) => {
                        const ticket = selectedTickets.find(t => t.webSaleItemId === item.webSaleItemId);
                        return ticket && isChildMembership(ticket) && !isFamilyMembership(ticket);
                      });
                      const adultMemberships = membershipBasketItems.filter((item) => {
                        const ticket = selectedTickets.find(t => t.webSaleItemId === item.webSaleItemId);
                        return ticket && !isChildMembership(ticket) && !isFamilyMembership(ticket) && !isFreeUnder3Ticket(ticket);
                      });
                      const familyMemberships = membershipBasketItems.filter((item) => {
                        const ticket = selectedTickets.find(t => t.webSaleItemId === item.webSaleItemId);
                        return ticket && isFamilyMembership(ticket);
                      });
                      
                      const childMembershipCount = childMemberships.reduce((total, item) => total + (item.quantity || 0), 0);
                      const adultMembershipCount = adultMemberships.reduce((total, item) => total + (item.quantity || 0), 0);
                      const familyMembershipCount = familyMemberships.reduce((total, item) => total + (item.quantity || 0), 0);
                      
                      // ✅ CRITICAL FIX: Family memberships need 2 adult + 3 child credits each
                      const totalAdultNeeded = adultMembershipCount + (familyMembershipCount * 2);
                      const totalChildNeeded = childMembershipCount + (familyMembershipCount * 3);
                      
                      console.log('🔍 [UPGRADE] Membership breakdown:', {
                        childMembershipCount,
                        adultMembershipCount,
                        familyMembershipCount,
                        totalChildNeeded,
                        totalAdultNeeded
                      });
                      
                      // Get available items
                      const availableChildItems = Array.isArray(sortedUpgradeItems) 
                        ? sortedUpgradeItems.filter(item => isChildProductCode(item.productCode))
                        : (sortedUpgradeItems.child || []);
                      const availableAdultItems = Array.isArray(sortedUpgradeItems)
                        ? sortedUpgradeItems.filter(item => isAdultProductCode(item.productCode))
                        : (sortedUpgradeItems.adult || []);
                      
                      // Calculate total credit that will be applied (using totalChildNeeded and totalAdultNeeded)
                      let totalCreditToApply = 0;
                      
                      if (totalChildNeeded > 0 && availableChildItems.length > 0) {
                        const childItemsToUse = availableChildItems.slice(0, totalChildNeeded);
                        const childCredit = childItemsToUse.reduce((total, item) => total + parseFloat(item.value || 0), 0);
                        totalCreditToApply += childCredit;
                        console.log('💰 [UPGRADE] Child credit:', childCredit, 'from', childItemsToUse.length, 'items');
                      }
                      
                      if (totalAdultNeeded > 0 && availableAdultItems.length > 0) {
                        const adultItemsToUse = availableAdultItems.slice(0, totalAdultNeeded);
                        const adultCredit = adultItemsToUse.reduce((total, item) => total + parseFloat(item.value || 0), 0);
                        totalCreditToApply += adultCredit;
                        console.log('💰 [UPGRADE] Adult credit:', adultCredit, 'from', adultItemsToUse.length, 'items');
                      }
                      
                      console.log('💰 [UPGRADE] Total credit to apply:', totalCreditToApply);
                      
                      if (totalCreditToApply > 0) {
                        // THROTTLING: Prevent rapid discount code creation (safety measure)
                        const now = Date.now();
                        const THROTTLE_MS = 3000; // 3 seconds between discount creations
                        
                        if (isCreatingDiscount) {
                          console.warn('⏳ [THROTTLE] Discount creation already in progress, skipping...');
                          setUpgradeDiscountMessage('⏳ Please wait, discount creation in progress...');
                          return;
                        }
                        
                        if (now - lastDiscountCreationTime < THROTTLE_MS) {
                          const waitTime = Math.ceil((THROTTLE_MS - (now - lastDiscountCreationTime)) / 1000);
                          console.warn(`⏳ [THROTTLE] Too soon since last creation, wait ${waitTime}s`);
                          setUpgradeDiscountMessage(`⏳ Please wait ${waitTime} second(s) before creating another discount...`);
                          return;
                        }
                        
                        // Set creating flag and update timestamp
                        setIsCreatingDiscount(true);
                        setLastDiscountCreationTime(now);
                        
                        // Build API URL based on environment
                        const hostname = window.location.hostname;
                        let apiBaseUrl;
                        if (hostname === 'localhost' || hostname === '127.0.0.1') {
                          apiBaseUrl = 'http://localhost:8888/Website/front-end/public/api';
                        } else if (hostname.includes('staging.belvoircastle.com')) {
                          apiBaseUrl = 'https://staging.belvoircastle.com/api';
                        } else {
                          apiBaseUrl = 'https://www.belvoircastle.com/api';
                        }
                        
                        const createDiscountUrl = `${apiBaseUrl}/create-upgrade-discount.php`;
                        
                        console.log('🔄 [UPGRADE] Calling create-upgrade-discount API:', {
                          url: createDiscountUrl,
                          locationId,
                          amount: totalCreditToApply,
                          bookingRef: upgradeFromBooking
                        });
                        
                        try {
                          // Get API key from environment or use default for development
                          // In production, this should be set via environment variable
                          const apiKey = process.env.REACT_APP_DISCOUNT_API_KEY || '';
                          
                          const discountResponse = await fetch(createDiscountUrl, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              ...(apiKey && { 'X-API-Key': apiKey })
                            },
                            body: JSON.stringify({
                              locationId: locationId,
                              amount: totalCreditToApply,
                              bookingRef: upgradeFromBooking,
                              expiresInDays: 30,
                              ...(apiKey && { apiKey: apiKey }) // Also include in body for backward compatibility
                            }),
                            // Prevent any redirects that might cause page reload
                            redirect: 'manual'
                          });
                          
                          // Check if response is ok
                          if (!discountResponse.ok) {
                            let errorText = '';
                            try {
                              errorText = await discountResponse.text();
                            } catch (textError) {
                              errorText = `HTTP ${discountResponse.status}`;
                            }
                            console.error('❌ [UPGRADE] Failed to create discount code:', errorText);
                            // Don't throw - just log and continue without discount
                            console.warn('⚠️ [UPGRADE] Continuing without discount code - user can still proceed');
                            setUpgradeDiscountApplied(false);
                            setUpgradeDiscountMessage('Unable to create discount code automatically.');
                            setIsCreatingDiscount(false);
                            
                            // Log the failure
                            await logDiscountEvent(
                              'CREATED',
                              'N/A',
                              upgradeCreditAmount || 0,
                              `HTTP error: ${errorText}`,
                              'ERROR'
                            );
                          } else {
                            // Try to parse JSON response
                            let discountData;
                            try {
                              const responseText = await discountResponse.text();
                              discountData = JSON.parse(responseText);
                            } catch (parseError) {
                              console.error('❌ [UPGRADE] Failed to parse discount response:', parseError);
                              console.warn('⚠️ [UPGRADE] Response might not be JSON, continuing without discount');
                              discountData = null;
                              setUpgradeDiscountApplied(false);
                              setUpgradeDiscountMessage('Unexpected response from discount service.');
                            }
                            
                            if (discountData && discountData.success && discountData.discountCode) {
                              upgradeDiscountCode = discountData.discountCode;
                              console.log('✅ [UPGRADE] Dynamic discount code created:', upgradeDiscountCode);
                              console.log('✅ [UPGRADE] Discount amount:', totalCreditToApply);
                              console.log('✅ [UPGRADE] Discount expiry:', discountData.expiryDate);
                              setUpgradeDiscountCodeState(upgradeDiscountCode);
                              setUpgradeDiscountMessage('Discount code created. Applying…');
                              
                              // Track created code for undo functionality
                              setCreatedDiscountCodes(prev => [...prev, upgradeDiscountCode]);
                              
                              // Log the successful creation
                              await logDiscountEvent(
                                'CREATED',
                                upgradeDiscountCode,
                                totalCreditToApply,
                                `Created for ${totalChildNeeded} child + ${totalAdultNeeded} adult credits (${childMembershipCount} child, ${adultMembershipCount} adult, ${familyMembershipCount} family memberships)`,
                                'SUCCESS'
                              );
                              
                              // Give Venpos a moment to process the new discount code
                              // This helps ensure it's available when we load the basket
                              console.log('⏳ [UPGRADE] Waiting 500ms for Venpos to process discount code...');
                              await new Promise(resolve => setTimeout(resolve, 500));
                            } else {
                              console.error('❌ [UPGRADE] API returned error:', discountData?.error || 'Unknown error');
                              // Don't throw - just log and continue without discount
                              console.warn('⚠️ [UPGRADE] Discount code creation failed, but continuing anyway');
                              setUpgradeDiscountApplied(false);
                              setUpgradeDiscountMessage('Discount code could not be created.');
                            }
                          }
                        } catch (fetchError) {
                          // Catch any network errors or other fetch failures
                          console.error('❌ [UPGRADE] Fetch error creating discount code:', fetchError);
                          console.warn('⚠️ [UPGRADE] Network error - continuing without discount code');
                          // Don't rethrow - this is non-critical
                          setUpgradeDiscountApplied(false);
                          setUpgradeDiscountMessage('Network error while creating discount code.');
                          setIsCreatingDiscount(false);
                          
                          // Log the error
                          await logDiscountEvent(
                            'CREATED',
                            'N/A',
                            upgradeCreditAmount || 0,
                            `Network error: ${fetchError.message}`,
                            'ERROR'
                          );
                        }
                      }
                    } catch (error) {
                      console.error('❌ [UPGRADE] Error creating dynamic discount code:', error);
                      console.error('❌ [UPGRADE] Error details:', {
                        message: error.message,
                        stack: error.stack,
                        name: error.name
                      });
                      // Don't fail the whole basket creation - just log the error
                      // The user can still proceed, they just won't get the upgrade discount
                      // Make sure we don't throw - this is non-critical
                      // Set upgradeDiscountCode to null to ensure we don't use a bad value
                      upgradeDiscountCode = null;
                      setUpgradeDiscountApplied(false);
                      setUpgradeDiscountMessage('Error while creating discount code.');
                      setIsCreatingDiscount(false);
                      
                      // Log the error
                      await logDiscountEvent(
                        'CREATED',
                        'N/A',
                        upgradeCreditAmount || 0,
                        `Exception: ${error.message}`,
                        'ERROR'
                      );
                    }
                  }
                  
                  // Log that we're continuing after discount code creation
                  console.log('🔄 [UPGRADE] Continuing with basket creation, discount code:', upgradeDiscountCode || 'none');
                  
                  // Prevent any potential page reloads by ensuring we're still in the same context
                  if (typeof window !== 'undefined' && window.location) {
                    const currentUrl = window.location.href;
                    console.log('🔍 [UPGRADE] Current URL after discount creation:', currentUrl);
                  }

                  // Load basket to get full details
                  const saverCode = getFamilySaverCode();
                  const circusCode = await createCircusDiscountIfNeeded();
                  const allDiscounts = [
                    ...(saverCode ? [saverCode] : []),
                    ...(circusCode ? [circusCode] : []),
                    ...(upgradeDiscountCode ? [upgradeDiscountCode] : []) // Use single dynamic code instead of multiple codes
                  ];
                  
                  console.log('🛒 [UPGRADE] Loading basket with discounts:', allDiscounts);
                  
                  const loadPayload = {
                    pageId,
                    items: membershipBasketItems,
                    giftAidSelected: false,
                    discounts: allDiscounts,
                    discountsFromMembership: [],
                    customerId: registeredCustomerId || basketCustomerId || undefined,
                    orderId: responseOrderId || responseBasketId
                  };

                  const loadBasketWithRetry = async (attempt = 0, maxAttempts = 2) => {
                    try {
                      const basketResult = await venposService.loadBasket(locationId, loadPayload, saverCode);
                      
                    if (basketResult?.isError) {
                        console.error('❌ [UPGRADE] Basket loading failed:', basketResult.error);
                        if (attempt >= maxAttempts) {
                          setUpgradeDiscountApplied(false);
                        setUpgradeDiscountValue(0);
                          setUpgradeDiscountMessage('Unable to load basket with discount code.');
                          return basketResult;
                        }
                        setUpgradeDiscountApplied(false);
                      setUpgradeDiscountValue(0);
                        setUpgradeDiscountMessage('Retrying basket load with discount code…');
                        await new Promise(resolve => setTimeout(resolve, 600));
                        return await loadBasketWithRetry(attempt + 1, maxAttempts);
                      }
                      
                      const basketDiscounts = basketResult?.data?.discounts || [];
                      const system1CodeInBasket = basketDiscounts.find(d => {
                        const code = d.code || d.discountCode || d;
                        return typeof code === 'string' && code.startsWith('System1-');
                      });
                      
                    if (upgradeDiscountCode && !system1CodeInBasket) {
                        console.warn('⚠️ [UPGRADE] Discount code created but not yet in basket response');
                        if (attempt >= maxAttempts) {
                          setUpgradeDiscountApplied(false);
                        setUpgradeDiscountValue(0);
                          setUpgradeDiscountMessage('Discount code created. If it does not appear, please enter it manually during payment.');
                          return basketResult;
                        }
                        setUpgradeDiscountApplied(false);
                      setUpgradeDiscountValue(0);
                        setUpgradeDiscountMessage('Waiting for Venpos to apply discount code…');
                        await new Promise(resolve => setTimeout(resolve, 700));
                        return await loadBasketWithRetry(attempt + 1, maxAttempts);
                      }
                      
                    if (system1CodeInBasket) {
                        const confirmedCode = system1CodeInBasket.code || system1CodeInBasket.discountCode || system1CodeInBasket;
                      const discountAmount = Math.abs(parseFloat(
                        system1CodeInBasket.discountedAmount ??
                        system1CodeInBasket.amount ??
                        system1CodeInBasket.value ??
                        0
                      ));
                      console.log('✅ [UPGRADE] Discount code confirmed in basket:', confirmedCode, 'amount:', discountAmount);
                      setUpgradeDiscountApplied(true);
                      setUpgradeDiscountCodeState(confirmedCode);
                      setUpgradeDiscountValue(discountAmount);
                      setUpgradeDiscountMessage('Discount code applied');
                      setIsCreatingDiscount(false); // Reset creating flag
                      
                      // Log successful application
                      await logDiscountEvent(
                        'APPLIED',
                        confirmedCode,
                        discountAmount,
                        'Discount successfully applied to basket',
                        'SUCCESS'
                      );
                    } else if (!upgradeDiscountCode) {
                      setUpgradeDiscountApplied(false);
                      setUpgradeDiscountValue(0);
                      setUpgradeDiscountMessage('');
                    }
                    
                    return basketResult;
                  } catch (basketError) {
                    console.error('❌ [UPGRADE] Error loading basket:', basketError);
                    setUpgradeDiscountApplied(false);
                    setUpgradeDiscountValue(0);
                    setUpgradeDiscountMessage('Error loading basket with discount code.');
                    if (attempt >= maxAttempts) {
                      return null;
                    }
                    await new Promise(resolve => setTimeout(resolve, 700));
                    return await loadBasketWithRetry(attempt + 1, maxAttempts);
                  }
                };
                  
                  let finalBasket = await loadBasketWithRetry();
                  
                  // Set basket even if there were issues - the journey can continue
                  // If we don't have a basket, try to create one without the discount code
                  if (!finalBasket || finalBasket.isError) {
                    console.warn('⚠️ [UPGRADE] Basket loading had issues, creating fallback basket...');
                    try {
                      // Create a fallback basket without the discount code
                      const fallbackPayload = {
                        ...loadPayload,
                        discounts: saverCode ? [saverCode] : [] // Remove upgrade discount code
                      };
                      const fallbackBasket = await venposService.loadBasket(locationId, fallbackPayload, saverCode);
                      if (!fallbackBasket?.isError) {
                        finalBasket = fallbackBasket;
                        console.log('✅ [UPGRADE] Fallback basket created successfully');
                        setUpgradeDiscountApplied(false);
                      setUpgradeDiscountValue(0);
                        setUpgradeDiscountMessage('Fallback basket created without discount code.');
                      }
                    } catch (fallbackError) {
                      console.error('❌ [UPGRADE] Fallback basket creation also failed:', fallbackError);
                    setUpgradeDiscountValue(0);
                      setUpgradeDiscountMessage('Unable to confirm discount at this time.');
                    }
                  }
                  
                  // Always set basket and continue - don't block the user journey
                  if (finalBasket && !finalBasket.isError) {
                  setVenposBasket(finalBasket);
                  } else if (venposBasket) {
                    // Keep existing basket if we have one
                    console.log('⚠️ [UPGRADE] Using existing basket due to errors');
                  } else {
                    // If we have no basket at all, try one more time without discount
                    console.log('🔄 [UPGRADE] No basket available, creating basic basket...');
                    try {
                      const emergencyPayload = {
                        pageId,
                        items: membershipBasketItems,
                        giftAidSelected: false,
                        discounts: allDiscounts,
                        discountsFromMembership: [],
                        customerId: registeredCustomerId || basketCustomerId || undefined,
                        orderId: responseOrderId || responseBasketId
                      };
                      const emergencyBasket = await venposService.loadBasket(locationId, emergencyPayload, saverCode);
                      if (!emergencyBasket?.isError) {
                        setVenposBasket(emergencyBasket);
                        console.log('✅ [UPGRADE] Emergency basket created');
                      }
                    } catch (emergencyError) {
                      console.error('❌ [UPGRADE] Emergency basket creation failed:', emergencyError);
                    }
                  }
                  
                  // Set preflight done FIRST to prevent useEffect from triggering
                  setPreflightDone(true);
                  console.log('✅ [UPGRADE] Preflight completed, proceeding to next step');
                  
                  // Use a ref to prevent multiple navigation attempts
                  const navigationRef = { dispatched: false };
                  
                  // Ensure we navigate to the next step - don't let errors block navigation
                  // Use setTimeout with a delay to ensure all state updates complete
                  setTimeout(() => {
                    if (navigationRef.dispatched) {
                      console.warn('⚠️ [UPGRADE] Navigation already dispatched, skipping');
                      return;
                    }
                    
                    try {
                      navigationRef.dispatched = true;
                      console.log('🚀 [UPGRADE] Dispatching navigation to next step...');
                      console.log('🚀 [UPGRADE] Current step before navigation:', currentStep);
              dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: false });
                      dispatch({ type: BOOKING_ACTIONS.NEXT_STEP });
                      console.log('✅ [UPGRADE] Navigation to next step dispatched successfully');
                    } catch (navError) {
                      console.error('❌ [UPGRADE] Navigation error:', navError);
                      console.error('❌ [UPGRADE] Navigation error stack:', navError.stack);
                      // Force navigation even if dispatch fails
                      dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: false });
                    }
                  }, 150);
                } // End of if (!basketResponse.isError)

                // Stop further processing in handleNext - navigation will now be handled by the membership flow
              return;
              } catch (innerError) {
                console.error('❌ [UPGRADE] Error in inner basket creation:', innerError);
                // Continue to outer catch
                throw innerError;
              }
            } // End of if (membershipBasketItems.length > 0)
            } catch (error) {
              console.error('❌ [UPGRADE] Error in membership basket creation flow:', error);
              console.error('❌ [UPGRADE] Error stack:', error.stack);
              
              // Don't block the user - try to continue with a basic basket
              // The discount might not apply, but they can still complete the purchase
              try {
                console.log('🔄 [UPGRADE] Attempting to create basic basket without discount...');
                const basicPayload = {
                  pageId,
                  items: membershipBasketItems,
                  giftAidSelected: false,
                  discounts: getFamilySaverCode() ? [getFamilySaverCode()] : [],
                  discountsFromMembership: [],
                  customerId: registeredCustomerId || basketCustomerId || undefined,
                  orderId: responseOrderId || responseBasketId
                };
                const basicBasket = await venposService.loadBasket(locationId, basicPayload, getFamilySaverCode());
                if (!basicBasket?.isError) {
                  setVenposBasket(basicBasket);
                  setPreflightDone(true);
                  console.log('✅ [UPGRADE] Basic basket created, continuing journey');
                  setUpgradeDiscountApplied(false);
                  setUpgradeDiscountValue(0);
                  setUpgradeDiscountMessage('Continuing without upgrade discount code.');
                  
                  // Navigate to review step even with error
            requestAnimationFrame(() => {
              dispatch({ type: BOOKING_ACTIONS.NEXT_STEP });
              dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: false });
            });
                  return;
                }
              } catch (fallbackError) {
                console.error('❌ [UPGRADE] Fallback basket creation also failed:', fallbackError);
              }
              
              // Even if everything failed, try to continue with whatever we have
              // Don't block the user - they can still proceed
              console.warn('⚠️ [UPGRADE] Multiple basket creation attempts failed, but continuing anyway');
              setUpgradeDiscountApplied(false);
              setUpgradeDiscountValue(0);
              setUpgradeDiscountMessage('Could not apply upgrade discount automatically.');
              
              // Try to use existing basket or create a minimal one
              if (!venposBasket && membershipBasketItems.length > 0) {
                try {
                  const minimalPayload = {
                    pageId,
                    items: membershipBasketItems,
                    giftAidSelected: false,
                    discounts: [],
                    discountsFromMembership: [],
                    customerId: registeredCustomerId || basketCustomerId || undefined
                  };
                  const minimalBasket = await venposService.loadBasket(locationId, minimalPayload, null);
                  if (!minimalBasket?.isError) {
                    setVenposBasket(minimalBasket);
                    console.log('✅ [UPGRADE] Minimal basket created as last resort');
                  setUpgradeDiscountApplied(false);
                  setUpgradeDiscountValue(0);
                  setUpgradeDiscountMessage('Proceeding without discount code.');
                  }
                } catch (minimalError) {
                  console.error('❌ [UPGRADE] Even minimal basket failed:', minimalError);
                setUpgradeDiscountValue(0);
                  setUpgradeDiscountMessage('Unable to continue with discount code.');
                }
              }
              
              // Always navigate forward - don't block the user
              setPreflightDone(true);
              // Use setTimeout to ensure state updates complete before navigation
              setTimeout(() => {
                try {
                  dispatch({ type: BOOKING_ACTIONS.NEXT_STEP });
                  dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: false });
                  console.log('✅ [UPGRADE] Navigation dispatched from catch block');
                } catch (navError) {
                  console.error('❌ [UPGRADE] Navigation error in catch:', navError);
                  dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: false });
                }
              }, 100);
              return;
            } // End of catch (error) for membership basket creation
            
            // Navigate to review step (this should already be handled above, but keep as fallback)
            if (!preflightDone) {
              setTimeout(() => {
                try {
                  dispatch({ type: BOOKING_ACTIONS.NEXT_STEP });
                  dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: false });
                  console.log('✅ [UPGRADE] Navigation dispatched from fallback');
                } catch (navError) {
                  console.error('❌ [UPGRADE] Navigation error in fallback:', navError);
                  dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: false });
                }
              }, 100);
            }
            return;
          }
          
          // Register customer with Venpos when leaving customer details step (only if we have complete details)
          if (customerDetails?.firstName && customerDetails?.lastName && customerDetails?.email && !registeredCustomerId) {


            // Ensure customer details are properly structured for Venpos API
            const registrationDetails = {
              ...customerDetails,
              // Ensure address is properly structured with required houseNameNumber
              address: {
                line1: customerDetails.address?.line1 || '',
                line2: customerDetails.address?.line2 || '',
                city: customerDetails.address?.city || '',
                postcode: customerDetails.address?.postcode || '',
                country: customerDetails.address?.country || 'GB',
                houseNumber: customerDetails.address?.houseNumber || '' // Required field
              }
            };

            try {
              const registrationResponse = await venposService.registerGuestUser(locationId, registrationDetails);
              if (!registrationResponse.isError && registrationResponse.data.webCustomerId) {
                const newCustomerId = registrationResponse.data.webCustomerId;
                setRegisteredCustomerId(newCustomerId);

                // Wait for state update to complete
                // proceed immediately without delay

                // Run preflight after customer registration with the new customer ID
                if (selectedTickets?.length > 0 && selectedDate && selectedTimeSlot && !preflightDone) {

                  try {
                    await runPreflightWithCustomerId(newCustomerId);

                  } catch (error) {

                    // Continue anyway - the payment step can handle fallbacks
                  }
                }

                // Navigate to next step after registration and preflight

                requestAnimationFrame(() => {
                  dispatch({ type: BOOKING_ACTIONS.NEXT_STEP });
                  dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: false });
                });
                return; // Early return after customer registration and navigation
              } else {


                // Don't continue - customer registration is required
                throw new Error('Customer registration failed - cannot proceed with booking');
              }
            } catch (error) {

              throw error; // Re-throw to prevent booking continuation
            }
          } else {

            // Run preflight for already-registered customer
            if (selectedTickets?.length > 0 && selectedDate && selectedTimeSlot && !preflightDone) {
          try {
            await runPreflight();
            // Add a small delay to prevent rapid state changes
            // proceed immediately without delay
          } catch (error) {

            // Continue anyway - the payment step can handle fallbacks
              }
            }
          }
        }

        // Use requestAnimationFrame to smooth the transition
        requestAnimationFrame(() => {
          dispatch({ type: BOOKING_ACTIONS.NEXT_STEP });
          dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: false });
        });
      }
    } catch (error) {

      dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: false });
      dispatch({ 
        type: BOOKING_ACTIONS.SET_ERROR, 
        payload: 'Navigation failed. Please try again.' 
      });
    }
  };

  const handleRestartJourney = async ({
    preserveCustomerDetails = true,
    preserveMembershipDetails = true,
  } = {}) => {

    setShowRestartModal(false);
    
    // Preserve details to restore after reset (for back/forward UX)
    const savedCustomerDetails = preserveCustomerDetails ? customerDetails : null;
    const savedCustomerAddress = preserveCustomerDetails ? customerDetails?.address : null;
    const savedMembershipDetails = preserveMembershipDetails ? membershipDetails : null;
    
    // Clear ALL state and restart (but optionally preserve details)
    setBasketId(null);
    setVenposBasket(null);
    setRegisteredCustomerId(null);
    setBasketCustomerId(null);
    setBasketOrderId(null);
    setPreflightDone(false);
    setAppliedSaverCode(null);
    setPaymentResult(null);
    setBookingReference(null);
    setFormattedEventQuestions([]);
    
    // Reset booking context unless we are preserving details (to avoid wiping them)
    if (!preserveCustomerDetails || !preserveMembershipDetails) {
    dispatch({ type: BOOKING_ACTIONS.RESET_BOOKING });
    } else {
      // Soft reset selections/dates but keep customer + membership details
      dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_TICKETS, payload: [] });
      dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_DATE, payload: null });
      dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_TIME_SLOT, payload: null });
      dispatch({ type: BOOKING_ACTIONS.SET_CURRENT_STEP, payload: 1 });
    }
    
    // Reload tickets for fresh start
    setTimeout(async () => {
      try {
        const response = await venposService.getWebSaleItems(
          locationId,
          pageId,
          formatDateLocal(new Date())
        );
        const items = (response?.data?.webSaleItems) || (response?.webSaleItems) || [];
        const normalized = items.map((it) => {
          const id = it.webSaleItemId || it.id;
          return {
            webSaleItemId: id,
            webSaleItemTypeID: it.webSaleItemTypeID,
            name: it.name || 'Ticket',
            description: it.description || '',
            price: typeof it.price === 'number' ? it.price : parseFloat(it.price) || 0,
            productCode: it.productCode || it.ProductCode || it.code || it.sku || '',
            maxPurchaseQty: isChoirFreeAdultTicket(it) ? 1 : (it.maxPurchaseQty || 50),
            quantity: 0 // Start fresh
          };
        }).filter(it => it.webSaleItemId);
        
        setTickets(normalized);
        dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_TICKETS, payload: normalized });
      } catch (err) {

      } finally {
        // Restore preserved details after reset (always run)
        if (savedCustomerDetails) {
          const { address, ...rest } = savedCustomerDetails;
          dispatch({ type: BOOKING_ACTIONS.UPDATE_CUSTOMER_DETAILS, payload: rest });
          if (savedCustomerAddress || address) {
            dispatch({ type: BOOKING_ACTIONS.UPDATE_CUSTOMER_ADDRESS, payload: savedCustomerAddress || address });
          }
        }
        if (savedMembershipDetails) {
          setMembershipDetails(savedMembershipDetails);
        }
      }
    }, 100);
  };

  const handleBack = async () => {

    // Membership journeys: Back should restart to step 1
    if (isMembershipJourney && currentStep > 1) {
      await handleRestartJourney({ preserveCustomerDetails: true, preserveMembershipDetails: true });
      dispatch({ type: BOOKING_ACTIONS.SET_CURRENT_STEP, payload: 1 });
      dispatch({ type: BOOKING_ACTIONS.CLEAR_ERROR });
      return;
    }

    // SPECIAL CASE: Step 2 (Date/Time) → Dog Park goes back to dog-friendly page; all others show Restart Modal
    if (currentStep === 2) {
      if (isDogParkBooking) {
        navigate('/about/dog-friendly');
        return;
      }
      setShowRestartModal(true);
      return; // Show modal instead of navigating
    }
    
    const targetStep = currentStep - 1;
    
    // Handle state cleanup based on which step we're going back from
    switch (currentStep) {
      case 6: // Going back from Payment
        // CHANGED: Allow back from payment via cancel button only
        // This is triggered by the onCancel handler in PaymentPage

        // Clear basket and preflight to force fresh initialization with current prices
        dispatch({ type: BOOKING_ACTIONS.SET_BASKET_ID, payload: null });
        dispatch({ type: BOOKING_ACTIONS.SET_VENPOS_BASKET, payload: null });
        dispatch({ type: BOOKING_ACTIONS.SET_PREFLIGHT_DONE, payload: false });
        // Note: Payment session will be re-created when returning to step 6
        break;
        
      case 5: // Going back from Review

        // Clear basket state to force recreation when moving forward
        dispatch({ type: BOOKING_ACTIONS.SET_BASKET_ID, payload: null });
        dispatch({ type: BOOKING_ACTIONS.SET_VENPOS_BASKET, payload: null });
        dispatch({ type: BOOKING_ACTIONS.SET_PREFLIGHT_DONE, payload: false });
        break;
        
      case 4: // Going back from Customer Details
        // Skip upsells for grotto retail village and memberships - go directly to step 2
        if (isMembershipJourney || NO_UPSELL_PAGE_IDS.includes(pageId)) {
          dispatch({ type: BOOKING_ACTIONS.SET_CURRENT_STEP, payload: 2 });
          // Keep customer details - they can be reused
          // Clear basket to allow updates if tickets/time changes
          dispatch({ type: BOOKING_ACTIONS.SET_BASKET_ID, payload: null });
          dispatch({ type: BOOKING_ACTIONS.SET_VENPOS_BASKET, payload: null });
          dispatch({ type: BOOKING_ACTIONS.SET_PREFLIGHT_DONE, payload: false });
          return;
        }

        // Keep customer details - they can be reused
        // Clear basket to allow updates if tickets/time changes
        dispatch({ type: BOOKING_ACTIONS.SET_BASKET_ID, payload: null });
        dispatch({ type: BOOKING_ACTIONS.SET_VENPOS_BASKET, payload: null });
        break;
        
      case 3: // Going back from Upsells

        // Keep upsells - user might just want to adjust entry time
        // Upsells will be validated if time/date changes
        // Only clear basket to allow time updates
        dispatch({ type: BOOKING_ACTIONS.SET_BASKET_ID, payload: null });
        dispatch({ type: BOOKING_ACTIONS.SET_VENPOS_BASKET, payload: null });
        dispatch({ type: BOOKING_ACTIONS.SET_PREFLIGHT_DONE, payload: false });
        break;
        
      default:

        break;
    }
    
    // Clear any errors
    dispatch({ type: BOOKING_ACTIONS.CLEAR_ERROR });
    
    // Navigate back
    dispatch({ type: BOOKING_ACTIONS.PREV_STEP });
  };

  const processBooking = async (completedPaymentResult = null) => {
    console.log('📦 [BOOKING] processBooking called');
    console.log('📦 [BOOKING] completedPaymentResult:', completedPaymentResult);
    console.log('📦 [BOOKING] paymentResult state:', paymentResult);
    // Show processing overlay
    setIsProcessingPayment(true);
    
    // Hoist variables so they're available in catch block
    let responseHeaderId = null;
    let existingBookingReference = null;
    try {
      dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: true });


      let finalBasketId = basketId;
      // Store WebTransHeaderId and booking reference from order creation for later confirmation step

      // Use existing basket if available, otherwise create one (but don't force new)
      if (!finalBasketId) {

        const basketResult = await createAndValidateBasket(); // Don't force new - reuse existing IDs if available
        finalBasketId = basketResult.basketId;
      } else {
        // Refresh existing basket to ensure we have latest totals

        await refreshExistingBasket();
      }

      if (!finalBasketId) {
        throw new Error('Failed to create or retrieve basket');
      }

      // Calculate final amount from venpos basket (use finalPrice, not totalAmount)
      const finalAmount = parseFloat(venposBasket?.data?.finalPrice ?? 
                         venposBasket?.finalPrice ?? 
                         calculateTotal()) || 0;
      
      console.log('📦 [BOOKING] Final amount calculated:', finalAmount);
      console.log('📦 [BOOKING] Venpos basket finalPrice:', venposBasket?.data?.finalPrice);
      console.log('📦 [BOOKING] Venpos basket (root) finalPrice:', venposBasket?.finalPrice);
      console.log('📦 [BOOKING] Calculated total:', calculateTotal());

      // Use the passed payment result or fall back to state
      const activePaymentResult = completedPaymentResult || paymentResult;
      console.log('📦 [BOOKING] activePaymentResult:', activePaymentResult);
      console.log('📦 [BOOKING] Using completedPaymentResult?', !!completedPaymentResult);
      console.log('📦 [BOOKING] Using paymentResult state?', !completedPaymentResult && !!paymentResult);

      // Handle payment completion (skip for free bookings)
      if (finalAmount > 0) {
        console.log('📦 [BOOKING] Final amount > 0, processing paid booking');
        // ✅ CRITICAL: Payment must be completed before booking confirmation
        // Do NOT proceed if payment hasn't been completed
        if (!activePaymentResult) {
          console.error('❌ [BOOKING] No activePaymentResult found!');
          console.error('❌ [BOOKING] completedPaymentResult:', completedPaymentResult);
          console.error('❌ [BOOKING] paymentResult state:', paymentResult);
          throw new Error('Payment not completed. Please complete payment first.');
        }
        
        // ✅ ADDITIONAL VALIDATION: Ensure payment was actually successful
        if (!activePaymentResult.authCode && !activePaymentResult.transactionId && !activePaymentResult.paymentToken) {
          console.error('❌ [BOOKING] Payment result missing required fields (authCode, transactionId, or paymentToken)');
          throw new Error('Payment not completed successfully. Please complete payment first.');
        }
        
        console.log('✅ [BOOKING] activePaymentResult found, proceeding with payment completion');

        // **CRITICAL**: Tell Venpos that the Payment Sense payment completed successfully
        console.log('💳 [PAYMENT] Calling venposService.completePayment');
        console.log('💳 [PAYMENT] Parameters:', {
          locationId,
          finalBasketId,
          paymentToken: activePaymentResult.paymentToken,
          paymentResult: activePaymentResult,
          pageId
        });

        try {
          // Call completePayment to tell Venpos the payment succeeded
          const completionResult = await venposService.completePayment(
            locationId,
            finalBasketId,
            activePaymentResult.paymentToken,
            activePaymentResult,
            pageId
          );
          
          console.log('💳 [PAYMENT] completePayment response:', completionResult);
          console.log('💳 [PAYMENT] completionResult.isError:', completionResult.isError);

          if (!completionResult.isError) {
            console.log('✅ [PAYMENT] completePayment succeeded');
            console.log('✅ [PAYMENT] completionResult.data:', completionResult.data);

            // Extract WebTransHeaderId AND booking reference from completion response
            if (completionResult.data?.orderId || completionResult.data?.webTransHeaderID) {
              responseHeaderId = completionResult.data.orderId || completionResult.data.webTransHeaderID;
              console.log('✅ [PAYMENT] Using responseHeaderId from completionResult:', responseHeaderId);
            } else {
              responseHeaderId = activePaymentResult.orderId || finalBasketId;
              console.log('✅ [PAYMENT] Using responseHeaderId from activePaymentResult or basketId:', responseHeaderId);
            }
            
            // Capture booking reference if available (avoids polling later)
            if (completionResult.data?.bookingReference) {
              existingBookingReference = completionResult.data.bookingReference;
              console.log('✅ [PAYMENT] Booking reference from completionResult:', existingBookingReference);
            } else {
              console.warn('⚠️ [PAYMENT] No booking reference in completionResult.data');
              console.log('⚠️ [PAYMENT] completionResult.data keys:', completionResult.data ? Object.keys(completionResult.data) : 'no data');
              console.log('⚠️ [PAYMENT] completionResult keys:', Object.keys(completionResult));
            }
          } else {
            console.warn('⚠️ [PAYMENT] completePayment returned error:', completionResult.error);
            responseHeaderId = activePaymentResult.orderId || finalBasketId;
            console.log('⚠️ [PAYMENT] Using fallback responseHeaderId:', responseHeaderId);
          }
        } catch (completionError) {
          console.error('❌ [PAYMENT] Error in completePayment call:', completionError);
          console.error('❌ [PAYMENT] Error stack:', completionError.stack);
          // Continue with the order ID we have
          responseHeaderId = activePaymentResult.orderId || finalBasketId;
          console.log('⚠️ [PAYMENT] Using fallback responseHeaderId after error:', responseHeaderId);
        }

      } else {

      }

      // For paid bookings, use the WebTransHeaderId from the order that was created by POST /Payment
      let confirmedHeaderId = null;
      if (finalAmount > 0) {


        // The order was already created when we called POST /Payment (in createPaymentRequest)
        // responseHeaderId should contain the WebTransHeaderId
        if (responseHeaderId && responseHeaderId !== '00000000-0000-0000-0000-000000000000') {
          confirmedHeaderId = responseHeaderId;

        } else {
          // If for some reason we don't have it, use the basket ID as fallback
          confirmedHeaderId = finalBasketId;

        }
      }

      // Confirm booking
      // Collect all applied discount codes from current basket (e.g., ADMIN100)


      const basketDiscountCodes = (venposBasket?.data?.discounts || [])
        .map(d => d?.code)
        .filter(Boolean);

      // Build memberships array for membership journeys
      // IMPORTANT: Each membership in the array represents one membership purchase
      // If quantity > 1, we need separate membership entries with their own memberDetails
      let membershipsArray = [];
      if (isMembershipJourney && membershipDetails && Array.isArray(membershipDetails)) {
        membershipsArray = membershipDetails.map((membership, index) => {
          const allMemberDetails = membership.memberDetails || [];

          const buildMemberDetailPayload = (memberDetail = {}, useCustomerFallback = false, isPrimaryContact = false, memberIndex = 0) => {
            const hasValue = (val) => val !== null && val !== undefined && val !== '';
            const billingAddress = customerDetails?.address || {};
            const useBillingAddress = memberDetail.useBillingAddress !== false; // default to true
            console.log('💳 [MEMBERSHIP] buildMemberDetailPayload - useBillingAddress:', useBillingAddress);
            console.log('💳 [MEMBERSHIP] billingAddress:', billingAddress);
            console.log('💳 [MEMBERSHIP] memberDetail.useBillingAddress:', memberDetail.useBillingAddress);
            console.log('💳 [MEMBERSHIP] isPrimaryContact:', isPrimaryContact);
            console.log('💳 [MEMBERSHIP] memberIndex:', memberIndex);
            console.log('💳 [MEMBERSHIP] registeredCustomerId:', registeredCustomerId);
            
            // Normalize country codes: UK -> GB, United Kingdom -> GB
            const normalizeCountry = (country) => {
              if (!country) return 'GB';
              if (country === 'UK' || country === 'United Kingdom') return 'GB';
              return country;
            };
            
            const resolvedAddress = useBillingAddress ? {
              houseNumber: billingAddress.houseNameNumber || billingAddress.houseNumber || '',
              line1: billingAddress.line1 || billingAddress.addressFirstLine || '',
              line2: billingAddress.line2 || billingAddress.addressSecondLine || '',
              city: billingAddress.city || billingAddress.cityTown || '',
              postcode: billingAddress.postcode || billingAddress.postCode || '',
              country: normalizeCountry(billingAddress.country)
            } : {
              houseNumber: memberDetail.houseNameNumber || memberDetail.houseNumber || '',
              line1: memberDetail.addressFirstLine || memberDetail.address1 || '',
              line2: memberDetail.addressSecondLine || memberDetail.address2 || '',
              city: memberDetail.cityTown || memberDetail.city || '',
              postcode: memberDetail.postCode || memberDetail.postcode || memberDetail.postalCode || '',
              country: normalizeCountry(memberDetail.selectedCountry || memberDetail.country || billingAddress.country)
            };
            console.log('💳 [MEMBERSHIP] resolvedAddress:', resolvedAddress);
            
            // Marketing consent: explicit opt-in only; default opt-out when not set
            const marketingOptIn = memberDetail.contactable !== undefined
              ? Boolean(memberDetail.contactable)
              : customerDetails?.contactable === true;

            // Get required field flags from membership config
            const telephoneRequired = !!membership.telephoneRequired;
            const mobilePhoneRequired = !!membership.mobilePhoneRequired;
            const emailRequired = !!membership.emailRequired;

            // Get original memberDetail from GetMembershipDetails response to preserve fields like optInText
            const originalMemberDetail = membership.memberDetails?.[0] || {};

            // Use member form name directly; only fall back to customer name when explicitly requested
            const memberFirstName = useCustomerFallback ? (customerDetails?.firstName || '') : (memberDetail.firstName || '');
            const memberLastName = useCustomerFallback ? (customerDetails?.lastName || '') : (memberDetail.lastName || '');
            const billingFirstName = customerDetails?.firstName || '';
            const billingLastName = customerDetails?.lastName || '';
            const namesMatch = memberFirstName.toLowerCase() === billingFirstName.toLowerCase() &&
                               memberLastName.toLowerCase() === billingLastName.toLowerCase();
            console.log('💳 [MEMBERSHIP] namesMatch:', namesMatch, '(member:', memberFirstName, memberLastName, 'vs billing:', billingFirstName, billingLastName, ')');
            
            // CRITICAL: For family memberships, only the primary contact (first member) should be linked to registered customer
            // All other members should have webCustomerId: null to create new member profiles
            let resolvedWebCustomerId = null;
            if (isPrimaryContact) {
              // Primary contact: link to registered customer if names match
              resolvedWebCustomerId = namesMatch
                ? (hasValue(memberDetail.webCustomerId) ? memberDetail.webCustomerId : (registeredCustomerId || null))
                : (hasValue(memberDetail.webCustomerId) ? memberDetail.webCustomerId : null);
            } else {
              // Other members: always null to create new member profiles (unless explicitly set in memberDetail)
              resolvedWebCustomerId = hasValue(memberDetail.webCustomerId) ? memberDetail.webCustomerId : null;
            }
            console.log('💳 [MEMBERSHIP] resolvedWebCustomerId:', resolvedWebCustomerId, '(isPrimaryContact:', isPrimaryContact, ')');

            // For primary contact (first member in family membership), ensure contact details are populated
            const resolvedMobile = isPrimaryContact 
              ? (memberDetail.mobile || customerDetails?.phone || customerDetails?.mobile || '')
              : (memberDetail.mobile || '');
            const resolvedEmail = isPrimaryContact
              ? (memberDetail.email || customerDetails?.email || '')
              : (memberDetail.email || '');
            const resolvedTelephone = isPrimaryContact
              ? (memberDetail.telephone || customerDetails?.telephone || '')
              : (memberDetail.telephone || '');

            return {
              webCustomerId: resolvedWebCustomerId,
              firstName: memberFirstName,
              lastName: memberLastName,
              selectedTitle: memberDetail.selectedTitle || customerDetails?.selectedTitle || 1,
              mobile: resolvedMobile,
              telephone: resolvedTelephone,
              email: resolvedEmail,
              selectedCountry: normalizeCountry(resolvedAddress.country),
              postCode: resolvedAddress.postcode || resolvedAddress.postCode || '',
              dob: memberDetail.dob || null,
              cityTown: resolvedAddress.city || '',
              addressFirstLine: resolvedAddress.line1 || '',
              addressSecondLine: resolvedAddress.line2 || '',
              houseNameNumber: resolvedAddress.houseNumber || '',
              password: memberDetail.password || '',
              name: memberDetail.name || '',
              contactable: marketingOptIn,
              contactableByPost: memberDetail.contactableByPost || false,
              contactableBySocialMedia: memberDetail.contactableBySocialMedia || false,
              contactableBySMS: memberDetail.contactableBySMS || false,
              contactableByEmail: memberDetail.contactableByEmail !== undefined ? memberDetail.contactableByEmail : marketingOptIn,
              contactableByPhone: memberDetail.contactableByPhone || false,
              contactableByThirdParty: memberDetail.contactableByThirdParty || false,
              isGuest: originalMemberDetail.isGuest !== undefined ? originalMemberDetail.isGuest : (memberDetail.isGuest !== undefined ? memberDetail.isGuest : false),
              // CRITICAL: customerCount must be memberIndex + 1 (1, 2, 3, 4, 5 for a 5-member family)
              customerCount: memberIndex + 1,
              isCustomerDetail: originalMemberDetail.isCustomerDetail !== undefined ? originalMemberDetail.isCustomerDetail : (memberDetail.isCustomerDetail !== undefined ? memberDetail.isCustomerDetail : false),
              isFirstAddress: originalMemberDetail.isFirstAddress !== undefined ? originalMemberDetail.isFirstAddress : (memberDetail.isFirstAddress !== undefined ? memberDetail.isFirstAddress : false),
              // Always default to true for these fields (matching official example)
              enablePostCodeLookup: true,
              useBillingAddress: useBillingAddress,
              useShippingAddress: false,
              shipToFirstAddress: originalMemberDetail.shipToFirstAddress !== undefined ? originalMemberDetail.shipToFirstAddress : (memberDetail.shipToFirstAddress !== undefined ? memberDetail.shipToFirstAddress : false),
              adoptionDate: originalMemberDetail.adoptionDate !== undefined ? originalMemberDetail.adoptionDate : (memberDetail.adoptionDate !== undefined ? memberDetail.adoptionDate : null),
              giftedTo: memberDetail.giftedTo || originalMemberDetail.giftedTo || '',
              giftedToFullName: memberDetail.giftedToFullName || originalMemberDetail.giftedToFullName || '',
              giftedFrom: memberDetail.giftedFrom || originalMemberDetail.giftedFrom || '',
              giftedMessage: memberDetail.giftedMessage || originalMemberDetail.giftedMessage || '',
              shippingFirstName: memberDetail.shippingFirstName || originalMemberDetail.shippingFirstName || '',
              shippingLastName: memberDetail.shippingLastName || originalMemberDetail.shippingLastName || '',
              shippingSelectedTitle: originalMemberDetail.shippingSelectedTitle !== undefined ? originalMemberDetail.shippingSelectedTitle : (memberDetail.shippingSelectedTitle !== undefined ? memberDetail.shippingSelectedTitle : 0),
              shippingPostCode: memberDetail.shippingPostCode || originalMemberDetail.shippingPostCode || '',
              shippingHouseNameNumber: memberDetail.shippingHouseNameNumber || originalMemberDetail.shippingHouseNameNumber || '',
              shippingCityTown: memberDetail.shippingCityTown || originalMemberDetail.shippingCityTown || '',
              shippingAddressFirstLine: memberDetail.shippingAddressFirstLine || originalMemberDetail.shippingAddressFirstLine || '',
              shippingAddressSecondLine: memberDetail.shippingAddressSecondLine || originalMemberDetail.shippingAddressSecondLine || '',
              // CRITICAL: cardRequired should be true ONLY for primary contact (first member), false for others
              cardRequired: isPrimaryContact,
              removeMemberEnabled: originalMemberDetail.removeMemberEnabled !== undefined ? originalMemberDetail.removeMemberEnabled : (memberDetail.removeMemberEnabled !== undefined ? memberDetail.removeMemberEnabled : false),
              canLookupPostcode: originalMemberDetail.canLookupPostcode !== undefined ? originalMemberDetail.canLookupPostcode : true,
              showPostcodeResults: memberDetail.showPostcodeResults ?? false,
              showShippingPostcodeResults: memberDetail.showShippingPostcodeResults ?? false,
              postcodeResults: memberDetail.postcodeResults || [],
              shippingPostcodeResults: memberDetail.shippingPostcodeResults || [],
              dataProtectionLevel: marketingOptIn ? 'OptIn' : 'OptOut',
              contactableText: memberDetail.contactableText || originalMemberDetail.contactableText || '',
              contactableByPostText: memberDetail.contactableByPostText || originalMemberDetail.contactableByPostText || '',
              contactableByPhoneText: memberDetail.contactableByPhoneText || originalMemberDetail.contactableByPhoneText || '',
              contactableBySMSText: memberDetail.contactableBySMSText || originalMemberDetail.contactableBySMSText || '',
              contactableBySocialMediaText: memberDetail.contactableBySocialMediaText || originalMemberDetail.contactableBySocialMediaText || '',
              contactableByThirdPartyText: memberDetail.contactableByThirdPartyText || originalMemberDetail.contactableByThirdPartyText || '',
              contactableByEmailText: memberDetail.contactableByEmailText || originalMemberDetail.contactableByEmailText || '',
              optInText: originalMemberDetail.optInText || memberDetail.optInText || '',
              optOutText: memberDetail.optOutText || originalMemberDetail.optOutText || '',
              eligibilityUploadText: memberDetail.eligibilityUploadText || originalMemberDetail.eligibilityUploadText || '',
              dpOptIn: marketingOptIn,
              dpMulti: memberDetail.dpMulti || false,
              dpOptOut: !marketingOptIn,
              isContactable: marketingOptIn,
              telephoneRequired: telephoneRequired,
              mobilePhoneRequired: mobilePhoneRequired,
              emailRequired: emailRequired,
              showBillingAddress: true,
              usingCustomAddress: false,
              usingCustomShippingAddress: false
            };
          };

          // Build member details array, marking the first member as primary contact for family memberships
          let memberDetailsArray = allMemberDetails.map((memberDetail, memberIndex) => 
            buildMemberDetailPayload(memberDetail, false, memberIndex === 0, memberIndex)
          );

          if (memberDetailsArray.length === 0) {
            // If no member details, create one with customer fallback and mark as primary contact
            memberDetailsArray.push(buildMemberDetailPayload({}, true, true, 0));
          }

          const finalMemberCount = membership.memberCount || memberDetailsArray.length || 1;
          console.log('💳 [MEMBERSHIP] Building membership payload:', {
            membershipTypeId: membership.membershipTypeId,
            originalMemberCount: membership.memberCount,
            memberDetailsArrayLength: memberDetailsArray.length,
            finalMemberCount: finalMemberCount,
            memberDetailsNames: memberDetailsArray.map(m => `${m.firstName} ${m.lastName}`),
            primaryContactEmail: memberDetailsArray[0]?.email,
            primaryContactMobile: memberDetailsArray[0]?.mobile,
            cardRequiredFlags: memberDetailsArray.map(m => m.cardRequired),
            customerCounts: memberDetailsArray.map(m => m.customerCount)
          });
          
          return {
            ...membership,
            memberCount: finalMemberCount,
            memberDetails: memberDetailsArray,
            // Add missing fields from official example (these should come from GetMembershipDetails but ensure they exist)
            numberOfCardsSelected: membership.numberOfCardsSelected ?? 0,
            membershipTypeTitle: membership.membershipTypeTitle || membership.description || membership.alternativeDescription || '',
            cardQuantity: membership.cardQuantity ?? 0,
            showAddAnotherCard: membership.showAddAnotherCard ?? false,
            cardRequiredText: membership.cardRequiredText || 'Add another card (0/0 selected)',
            cardRequiredDisabledText: membership.cardRequiredDisabledText || 'Limit met. Please deselect another card (0/0 selected)',
            cardRequiredEnabled: membership.cardRequiredEnabled ?? false
          };
        });
      }

      // ✅ CRITICAL: For paid bookings, ensure payment was actually completed before confirming booking
      if (finalAmount > 0 && !activePaymentResult) {
        console.error('❌ [BOOKING] Cannot confirm paid booking without payment result');
        throw new Error('Payment not completed. Cannot confirm booking without successful payment.');
      }
      
      // ✅ ADDITIONAL VALIDATION: For paid bookings, ensure we have payment proof
      if (finalAmount > 0 && activePaymentResult) {
        const hasPaymentProof = activePaymentResult.authCode || 
                               activePaymentResult.transactionId || 
                               activePaymentResult.paymentToken ||
                               activePaymentResult.completed === true;
        if (!hasPaymentProof) {
          console.error('❌ [BOOKING] Payment result missing proof of completion:', activePaymentResult);
          throw new Error('Payment not completed successfully. Cannot confirm booking.');
        }
        console.log('✅ [BOOKING] Payment proof verified, proceeding with booking confirmation');
      }

      console.log('📝 [BOOKING] Calling venposService.confirmBooking');
      console.log('📝 [BOOKING] Parameters:', {
        finalBasketId,
        locationId,
        selectedDate: isMembershipJourney ? null : (selectedDate ? formatDateLocal(selectedDate) : null),
        selectedTimeSlot: isMembershipJourney ? null : selectedTimeSlot,
        totalAmount: finalAmount,
        paymentToken: activePaymentResult?.paymentToken,
        transactionId: confirmedHeaderId || activePaymentResult?.transactionId || activePaymentResult?.authCode,
        isMembershipJourney,
        hasPaymentProof: finalAmount > 0 ? !!activePaymentResult : true
      });

      const bookingResponse = await venposService.confirmBooking(
        finalBasketId,
        locationId,
        {
          selectedDate: isMembershipJourney ? null : (selectedDate ? formatDateLocal(selectedDate) : null),
          selectedTimeSlot: isMembershipJourney ? null : selectedTimeSlot,
          selectedTickets: selectedTickets,
          pageId: pageId,
          totalAmount: finalAmount,
          // ✅ CRITICAL: Pass venposBasket (same as paid orders) - it contains all event metadata
          venposBasket: venposBasket,
          // ✅ CRITICAL FIX: Use the actual payment token from Payment Sense result
          // For paid bookings: use the paymentToken from the payment result (NOT 'FREE_BOOKING')
          // For free bookings: totalAmount will be 0, so this still works
          paymentToken: activePaymentResult?.paymentToken || (finalAmount > 0 ? null : 'FREE_BOOKING'),
          transactionId: confirmedHeaderId || activePaymentResult?.transactionId || activePaymentResult?.authCode || (finalAmount > 0 ? null : 'FREE_BOOKING'),
          customerDetails: {
            ...customerDetails,
            webCustomerId: registeredCustomerId // Include registered customer ID
          },
          paymentDetails: activePaymentResult || { method: 'free_booking' },
          // Merge basket discounts (e.g., ADMIN100) with any saver codes
          discounts: [
            ...basketDiscountCodes,
            ...(appliedSaverCode ? [appliedSaverCode] : []),
            ...(getFamilySaverCode() ? [getFamilySaverCode()] : [])
          ],
          familySaverCode: getFamilySaverCode(),
          confirmedHeaderId: confirmedHeaderId, // Pass the confirmed header ID if available
          webTransHeaderId: confirmedHeaderId || finalBasketId, // Use confirmed ID or fallback to basketId
          isPaidBooking: finalAmount > 0, // ✅ NEW: Explicitly flag as paid booking
          existingBookingReference: existingBookingReference, // ✅ Pass booking reference from completePayment if available
          formattedEventQuestions: formattedEventQuestions, // ✅ Pass formatted event questions for CreateFreeOrder
          memberships: membershipsArray, // ✅ Pass memberships array for membership journeys
          isMembershipJourney: isMembershipJourney // ✅ Flag to indicate membership journey
        }
      );

      console.log('📝 [BOOKING] confirmBooking response received');
      console.log('📝 [BOOKING] bookingResponse.data?.bookingReference:', bookingResponse.data?.bookingReference);
      console.log('📝 [BOOKING] bookingResponse.bookingReference:', bookingResponse.bookingReference);

      // Check if booking response indicates an error
      if (bookingResponse.isError) {
        throw new Error(bookingResponse.error || 'Booking confirmation failed');
      }

      const newBookingReference = bookingResponse.data?.bookingReference || 
                                  bookingResponse.bookingReference;

      // ❌ CRITICAL: Check for FALLBACK booking references - these indicate a failed booking
      if (!newBookingReference || newBookingReference.startsWith('FALLBACK') || newBookingReference.startsWith('BC') && newBookingReference.length < 10) {
        console.error('🚨 [BOOKING] Invalid or fallback booking reference detected:', newBookingReference);
        throw new Error('Booking confirmation failed. The booking could not be completed. Please try again or contact support.');
      }

      console.log('📝 [BOOKING] New booking reference:', newBookingReference);
      console.log('📝 [BOOKING] Setting booking reference in state');
      setBookingReference(newBookingReference);
      console.log('📝 [BOOKING] Booking reference set successfully');

      // ── Summer Solstice capacity tracker ────────────────────────────────────
      // After a confirmed overnight booking, record it in our WordPress table so
      // subsequent orders can't exceed the 9-guest cap per property.
      // Fire-and-forget: we never block the confirmation flow on this call.
      if (pageId === '0efbadd8-388c-4ad0-ba36-c88282ac634d') {
        const overnightTicket = selectedTickets.find(
          t => t.quantity > 0 && t.price >= 150
        );
        if (overnightTicket) {
          // Vale = cheaper overnight (£195), Lodge = more expensive (£295)
          const property = overnightTicket.price < 250 ? 'vale' : 'lodge';
          fetch('https://www.belvoircastle.com/wp/wp-json/custom/v1/solstice-capacity', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              property,
              booking_ref: newBookingReference,
              quantity:    overnightTicket.quantity,
              guest_email: customerDetails?.email || '',
            }),
          })
            .then(r => r.json())
            .then(d => console.log('✅ [SOLSTICE] Capacity recorded:', d))
            .catch(e => console.warn('⚠️ [SOLSTICE] Capacity record failed (non-blocking):', e));
        }
      }
      // ── End Solstice capacity tracker ───────────────────────────────────────
      
      // Mark the original booking reference as used if this is an upgrade journey
      if (upgradeFromBooking && newBookingReference) {
        try {
          // Helper functions to determine membership and product types
          const isChildMembership = (ticket) => {
            const name = (ticket.name || '').toLowerCase();
            const description = (ticket.description || '').toLowerCase();
            
            // Exclude free child tickets (under 3, under 2, infant) - these don't count as child memberships
            if (name.includes('under 3') || name.includes('under 2') || name.includes('infant') ||
                description.includes('under 3') || description.includes('under 2') || description.includes('infant')) {
              return false;
            }
            
            return name.includes('child') || name.includes('children') ||
                   description.includes('child') || description.includes('junior');
          };
          
          // Helper function to check if ticket is a free "under 3" ticket (should not count for any discounts)
          const isFreeUnder3Ticket = (ticket) => {
            const name = (ticket.name || '').toLowerCase();
            const description = (ticket.description || '').toLowerCase();
            return name.includes('under 3') || name.includes('under 2') || name.includes('infant') ||
                   description.includes('under 3') || description.includes('under 2') || description.includes('infant');
          };
          
          // Calculate the actual upgrade credit amount that was applied
          let calculatedUpgradeAmount = 0;
          
          // Get available items from sortedUpgradeItems (now an object with child/adult arrays)
          const availableChildItems = (sortedUpgradeItems && sortedUpgradeItems.child) ? sortedUpgradeItems.child : [];
          const availableAdultItems = (sortedUpgradeItems && sortedUpgradeItems.adult) ? sortedUpgradeItems.adult : [];
          
          // Count memberships by type
          const childMemberships = selectedTickets.filter(ticket => 
            ticket.webSaleItemTypeID === 4 && isChildMembership(ticket)
          );
          const adultMemberships = selectedTickets.filter(ticket => 
            ticket.webSaleItemTypeID === 4 && !isChildMembership(ticket) && !isFreeUnder3Ticket(ticket)
          );
          
          const childMembershipCount = childMemberships.reduce((total, ticket) => total + (ticket.quantity || 0), 0);
          const adultMembershipCount = adultMemberships.reduce((total, ticket) => total + (ticket.quantity || 0), 0);
          
          // Calculate credit from items that were actually used
          const childItemsToUse = availableChildItems.slice(0, childMembershipCount);
          const adultItemsToUse = availableAdultItems.slice(0, adultMembershipCount);
          
          calculatedUpgradeAmount = [
            ...childItemsToUse,
            ...adultItemsToUse
          ].reduce((total, item) => total + (parseFloat(item.value) || 0), 0);
          
          // Build the correct API URL based on environment
          const hostname = window.location.hostname;
          let trackerBaseUrl;
          if (hostname === 'localhost' || hostname === '127.0.0.1') {
            trackerBaseUrl = 'http://localhost:8888/Website/front-end/public/api';
          } else if (hostname.includes('staging.belvoircastle.com')) {
            trackerBaseUrl = 'https://staging.belvoircastle.com/api';
          } else {
            trackerBaseUrl = 'https://www.belvoircastle.com/api';
          }
          
          // Mark only the items that were actually used (not all items from the booking)
          // Each item instance is tracked separately, even if they have the same product code and value
          const itemsToMarkAsUsed = [];
          
          // Add child items that were used
          childItemsToUse.forEach((item, index) => {
            itemsToMarkAsUsed.push({
              productCode: item.productCode,
              productDescription: item.productDescription || item.name || '',
              value: item.value,
              webTransItemId: item.webTransItemId || item.webTransItemID || `child_index_${index}`,
              itemIndex: index
            });
          });
          
          // Add adult items that were used
          adultItemsToUse.forEach((item, index) => {
            itemsToMarkAsUsed.push({
              productCode: item.productCode,
              productDescription: item.productDescription || item.name || '',
              value: item.value,
              webTransItemId: item.webTransItemId || item.webTransItemID || `adult_index_${index}`,
              itemIndex: index
            });
          });
          
          console.log('📝 [UPGRADE] Items to mark as used:', {
            childItemsCount: childItemsToUse.length,
            adultItemsCount: adultItemsToUse.length,
            totalItems: itemsToMarkAsUsed.length,
            items: itemsToMarkAsUsed
          });
          
          if (itemsToMarkAsUsed.length > 0) {
            const trackerUrl = `${trackerBaseUrl}/upgrade-booking-tracker.php`;
            
            console.log('📝 [UPGRADE] Calling tracker API to mark items as used:', {
              url: trackerUrl,
              bookingRef: upgradeFromBooking,
              newBookingRef: newBookingReference,
              itemsCount: itemsToMarkAsUsed.length,
              items: itemsToMarkAsUsed
            });
            
            try {
              // Use JSON instead of form-encoded for better data handling
              const trackerResponse = await fetch(trackerUrl, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  bookingRef: upgradeFromBooking,
                  newBookingRef: newBookingReference,
                  upgradeAmount: calculatedUpgradeAmount,
                  items: itemsToMarkAsUsed
                })
              });
              
              console.log('📝 [UPGRADE] Tracker API response status:', trackerResponse.status);
              
              if (trackerResponse.ok) {
                const contentType = trackerResponse.headers.get('content-type');
                if (contentType && contentType.includes('application/json')) {
                  const trackerResult = await trackerResponse.json();
                  console.log('📝 [UPGRADE] Tracker API response:', trackerResult);
                  if (trackerResult.success) {
                    console.log('✅ [UPGRADE] Items marked as used:', {
                      original: upgradeFromBooking,
                      new: newBookingReference,
                      itemsMarked: trackerResult.total_inserted,
                      itemsSkipped: trackerResult.total_skipped
                    });
                  } else {
                    console.error('❌ [UPGRADE] Tracker API returned error:', trackerResult.error || trackerResult);
                  }
                } else {
                  const responseText = await trackerResponse.text();
                  console.warn('⚠️ [UPGRADE] Tracker API returned non-JSON response:', responseText);
                }
              } else {
                const errorText = await trackerResponse.text();
                let parsedError = errorText;
                try {
                  const errorJson = JSON.parse(errorText);
                  parsedError = errorJson.error || errorText;
                } catch (e) {
                  // Not JSON, use as-is
                }
                
                // Check if it's a database connection error
                const isDbError = parsedError.includes('Database connection') || 
                                 parsedError.includes('Access denied') ||
                                 parsedError.includes('using password');
                
                if (isDbError) {
                  console.error('❌ [UPGRADE] Database connection error - tracker unavailable:', {
                    status: trackerResponse.status,
                    error: parsedError,
                    note: 'Booking completed successfully, but upgrade tracking failed. Please check database configuration.'
                  });
                } else {
                  console.error('❌ [UPGRADE] Failed to mark items as used:', {
                    status: trackerResponse.status,
                    statusText: trackerResponse.statusText,
                    error: parsedError
                  });
                }
              }
            } catch (trackerError) {
              // Log error but don't fail the booking if tracker is unavailable
              console.error('❌ [UPGRADE] Could not mark items as used (network/connection error):', trackerError);
              console.warn('⚠️ [UPGRADE] Booking completed successfully, but upgrade tracking may have failed. This is non-critical.');
            }
          } else {
            console.warn('⚠️ [UPGRADE] No items to mark as used - itemsToMarkAsUsed is empty');
          }
        } catch (error) {
          console.error('❌ [UPGRADE] Error marking booking reference as used:', error);
          // Don't fail the booking if tracking fails - just log it
        }
      }

      dispatch({ 
        type: BOOKING_ACTIONS.SET_BOOKING_REFERENCE, 
        payload: newBookingReference 
      });

      // Store complete pricing information including discounts for confirmation screen
      const basketDiscounts = venposBasket?.data?.discounts || [];
      const basketOriginalPrice = venposBasket?.data?.totalPrice;
      const basketFinalPrice = venposBasket?.data?.finalPrice;
      
      dispatch({ 
        type: BOOKING_ACTIONS.SET_BOOKING_SUMMARY, 
        payload: {
          totalAmount: finalAmount,
          bookingReference: newBookingReference,
          confirmationEmail: customerDetails.email,
          // Add discount/pricing info for confirmation screen
          originalPrice: basketOriginalPrice,
          finalPrice: basketFinalPrice,
          discounts: basketDiscounts,
          totalSavings: basketDiscounts.reduce((sum, d) => sum + (d.discountedAmount || 0), 0)
        }
      });


      // 📦 Store full booking data for print tickets from email

      try {
        // CRITICAL FIX: Extract individual tickets from venposBasket (with quantity 1 each)
        // instead of using grouped selectedTickets. Venpos returns the correct expanded tickets.
        const basketGroups = venposBasket?.data?.groups || [];
        const basketItems = basketGroups.flatMap(g => g.items || []);
        
        console.log('🎫 [STORE-BOOKING] Basket Items:', basketItems.map(item => ({
          name: item.name,
          unitPrice: item.unitPrice,
          price: item.price,
          totalPrice: item.totalPrice,
          discountedAmount: item.discountedAmount,
          quantity: item.quantity
        })));
        
        // CRITICAL FIX: Group basket items for display in emails/printed tickets
        // Group items by webSaleItemId and name for better readability
        const groupedMap = new Map();
        
        basketItems.forEach(item => {
          const key = `${item.webSaleItemId}-${item.name}`;
          const itemQuantity = item.quantity || 1;
          const unitPrice = item.unitPrice || (item.totalPrice / itemQuantity) || item.price || 0;
          
          if (groupedMap.has(key)) {
            // Add to existing group
            const existing = groupedMap.get(key);
            existing.quantity += itemQuantity;
            existing.totalPrice += item.totalPrice || (unitPrice * itemQuantity);
          } else {
            // Create new group
            groupedMap.set(key, {
              webSaleItemId: item.webSaleItemId,
              webSaleItemTypeID: item.webSaleItemTypeID || item.typeId,
              name: item.name || item.description,
              quantity: itemQuantity,
              price: unitPrice,
              totalPrice: item.totalPrice || (unitPrice * itemQuantity),
              eventId: item.eventId,
              eventCapacityUsageId: item.eventCapacityUsageId
            });
          }
        });
        
        const expandedTicketsFromBasket = Array.from(groupedMap.values());
        
        console.log('🎫 [STORE-BOOKING] Grouped Tickets:', expandedTicketsFromBasket);

        const fullBookingData = {
          bookingReference: newBookingReference,
          selectedTickets: expandedTicketsFromBasket.length > 0 ? expandedTicketsFromBasket : selectedTickets,
          selectedDate: formatDateLocal(selectedDate),
          selectedTimeSlot: selectedTimeSlot,
          customerDetails: customerDetails,
          totalAmount: finalAmount,
          venposBasket: venposBasket,
          bookingSummary: { ...(bookingSummary || {}), ...(pageId ? { pageId } : {}) },
          isFamilySaver: !!getFamilySaverCode()
        };
        
        await fetch(`${config.apiEndpoint}/custom/v1/store-booking-data`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(fullBookingData)
        });

      } catch (storeError) {

      }
      
      // 🍵 Store afternoon tea booking data for booking management
      try {
        // For direct AT bookings, expand each ticket into one-per-person rows so
        // extractAfternoonTeaItems can attach the user-selected modifier and allergen notes
        // collected in CustomerDetails (stored via SET_MODIFIER_SELECTION / SET_DIRECT_AT_ALLERGENS).
        let ticketsForATExtraction = selectedTickets;
        if (isDirectAfternoonTeaBooking) {
          const expandedTickets = [];
          (selectedTickets || [])
            .filter(t => (Number(t.quantity) || 0) > 0)
            .forEach(ticket => {
              const qty = Number(ticket.quantity) || 1;
              for (let i = 0; i < qty; i++) {
                const key = `${ticket.webSaleItemId}-${i}`;
                // Modifier selected via the tiles in CustomerDetails (same SET_MODIFIER_SELECTION used by upsell modal)
                const contextModifier = modifierSelections?.[key] || null;
                // Allergen notes object keyed by "webSaleItemId-personIndex"
                const allergens = (directAfternoonTeaAllergens || {})[key] || null;
                expandedTickets.push({
                  ...ticket,
                  quantity: 1,
                  allergenNotes: allergens,
                  ...(contextModifier ? {
                    modifierSelection: {
                      modifierPrompt: contextModifier.modifierPrompt || '',
                      modifierName: contextModifier.modifierName || '',
                      modifierPrice: contextModifier.modifierPrice || 0,
                      modifierId: contextModifier.modifierId,
                      modifierTextId: contextModifier.modifierTextId,
                    },
                  } : {}),
                });
              }
            });
          ticketsForATExtraction = expandedTickets;
          console.log('🍵 [AFTERNOON-TEA] Direct booking — expanded tickets for extraction:', ticketsForATExtraction.length);
        }

        const afternoonTeaData = extractAfternoonTeaItems(
          venposBasket,
          selectedDate,
          customerDetails,
          finalAmount,
          newBookingReference,
          ticketsForATExtraction,
          isDirectAfternoonTeaBooking
        );
        
        if (afternoonTeaData && afternoonTeaData.products && afternoonTeaData.products.length > 0) {
          console.log('🍵 [AFTERNOON-TEA] Storing afternoon tea booking data:', afternoonTeaData);
          
          await fetch(`${config.apiEndpoint}/custom/v1/store-afternoon-tea-booking`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(afternoonTeaData)
          });
          
          console.log('✅ [AFTERNOON-TEA] Afternoon tea booking data stored successfully');
        } else {
          console.log('ℹ️ [AFTERNOON-TEA] No afternoon tea items found in booking');
        }
      } catch (teaError) {
        console.error('❌ [AFTERNOON-TEA] Error storing afternoon tea booking:', teaError);
        // Don't block booking completion if afternoon tea storage fails
      }

      // 🍷 VINEYARD TOUR — save to dedicated vineyard tour tables (not afternoon tea)
      if (pageId === VINEYARD_TOUR_PAGE_ID) {
        try {
          // Expand tickets into one product/guest row per person with their dietary choice
          const products = [];
          let guestIdx = 0;
          (selectedTickets || [])
            .filter(t => (Number(t.quantity) || 0) > 0)
            .forEach(t => {
              const qty = Number(t.quantity) || 1;
              for (let i = 0; i < qty; i++) {
                const dietary = vineyardDietaryRequirements?.[guestIdx] || { choice: 'None', notes: '' };
                products.push({
                  webSaleItemId: t.webSaleItemId || '',
                  name: t.name || t.title || 'Vineyard Tour Ticket',
                  quantity: 1,
                  price: t.price || t.unitPrice || 0,
                  unitPrice: t.price || t.unitPrice || 0,
                  totalPrice: t.price || t.unitPrice || 0,
                  modifierSelection: {
                    modifierPrompt: 'Dietary Requirements',
                    modifierName: dietary.choice || 'None',
                  },
                  allergenNotes: dietary.choice === 'Other' ? (dietary.notes || '') : '',
                  dietaryChoice: dietary.choice || 'None',
                  dietaryNotes: dietary.choice === 'Other' ? (dietary.notes || '') : '',
                });
                guestIdx += 1;
              }
            });

          while (guestIdx < (vineyardDietaryRequirements?.length || 0)) {
            const dietary = vineyardDietaryRequirements[guestIdx] || { choice: 'None', notes: '' };
            const template = products[0] || {
              webSaleItemId: '',
              name: 'Vineyard Tour Ticket',
              price: 0,
              unitPrice: 0,
              totalPrice: 0,
            };
            products.push({
              ...template,
              quantity: 1,
              modifierSelection: {
                modifierPrompt: 'Dietary Requirements',
                modifierName: dietary.choice || 'None',
              },
              allergenNotes: dietary.choice === 'Other' ? (dietary.notes || '') : '',
              dietaryChoice: dietary.choice || 'None',
              dietaryNotes: dietary.choice === 'Other' ? (dietary.notes || '') : '',
            });
            guestIdx += 1;
          }

          const slotTime = (selectedTimeSlot?.time || '12:00').toString().substring(0, 5);
          const vineyardPayload = {
            bookingReference: newBookingReference,
            customerDetails: {
              firstName: customerDetails.firstName,
              lastName: customerDetails.lastName,
              email: customerDetails.email,
              phone: customerDetails.phone || '',
            },
            visitDate: selectedDate ? formatDateLocal(selectedDate) : '',
            visitTime: slotTime,
            totalAmount: finalAmount.toFixed(2),
            dietaryRequirements: vineyardDietaryRequirements || [],
            products,
          };

          console.log('🍷 [VINEYARD-TOUR] Saving booking payload:', vineyardPayload);

          const vineyardRes = await fetch(`${config.apiEndpoint}/custom/v1/store-vineyard-tour-booking`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(vineyardPayload),
          });
          const vineyardText = await vineyardRes.text();
          if (!vineyardRes.ok) {
            console.error('❌ [VINEYARD-TOUR] Store failed', vineyardRes.status, vineyardText);
          } else {
            console.log('✅ [VINEYARD-TOUR] Booking stored:', vineyardText);
          }
        } catch (vineyardError) {
          console.error('❌ [VINEYARD-TOUR] Error storing vineyard tour booking:', vineyardError);
        }
      }

      // 🐕 DOG PARK — save booking + extra dogs to our own tables
      if (isDogParkBooking) {
        try {
          const extraDogs = (selectedTickets || [])
            .filter((ticket) => (ticket.name || '').toLowerCase().includes('additional dog'))
            .reduce((sum, ticket) => sum + (Number(ticket.quantity) || 0), 0);
          const baseDogs = 1;
          const slotTime = (selectedTimeSlot?.time || '').toString().substring(0, 5);
          const dogParkPayload = {
            bookingReference: newBookingReference,
            customerDetails: {
              firstName: customerDetails.firstName,
              lastName: customerDetails.lastName,
              email: customerDetails.email,
              phone: customerDetails.phone || '',
            },
            visitDate: selectedDate ? formatDateLocal(selectedDate) : '',
            visitTime: slotTime,
            totalAmount: Number(finalAmount || 0).toFixed(2),
            baseDogs,
            extraDogs,
            totalDogs: baseDogs + extraDogs,
            carRegistration: (customerDetails.carRegistration || '').toUpperCase().replace(/\s/g, ''),
          };

          const dogParkRes = await fetch(`${config.apiEndpoint}/custom/v1/store-dog-park-booking`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(dogParkPayload),
          });
          const dogParkText = await dogParkRes.text();
          if (!dogParkRes.ok) {
            console.error('❌ [DOG-PARK] Store failed', dogParkRes.status, dogParkText);
          } else {
            console.log('✅ [DOG-PARK] Booking stored:', dogParkText);
          }
        } catch (dogParkError) {
          console.error('❌ [DOG-PARK] Error storing dog park booking:', dogParkError);
        }
      }

      // 🎅 Santa's Grotto — save child name + age for staff
      try {
        const grottoTickets = (selectedTickets || []).filter(t => {
          const n = `${t.name || ''} ${t.parentUpsellName || ''}`.toLowerCase();
          return (Number(t.quantity) || 0) > 0 && (
            n.includes('grotto') ||
            GROTTO_NO_UPSELL_PAGE_IDS.includes(pageId)
          );
        });
        const grottoChildren = (santasGrottoChildren || []).filter(c => String(c?.name || '').trim());

        if (grottoTickets.length > 0 && grottoChildren.length > 0) {
          const locationFromTicket = (ticket) => {
            const n = `${ticket?.name || ''} ${ticket?.parentUpsellName || ''}`.toLowerCase();
            if (n.includes('village') || n.includes('retail')) return 'Retail Village';
            if (n.includes('castle') || pageId === CASTLE_GROTTO_ADD_LATER_PAGE_ID) return 'Castle';
            if (GROTTO_STANDALONE_PAGE_IDS.includes(pageId)) return 'Retail Village';
            return 'Castle';
          };

          let visitTime = selectedTimeSlot?.time ? String(selectedTimeSlot.time).substring(0, 5) : '';
          const grottoWithTime = grottoTickets.find(t => t.selectedEventTime?.startDateTime || (t.selectedDate && String(t.selectedDate).includes('T')));
          if (grottoWithTime) {
            const dateTimeStr = grottoWithTime.selectedEventTime?.startDateTime || grottoWithTime.selectedDate;
            const d = new Date(dateTimeStr);
            if (!Number.isNaN(d.getTime())) {
              visitTime = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
            }
          }

          const primaryLocation = grottoChildren[0]?.location || locationFromTicket(grottoTickets[0]);
          const grottoCustomer = {
            firstName: customerDetails.firstName,
            lastName: customerDetails.lastName,
            email: customerDetails.email,
            phone: customerDetails.phone || '',
          };
          const grottoPayload = {
            booking_reference: newBookingReference,
            bookingReference: newBookingReference,
            customer: grottoCustomer,
            customerDetails: grottoCustomer,
            visitDate: selectedDate ? formatDateLocal(selectedDate) : '',
            visitTime,
            totalAmount: finalAmount.toFixed(2),
            location: primaryLocation,
            children: grottoChildren.map((child, idx) => ({
              name: child.name,
              age: child.age,
              ticketName: child.ticketName || grottoTickets[0]?.name || "Santa's Grotto",
              location: child.location || primaryLocation,
              unitPrice: grottoTickets[0]?.price || grottoTickets[0]?.unitPrice || 0,
              childNumber: idx + 1,
            })),
          };

          console.log('🎅 [SANTAS-GROTTO] Saving booking payload:', grottoPayload);

          const grottoRes = await fetch(`${config.apiEndpoint}/custom/v1/store-santas-grotto-booking`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(grottoPayload),
          });
          const grottoText = await grottoRes.text();
          if (!grottoRes.ok) {
            console.error('❌ [SANTAS-GROTTO] Store failed', grottoRes.status, grottoText);
          } else {
            console.log('✅ [SANTAS-GROTTO] Booking stored:', grottoText);
          }
        }
      } catch (grottoError) {
        console.error('❌ [SANTAS-GROTTO] Error storing Santa\'s Grotto booking:', grottoError);
      }

      // 🎅 Search for Santa — child name + age for Child / Under 3, plus adult ticket counts
      try {
        const isSearchForSantaTicket = (t) => {
          const n = `${t.name || ''} ${t.parentUpsellName || ''}`.toLowerCase();
          return n.includes('search for santa');
        };
        const isSearchForSantaChildTicket = (t) => {
          const n = `${t.name || ''} ${t.parentUpsellName || ''}`.toLowerCase();
          if (!n.includes('search for santa')) return false;
          return n.includes('under 3') || n.includes('under3') || n.includes('under-3') || n.includes('child');
        };

        const searchTickets = (selectedTickets || []).filter(
          t => (Number(t.quantity) || 0) > 0 && isSearchForSantaTicket(t)
        );

        if (searchTickets.length > 0) {
          const adultTickets = searchTickets.filter(t => !isSearchForSantaChildTicket(t));
          const adults = adultTickets.map(t => ({
            ticketName: t.name || 'Search for Santa (Adult)',
            quantity: Number(t.quantity) || 1,
            unitPrice: t.price || t.unitPrice || 0,
          }));

          const children = (searchForSantaChildren || [])
            .filter(c => String(c?.name || '').trim())
            .map((child, idx) => ({
              name: child.name,
              age: child.age,
              ticketName: child.ticketName || 'Search for Santa (Child)',
              ticketType: child.ticketType || 'child',
              childNumber: idx + 1,
            }));

          let visitTime = selectedTimeSlot?.time ? String(selectedTimeSlot.time).substring(0, 5) : '';
          const timed = searchTickets.find(t => t.selectedEventTime?.startDateTime || (t.selectedDate && String(t.selectedDate).includes('T')));
          if (timed) {
            const dateTimeStr = timed.selectedEventTime?.startDateTime || timed.selectedDate;
            const d = new Date(dateTimeStr);
            if (!Number.isNaN(d.getTime())) {
              visitTime = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
            }
          }

          const searchPayload = {
            booking_reference: newBookingReference,
            bookingReference: newBookingReference,
            customer: {
              firstName: customerDetails.firstName,
              lastName: customerDetails.lastName,
              email: customerDetails.email,
              phone: customerDetails.phone || '',
            },
            customerDetails: {
              firstName: customerDetails.firstName,
              lastName: customerDetails.lastName,
              email: customerDetails.email,
              phone: customerDetails.phone || '',
            },
            visitDate: selectedDate ? formatDateLocal(selectedDate) : '',
            visitTime,
            totalAmount: finalAmount.toFixed(2),
            children,
            adults,
          };

          console.log('🎅 [SEARCH-FOR-SANTA] Saving booking payload:', searchPayload);

          const searchRes = await fetch(`${config.apiEndpoint}/custom/v1/store-search-for-santa-booking`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(searchPayload),
          });
          const searchText = await searchRes.text();
          if (!searchRes.ok) {
            console.error('❌ [SEARCH-FOR-SANTA] Store failed', searchRes.status, searchText);
          } else {
            console.log('✅ [SEARCH-FOR-SANTA] Booking stored:', searchText);
          }
        }
      } catch (searchError) {
        console.error('❌ [SEARCH-FOR-SANTA] Error storing Search for Santa booking:', searchError);
      }

      // 🛍️ Store merchandise pickup data
      try {
        const merchandiseTickets = selectedTickets.filter(
          t => t.pageType === 'Merchandise' && (t.quantity || 0) > 0
        );

        if (merchandiseTickets.length > 0) {
          // Group by webSaleItemId so duplicate rows (one per qty=1 item) are collapsed
          const groupedMap = {};
          merchandiseTickets.forEach(t => {
            const key = t.webSaleItemId || t.name;
            if (!groupedMap[key]) {
              groupedMap[key] = {
                name: t.name || '',
                webSaleItemId: t.webSaleItemId || '',
                quantity: 0,
                price: t.price ?? 0,
                total: 0
              };
            }
            groupedMap[key].quantity += (t.quantity || 1);
            groupedMap[key].total += (t.price ?? 0) * (t.quantity || 1);
          });

          const merchandisePayload = {
            bookingReference: newBookingReference,
            visitDate: formatDateLocal(selectedDate),
            visitTime: selectedTimeSlot?.time || '',
            customerName: `${customerDetails.firstName} ${customerDetails.lastName}`,
            customerEmail: customerDetails.email,
            customerPhone: customerDetails.phone || '',
            bookingTotal: finalAmount,
            merchandiseItems: Object.values(groupedMap)
          };

          await fetch(`${config.apiEndpoint}/custom/v1/store-merchandise-booking`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(merchandisePayload)
          });
        }
      } catch (mercError) {
        // Non-blocking — don't fail the booking if merchandise logging fails
      }

      // 📧 Automatically send wallet tickets email (ONLY for ticket bookings, NOT memberships)
      // Skip wallet tickets email for membership journeys - memberships don't have tickets
      if (!isMembershipJourney) {
      try {
        const activeTicketNames = selectedTickets
          .filter(t => (t.quantity || 0) > 0)
          .map(t => t.name || t.title)
          .filter(Boolean);
        const derivedEventName = isFlowerGardenShow
          ? 'Flower and Garden Show 2026'
          : activeTicketNames.length === 1
            ? activeTicketNames[0]
            : activeTicketNames.length > 1
              ? `${activeTicketNames[0]} +${activeTicketNames.length - 1} more`
              : 'Belvoir Castle Visit';

        const ticketData = {
          TicketReference: newBookingReference,
          EventName: derivedEventName,
          EventDate: formatDateLocal(selectedDate), // Use local timezone format (YYYY-MM-DD)
          EventTime: selectedTimeSlot?.time || (isFlowerGardenShow ? 'All Day' : '10:00'),
          CustomerName: `${customerDetails.firstName} ${customerDetails.lastName}`,
          VenueName: 'Belvoir Castle',
          QRCodeData: newBookingReference
        };
        
        // Generate wallet pass URLs from Railway server
        const passServerUrl = 'https://considerate-compassion-production.up.railway.app';
        
        const appleWalletUrl = `${passServerUrl}/api/tickets/download/${encodeURIComponent(newBookingReference)}/${encodeURIComponent(derivedEventName)}/${encodeURIComponent(formatDateLocal(selectedDate))}/${encodeURIComponent(selectedTimeSlot?.time || '10:00')}`;
        
        let googleWalletUrl = null;
        try {
          const googleResponse = await fetch(`${passServerUrl}/api/google/tickets`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(ticketData)
          });
          if (googleResponse.ok) {
            const googleData = await googleResponse.json();
            googleWalletUrl = googleData.saveUrl;

          }
        } catch (e) {

        }
        
        // Send email via WordPress (has working SMTP access)
          const wordpressApiUrl = `${config.apiEndpoint}/custom/v1/send-wallet-tickets`;

        // Build individual ticket lines from basket (most accurate) or fall back to selectedTickets.
        // Each line carries its own start time so tours, afternoon tea, etc. show clearly.
        const extractTicketTime = (ticket) => {
          // 1. Explicit selectedEventTime (upsells/tours/afternoon tea)
          if (ticket.selectedEventTime?.time) return ticket.selectedEventTime.time;
          // 2. selectedDate with a time component (ISO string with T)
          if (ticket.selectedDate && String(ticket.selectedDate).includes('T')) {
            const t = String(ticket.selectedDate).split('T')[1]?.substring(0, 5);
            if (t && t !== '00:00') return t;
          }
          return null; // entry ticket — time comes from selectedTimeSlot
        };

        const basketGroups = venposBasket?.data?.groups || [];
        const basketItems = basketGroups.flatMap(g => g.items || []);

        // Map basket item ids back to selectedTickets so we can recover event times
        const ticketTimeMap = {};
        selectedTickets.forEach(t => {
          const time = extractTicketTime(t);
          if (time) ticketTimeMap[t.webSaleItemId] = time;
        });

        const entryTime = selectedTimeSlot?.time || '';

        const ticketLines = basketItems.length > 0
          ? (() => {
              const map = new Map();
              basketItems.forEach(item => {
                const key = `${item.webSaleItemId}-${item.name}`;
                const qty = item.quantity || 1;
                const unit = item.unitPrice || (item.totalPrice / qty) || item.price || 0;
                // Recover the specific time for this ticket type
                const itemTime = ticketTimeMap[item.webSaleItemId] || entryTime;
                if (map.has(key)) {
                  const e = map.get(key);
                  e.quantity += qty;
                  e.totalPrice += item.totalPrice || (unit * qty);
                } else {
                  map.set(key, {
                    name: item.name || 'Ticket',
                    quantity: qty,
                    unitPrice: unit,
                    totalPrice: item.totalPrice || (unit * qty),
                    startTime: itemTime
                  });
                }
              });
              return Array.from(map.values());
            })()
          : selectedTickets
              .filter(t => t.quantity > 0)
              .map(t => ({
                name: t.name || t.title || 'Admission Ticket',
                quantity: t.quantity || 1,
                unitPrice: t.price || t.unitPrice || 0,
                totalPrice: t.totalPrice || ((t.price || 0) * (t.quantity || 1)),
                startTime: extractTicketTime(t) || entryTime
              }));

        const emailPayload = {
          customerEmail: customerDetails.email,
          customerName: `${customerDetails.firstName} ${customerDetails.lastName}`,
          bookingReference: newBookingReference,
          visitDate: new Date(selectedDate).toLocaleDateString('en-GB'),
          visitTime: selectedTimeSlot?.time || '',
          ticketCount: selectedTickets.filter(t => t.quantity > 0).reduce((sum, t) => sum + t.quantity, 0),
          totalAmount: finalAmount.toFixed(2),
          appleWalletUrl: appleWalletUrl,
          googleWalletUrl: googleWalletUrl || '',
          frontendUrl: config.frontendUrl,
          tickets: ticketLines,
          pageId: pageId || ''
        };

        const emailResponse = await fetch(wordpressApiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(emailPayload)
        });
        
        if (!emailResponse.ok) {
          const errorText = await emailResponse.text();
          console.error('❌ [EMAIL] Wallet ticket email failed:', errorText);
        }
      } catch (emailError) {

        // Don't fail the whole booking if email fails - customer can still download from confirmation page
        console.error('❌ [EMAIL] Wallet ticket email error:', emailError);
      }
      } // End of if (!isMembershipJourney) - only send wallet tickets for ticket bookings, not memberships

      // 🦁 SUMMER SAFARI — save booking to database
      console.log('🦁 [SAFARI-DB] isSafariBooking:', isSafariBooking, '| pageId:', pageId);
      if (isSafariBooking) {
        try {
          const safariPayload = {
            bookingReference: newBookingReference,
            pageId:           pageId,
            firstName:        customerDetails.firstName,
            lastName:         customerDetails.lastName,
            email:            customerDetails.email,
            phone:            customerDetails.phone || '',
            visitDate:        selectedDate ? new Date(selectedDate).toLocaleDateString('en-GB') : '',
            visitTime:        selectedTimeSlot?.time || '',
            ticketCount:      selectedTickets.filter(t => t.quantity > 0).reduce((sum, t) => sum + t.quantity, 0),
            totalAmount:      finalAmount.toFixed(2),
            ticketLines:      selectedTickets
              .filter(t => (t.quantity || 0) > 0)
              .map(t => ({
                name:       t.name || t.title || 'Ticket',
                quantity:   t.quantity,
                unitPrice:  t.price || t.unitPrice || 0,
                totalPrice: t.totalPrice || ((t.price || 0) * (t.quantity || 1)),
              })),
          };
          console.log('🦁 [SAFARI-DB] Saving booking payload:', safariPayload);
          const safariRes = await fetch(`${config.apiEndpoint}/custom/v1/safari-booking`, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify(safariPayload),
          });
          const safariResText = await safariRes.text();
          if (!safariRes.ok) {
            console.error('❌ [SAFARI-DB] API error', safariRes.status, safariResText);
          } else {
            console.log('✅ [SAFARI-DB] Booking recorded:', safariResText);
          }
        } catch (safariDbError) {
          console.error('❌ [SAFARI-DB] Network error recording safari booking:', safariDbError);
        }
      }

      // 🎒 CHILDREN'S SUMMER RETURN DISCOUNT
      // If this is a June visit with child admission tickets, create a Venpos
      // discount code worth the total child ticket value and email it to the
      // customer as a "children return this summer holiday" promotion.
      if (CHILDREN_RETURN_OFFER_ENABLED && !isMembershipJourney && isJuneVisit()) {
        try {
          const childTotal = calculateChildTicketsTotalValue();
          if (childTotal > 0) {
            const returnCode = await createChildrenReturnDiscount(newBookingReference);
            if (returnCode) {
              await sendChildrenReturnEmail({
                code:             returnCode,
                amount:           childTotal,
                bookingReference: newBookingReference,
                visitDate:        selectedDate
                  ? new Date(selectedDate).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })
                  : '',
              });
            }
          }
        } catch (childReturnError) {
          // Non-fatal — never block booking completion
          console.error('❌ [CHILDREN-RETURN] Error (non-fatal):', childReturnError);
        }
      }

      // 🎒 Mark children's return code redeemed if one was used in this booking.
      // Searches every place Venpos might surface applied discount codes:
      //   1. venposBasket.data.discounts  — array of code strings or {code} objects
      //   2. venposBasket.data.groups[].items[].discountCodes — per-line codes
      // This fires on both free and paid completed bookings.
      try {
        const findChildCode = (list = []) =>
          list.find(d => {
            const s = typeof d === 'string' ? d : (d?.code || d?.discountCode || '');
            return s.toUpperCase().startsWith('CHILD-');
          });

        const extractStr = (d) =>
          typeof d === 'string' ? d : (d?.code || d?.discountCode || '');

        // Source 1: top-level discounts array
        let usedChildCode = findChildCode(venposBasket?.data?.discounts || []);

        // Source 2: per-item discount codes from basket lines
        if (!usedChildCode) {
          const allItemCodes = (venposBasket?.data?.groups || [])
            .flatMap(g => g.items || [])
            .flatMap(item => item.discountCodes || item.appliedDiscounts || []);
          usedChildCode = findChildCode(allItemCodes);
        }

        if (usedChildCode) {
          const codeStr = extractStr(usedChildCode).toUpperCase();
          console.log('🎒 [CHILD-RETURN] Marking code redeemed:', codeStr);
          await markChildReturnCodeRedeemed(codeStr, newBookingReference);
        }
      } catch (redeemError) {
        console.warn('⚠️ [CHILD-RETURN] Error marking code redeemed (non-fatal):', redeemError);
      }

      if (choirBookingToken) {
        try {
          await fetch(`${config.apiEndpoint}/belvoir/v1/choir-booking-link/redeem`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            credentials: 'omit',
            body: JSON.stringify({
              token: choirBookingToken,
              bookingReference: newBookingReference || '',
            }),
          });
        } catch (choirRedeemError) {
          console.warn('⚠️ [CHOIR] Failed to mark booking link used (non-fatal):', choirRedeemError);
        }
      }

      // DON'T hide processing overlay here - it will be hidden by useEffect when step changes to 7

      // Meta Pixel Purchase — only for paid bookings (Meta requires value > 0)
      try {
        // Update URL first so the Purchase event is attributed to the success URL,
        // not the mid-journey booking page (and never the homepage).
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('booking', 'success');
          if (newBookingReference) {
            url.searchParams.set('ref', newBookingReference);
          }
          if (isSafariBooking) {
            url.searchParams.set('product', 'summer-safari');
          }
          window.history.replaceState({}, '', url.toString());
        } catch (urlError) {
          // Non-fatal
        }

        const purchaseContents = selectedTickets
          .filter(t => (t.quantity || 0) > 0)
          .map(t => ({
            id: String(t.webSaleItemId || t.name || t.title || 'ticket'),
            quantity: t.quantity || 1,
            item_price: Number(t.price ?? t.unitPrice ?? 0) || 0
          }));

        trackPurchase({
          value: finalAmount,
          currency: 'GBP',
          eventId: newBookingReference,
          contents: purchaseContents,
          contentName: isMembershipJourney
            ? 'Belvoir Castle Membership'
            : isSafariBooking
              ? 'Summer Safari'
              : isFlowerGardenShow
                ? 'Flower and Garden Show'
                : 'Belvoir Castle Tickets'
        });
      } catch (pixelError) {
        console.warn('⚠️ [META PIXEL] Purchase tracking failed (non-fatal):', pixelError);
      }

      // First-party analytics conversion
      try {
        const conversionType = isMembershipJourney ? 'membership_signup' : 'ticket_booking';
        const conversionItems = selectedTickets
          .filter(t => (t.quantity || 0) > 0)
          .map(t => ({
            id:       String(t.webSaleItemId || t.name || 'ticket'),
            name:     t.name || t.title || '',
            quantity: t.quantity || 1,
            price:    Number(t.price ?? t.unitPrice ?? 0) || 0,
          }));
        trackConversion({
          type:             conversionType,
          bookingReference: newBookingReference,
          value:            finalAmount,
          currency:         'GBP',
          locationId:       locationId,
          pageId:           pageId,
          items:            conversionItems,
        });
      } catch (trackerError) {
        console.warn('⚠️ [TRACKER] Conversion tracking failed (non-fatal):', trackerError);
      }

      // Navigate to confirmation immediately - use SET_CURRENT_STEP for both paid and free to ensure consistency
      console.log('✅ [BOOKING] processBooking completed successfully');
      console.log('✅ [BOOKING] Navigating to confirmation step (step 7)');
      console.log('✅ [BOOKING] Booking reference:', newBookingReference);
      console.log('✅ [BOOKING] Final amount:', finalAmount);
      dispatch({ type: BOOKING_ACTIONS.SET_CURRENT_STEP, payload: 7 });
      console.log('✅ [BOOKING] Step changed to 7 (confirmation)');

    } catch (err) {
      console.error('❌ [BOOKING] Error in processBooking:', err);
      console.error('❌ [BOOKING] Error message:', err.message);
      console.error('❌ [BOOKING] Error stack:', err.stack);

      // Determine error type and severity
      const finalAmount = parseFloat(venposBasket?.data?.finalPrice ?? calculateTotal()) || 0;
      const activePaymentResult = completedPaymentResult || paymentResult;
      
      // CRITICAL: Detect if payment was taken
      const paymentWasTaken = !!(
        activePaymentResult && 
        finalAmount > 0 && 
        (activePaymentResult.paymentToken || activePaymentResult.transactionId || activePaymentResult.authCode)
      );
      
      const errorType = paymentWasTaken ? 'PAYMENT_TAKEN_ORDER_FAILED' : 
                       (finalAmount > 0 ? 'PAYMENT_FAILED' : 'GENERAL_ERROR');

      // Prepare comprehensive error data
      const errorData = {
        errorType,
        errorMessage: err.message || 'Booking failed',
        bookingReference: existingBookingReference || bookingReference,
        orderId: responseHeaderId || basketId,
        paymentToken: activePaymentResult?.paymentToken,
        transactionId: activePaymentResult?.transactionId || activePaymentResult?.authCode,
        authCode: activePaymentResult?.authCode,
        amount: finalAmount,
        customerDetails,
        selectedTickets: selectedTickets.filter(t => t.quantity > 0),
        selectedDate,
        selectedTimeSlot,
        timestamp: new Date().toISOString(),
        stackTrace: err.stack,
        // Include diagnostic info if available
        ...(err.diagnosticInfo && { diagnosticInfo: err.diagnosticInfo })
      };
      
      // Log comprehensive error details
      logErrorDetails(errorData);
      
      // 🚨 SPECIAL HANDLING: BookingMetadataMissingError (automatic fallback)
      if (err.name === 'BookingMetadataMissingError' || err.requiresSystemStatusUpdate) {
        console.error('🚨 [BOOKING] BookingMetadataMissingError detected - triggering automatic fallback');
        
        // 1. Update System Status to DEPRECATED
        try {
          await markBookingSystemDeprecated({
            pageName: err.diagnosticInfo?.pageName || 'Unknown Event',
            errorMessage: err.message,
            basketId: errorData.orderId,
            customerEmail: customerDetails?.email,
            updates: [
              {
                description: `Automatic fallback triggered for ${err.diagnosticInfo?.pageName || 'booking'} due to missing event metadata`,
                timestamp: new Date().toLocaleString('en-GB', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit'
                })
              }
            ]
          });
          console.log('✅ [BOOKING] System status updated to DEPRECATED');
        } catch (statusError) {
          console.error('❌ [BOOKING] Failed to update system status:', statusError);
        }
        
        // 2. Send enhanced email notification to jhartwell@belvoircastle.com
        const enhancedErrorData = {
          ...errorData,
          errorType: 'BOOKING_METADATA_MISSING',
          context: 'Automatic fallback required - booking system marked as DEPRECATED',
          endpoint: 'CreateFreeOrder',
          diagnosticInfo: err.diagnosticInfo
        };
        
        sendErrorNotification(enhancedErrorData).then(success => {
          if (success) {
            console.log('✅ [BOOKING] Enhanced error notification sent to jhartwell@belvoircastle.com');
          } else {
            console.error('❌ [BOOKING] Failed to send enhanced error notification');
          }
        }).catch(notifError => {
          console.error('❌ [BOOKING] Error sending enhanced notification:', notifError);
        });
        
        // 3. Show clear error message to user on front-end
        errorData.userMessage = `We're experiencing technical difficulties completing bookings for ${err.diagnosticInfo?.pageName || 'this event'}. Our technical team has been automatically notified and will investigate this issue. Please try again later or contact support at info@belvoircastle.com for assistance.`;
        errorData.systemDeprecated = true;
      } else {
        // 🚨 AUTOMATIC MONITORING: Monitor critical errors and trigger maintenance mode if needed
        if (paymentWasTaken) {
          // CRITICAL: Payment taken but booking failed - immediately trigger maintenance mode
          await monitorPaymentTakenNoBooking(
            activePaymentResult,
            responseHeaderId || basketId,
            err
          );
        } else if (finalAmount > 0) {
          // Payment failed - monitor but don't trigger maintenance mode immediately
          await monitorApiEndpointFailure('Payment Processing', err, {
            orderId: responseHeaderId || basketId,
            amount: finalAmount
          });
        } else {
          // Free booking failed - monitor the failure
          await monitorBookingCreatedNoPayment(
            responseHeaderId || basketId,
            err,
            {
              customerEmail: customerDetails?.email,
              selectedDate,
              selectedTickets: selectedTickets.filter(t => t.quantity > 0)
            }
          );
        }
        
        // Send email notification (don't await - don't block user)
        sendErrorNotification(errorData).then(success => {
          if (success) {
            console.log('✅ [BOOKING] Error notification sent');
          } else {
            console.error('❌ [BOOKING] Failed to send error notification');
          }
        }).catch(notifError => {
          console.error('❌ [BOOKING] Error sending notification:', notifError);
        });
      }
      
      // Hide processing overlay
      setIsProcessingPayment(false);
      
      // Navigate to error page with full context
      navigate('/booking-error', {
        state: { errorData },
        replace: true
      });
      
      return;
    } finally {
      dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: false });
    }
  };

  const renderStepContent = () => {
    // For membership journeys, skip steps 2 and 3 (redirect handled by useEffect above)
    if (isMembershipJourney && (currentStep === 2 || currentStep === 3)) {
      return null;
    }
    
    // For grotto retail village page, skip step 3 (upsells)
    if (NO_UPSELL_PAGE_IDS.includes(pageId) && currentStep === 3) {
      return null;
    }
    
    switch (currentStep) {
      case 1:
        return (
          <TicketSelection
            tickets={tickets.filter(t => choirFreeAdultAllowed || !isChoirFreeAdultTicket(t))}
            selectedTickets={selectedTickets}
            choirFreeAdultAllowed={choirFreeAdultAllowed}
            choirLinkMessage={choirLinkMessage}
            onTicketUpdate={(ticketId, quantity) => {
              const ticket = (selectedTickets || []).find(t => t.webSaleItemId === ticketId)
                || (tickets || []).find(t => t.webSaleItemId === ticketId);
              let nextQty = quantity;
              if (isChoirFreeAdultTicket(ticket)) {
                if (!choirFreeAdultAllowed) nextQty = 0;
                else nextQty = Math.min(1, Math.max(0, Number(quantity) || 0));
              }
              dispatch({ 
                type: BOOKING_ACTIONS.UPDATE_TICKET_QUANTITY, 
                payload: { ticketId, quantity: nextQty } 
              });
            }}
            onContinue={handleNext}
            upgradeCreditAmount={upgradeCreditAmount}
            sortedUpgradeItems={sortedUpgradeItems}
            usedUpgradeItems={usedUpgradeItems}
            originalBookingData={originalBookingData}
            upgradeFromBooking={upgradeFromBooking}
            isLoadingUpgradeBooking={isLoadingUpgradeBooking}
            upgradeDiscountApplied={upgradeDiscountApplied}
            upgradeDiscountCode={upgradeDiscountCodeState}
            upgradeDiscountMessage={upgradeDiscountMessage}
            upgradeDiscountValue={upgradeDiscountValue}
          />
        );
      case 2:
        // Skip date selection for memberships
        if (isMembershipJourney) {
          return null;
        }
        return (
          <DateSelection
            availableDates={availableDates}
            datesLoading={datesLoading}
            pinnedVisitDate={pinnedVisitDate}
            filterMonth={filterMonth}
            selectedDate={selectedDate}
            timeSlots={timeSlots}
            selectedTimeSlot={selectedTimeSlot}
            onDateSelect={(date) => {
              // Check if user has upsells - if so, warn them
              const upsellTickets = selectedTickets.filter(t => t.isUpsellItem || t.pageType === 'EventTemplate');

              if (upsellTickets.length > 0) {

                setPendingDateTimeChange({ type: 'date', value: date });
                setShowDateTimeChangeModal(true);
                return;
              }
              
              // No upsells - proceed normally

              dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_DATE, payload: date });
            }}
            onTimeSlotSelect={async (rawTimeSlot) => {
              const timeSlot = typeof rawTimeSlot === 'string' ? { time: rawTimeSlot } : rawTimeSlot;
              const slotTime = timeSlot.time || (typeof rawTimeSlot === 'string' ? rawTimeSlot : '');

              // Check if user has upsells - if so, warn them
              const upsellTickets = selectedTickets.filter(t => t.isUpsellItem || t.pageType === 'EventTemplate');

              if (upsellTickets.length > 0 && selectedTimeSlot?.time && selectedTimeSlot.time !== slotTime) {

                setPendingDateTimeChange({ type: 'time', value: timeSlot });
                setShowDateTimeChangeModal(true);

                return;
              }
              
              // Update UI immediately so the chosen slot appears selected
              dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_TIME_SLOT, payload: timeSlot });

              // No upsells or same time - proceed with capacity reservation

              // CRITICAL: Log time slot to verify it has eventId (required for capacity reservation)
              console.log('⚠️ [CAPACITY] Time slot selected:', {
                time: slotTime,
                hasEventId: !!timeSlot.eventId,
                hasEventCapacityUsageId: !!timeSlot.eventCapacityUsageId,
                timeSlot: timeSlot
              });

              // Without eventId we cannot pre-reserve capacity, but the guest can still continue
              if (!timeSlot.eventId) {
                console.warn('⚠️ [CAPACITY] Time slot missing eventId — selection saved, skipping pre-reservation', {
                  timeSlot,
                  pageId,
                  selectedDate: formatLocalDate(selectedDate)
                });
                return;
              }

              // Proactively resolve capacity usages following the real Venpos flow
              try {
                const items = selectedTickets
                  .filter(ticket => (ticket.quantity || 0) > 0)
                  .map(ticket => ({
                    pageType: 'WebPage',
                    pageId: pageId,
                    webSaleItemId: ticket.webSaleItemId,
                    webSaleItemTypeID: ticket.webSaleItemTypeID,
                    quantity: ticket.quantity,
                    selectedDate: `${formatDateLocal(selectedDate)}T${slotTime}:00`,
                    fixedDonationAmount: 0,
                    name: ticket.name,
                    giftAidEligible: false,
                    isSysGift: false,
                    isSysMemberCard: false,
                    reservationExpiryDateTime: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
                    // Include eventId from selected time slot (already validated above)
                    eventId: timeSlot.eventId
                  }));

                // Step 1: Add event capacity usages (this reserves the capacity and returns eventCapacityUsageId)
                console.log('🔒 [CAPACITY] Reserving capacity for items:', items.map(i => ({
                  webSaleItemId: i.webSaleItemId,
                  quantity: i.quantity,
                  eventId: i.eventId
                })));

                const capacityResponse = await venposService.addEventCapacityUsages(
                  locationId,
                  pageId,
                  items,
                  null,
                  []
                );

                console.log('✅ [CAPACITY] Capacity response received:', {
                  isError: capacityResponse.isError,
                  hasData: !!capacityResponse.data,
                  itemsCount: (capacityResponse.data?.items || capacityResponse.data || []).length
                });

                if (!capacityResponse.isError && capacityResponse.data) {

                  // Step 2: Extract eventCapacityUsageId from response and update the timeSlot
                  // The response should contain the items with eventCapacityUsageId populated
                  const responseItems = capacityResponse.data.items || capacityResponse.data || [];
                  
                  // CRITICAL: Validate that capacity was actually reserved (must have eventCapacityUsageId)
                  const itemsWithCapacity = responseItems.filter(item => item.eventCapacityUsageId);
                  console.log('🔍 [CAPACITY] Response items:', {
                    totalItems: responseItems.length,
                    itemsWithCapacityId: itemsWithCapacity.length,
                    items: responseItems.map(i => ({
                      webSaleItemId: i.webSaleItemId,
                      hasEventCapacityUsageId: !!i.eventCapacityUsageId,
                      eventCapacityUsageId: i.eventCapacityUsageId,
                      eventId: i.eventId
                    }))
                  });

                  if (itemsWithCapacity.length === 0) {
                    console.error('❌ [CAPACITY] Capacity reservation failed - no items have eventCapacityUsageId!');
                    alert('Unable to reserve this time slot. The slot may be full or unavailable. Please try a different time.');
                    dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_TIME_SLOT, payload: null });
                    return;
                  }

                  if (responseItems.length > 0 && responseItems[0].eventCapacityUsageId) {
                    const enrichedTimeSlot = {
                      ...timeSlot,
                      eventCapacityUsageId: responseItems[0].eventCapacityUsageId,
                      eventId: responseItems[0].eventId || timeSlot.eventId
                    };

                    dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_TIME_SLOT, payload: enrichedTimeSlot });
                    
                    // CRITICAL FIX: Assign individual eventCapacityUsageId to each ticket
                    // This ensures each participant gets their own capacity reservation in Venpos

                    const updatedTickets = selectedTickets.map(ticket => {
                      if (ticket.quantity > 0) {
                        // Find matching response item by webSaleItemId
                        const matchingResponse = responseItems.find(item => 
                          item.webSaleItemId === ticket.webSaleItemId
                        );
                        
                        if (matchingResponse) {

                          return {
                            ...ticket,
                            eventCapacityUsageId: matchingResponse.eventCapacityUsageId,
                            eventId: matchingResponse.eventId || ticket.eventId
                          };
                        }
                      }
                      return ticket;
                    });

                    // Update selected tickets with individual capacity usage IDs
                    dispatch({
                      type: BOOKING_ACTIONS.SET_SELECTED_TICKETS,
                      payload: updatedTickets
                    });

                    // Step 3: Validate times with individual capacity IDs (CRITICAL FIX)
                    const itemsWithCapacityId = items.map(item => {
                      // Find matching response item for this specific item
                      const matchingResponse = responseItems.find(resp => 
                        resp.webSaleItemId === item.webSaleItemId
                      );
                      
                      const assignedCapacityId = matchingResponse?.eventCapacityUsageId || responseItems[0].eventCapacityUsageId;
                      const assignedEventId = matchingResponse?.eventId || responseItems[0].eventId || timeSlot.eventId;

                      return {
                        ...item,
                        eventCapacityUsageId: assignedCapacityId,
                        eventId: assignedEventId
                      };
                    });

                    await venposService.validateBasketTimes(locationId, itemsWithCapacityId);
                  } else {
                    console.error('❌ [CAPACITY] Capacity reservation response missing eventCapacityUsageId');
                    alert('Unable to reserve this time slot. Please try a different time or contact support.');
                    dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_TIME_SLOT, payload: null });
                    return;
                  }
                } else {
                  console.error('❌ [CAPACITY] Capacity reservation failed:', {
                    isError: capacityResponse.isError,
                    error: capacityResponse.error,
                    response: capacityResponse
                  });
                  alert('Unable to reserve this time slot. The slot may be full. Please try a different time.');
                  dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_TIME_SLOT, payload: null });
                  return;
                }
              } catch (e) {
                console.error('❌ [CAPACITY] Capacity reservation error:', e);
                alert('Unable to reserve this time slot. Please try again or contact support.');
                dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_TIME_SLOT, payload: null });
                return;
              }
            }}
            pageId={pageId}
            requireAfternoonTea={requireAfternoonTea}
            onContinue={handleNext}
            canContinue={canProceedToNextStep()}
          />
        );
      case 3:
        // Skip upsells for memberships and grotto retail village
        if (isMembershipJourney || NO_UPSELL_PAGE_IDS.includes(pageId)) {
          return null;
        }
        return (
          <UpsellStep
            upsells={availableUpsells}
            locationId={locationId}
            pageId={pageId}
            isFeaturedTour={FEATURED_TOUR_PAGE_IDS.includes(pageId)}
            requireAfternoonTea={requireAfternoonTea}
            selectedUpsells={selectedUpsells}
            selectedTickets={selectedTickets}
            selectedTimeSlot={selectedTimeSlot}
            onUpsellRemove={async (upsellId) => {
              try {
                // Remove upsell tickets from local state
                const ticketsWithoutThisUpsell = selectedTickets.filter(t => {
                  if (!(t.isUpsellItem || t.pageType === 'EventTemplate')) return true;
                  const id = t.parentUpsellId || t.pageId;
                  return id !== upsellId;
                });
                dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_TICKETS, payload: ticketsWithoutThisUpsell });

                // Update selectedUpsells list
                const updatedSelectedUpsells = selectedUpsells.filter(u => u.id !== upsellId);
                dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_UPSELLS, payload: updatedSelectedUpsells });

                // If basket exists, refresh it without the removed upsell items
                if (basketId) {
                  const saverCode = getFamilySaverCode();
                  const circusCode = circusDiscountCodeState; // Use existing circus code if available
                  const currentDiscounts = [saverCode, circusCode].filter(Boolean);

                  // CRITICAL FIX: Expand main tickets before building basket items
                  const expandedRemainingTickets = [];
                  ticketsWithoutThisUpsell
                    .filter(ticket => ticket.quantity > 0)
                    .forEach(ticket => {
                      const isUpsell = ticket.isUpsellItem || ticket.pageType === 'EventTemplate';
                      const ticketQty = ticket.quantity || 1;
                      
                      // Don't expand upsell items
                      if (isUpsell || ticketQty === 1) {
                        expandedRemainingTickets.push({ ...ticket, quantity: ticketQty });
                      } else {
                        // Expand main tickets into individual items
                        for (let i = 0; i < ticketQty; i++) {
                          expandedRemainingTickets.push({
                            ...ticket,
                            quantity: 1,
                            personIndex: i
                          });
                        }
                      }
                    });

                  // Build existing (remaining) items payload
                  const existingBasketItems = expandedRemainingTickets
                    .map(ticket => {
                      const isUpsell = ticket.isUpsellItem || ticket.pageType === 'EventTemplate';
                      return {
                        pageType: ticket.pageType || (isUpsell ? 'EventTemplate' : 'WebPage'),
                        pageId: ticket.pageId || (isUpsell ? ticket.parentUpsellId : pageId),
                        webSaleItemId: ticket.webSaleItemId,
                        webSaleItemTypeID: ticket.webSaleItemTypeID || 2,
                        quantity: ticket.quantity,
                        selectedDate: ticket.selectedDate || ticket.selectedEventTime?.startDateTime || (formatDateLocal(selectedDate) + 'T' + (selectedTimeSlot?.time || '10:00') + ':00'),
                        fixedDonationAmount: 0,
                        name: ticket.name || '',
                        giftAidEligible: ticket.giftAidEligible || false,
                        isSysGift: false,
                        isSysMemberCard: false,
                        reservationExpiryDateTime: ticket.reservationExpiryDateTime || new Date(Date.now() + 30 * 60 * 1000).toISOString(),
                        eventId: ticket.eventId || ticket.selectedEventTime?.eventId || (isUpsell ? null : selectedTimeSlot?.eventId),
                        eventCapacityUsageId: ticket.eventCapacityUsageId || ticket.selectedEventTime?.eventCapacityUsageId || (isUpsell ? null : selectedTimeSlot?.eventCapacityUsageId),
                        ...(ticket.parentWebSaleItemId && { parentWebSaleItemId: ticket.parentWebSaleItemId }),
                        questionAnswers: ticket.questionAnswers || {},
                        modifierSelection: ticket.modifierSelection || null
                      };
                    });

                  // Directly reload basket with remaining items (no new upsells)
                  const modifiersArray = [];
                  existingBasketItems.forEach((item, idx) => {
                    if (item.modifierSelection) {
                      modifiersArray.push({
                        webSaleItemId: item.webSaleItemId,
                        productModifierId: item.modifierSelection.modifierId,
                        productModifierTextId: item.modifierSelection.modifierTextId,
                        value: "1",
                        index: idx
                      });
                    }
                  });

                  const basketPayload = {
                    items: existingBasketItems,
                    discounts: currentDiscounts,
                    giftAidSelected: false,
                    pageId,
                    modifiers: modifiersArray,
                    discountsFromMembership: [],
                    orderId: basketId,
                    ...(registeredCustomerId || basketCustomerId ? { customerId: registeredCustomerId || basketCustomerId } : {})
                  };

                  const reload = await venposService.loadBasket(locationId, basketPayload, getFamilySaverCode());
                  if (!reload.isError) {
                    setVenposBasket(reload);
                  }
                }
              } catch (err) {

                dispatch({ type: BOOKING_ACTIONS.SET_ERROR, payload: 'Failed to remove item. Please try again.' });
              }
            }}
            onUpsellSelection={(upsell, quantity) => {
              if (quantity === 0) {
                // Remove upsell from selection
                dispatch({
                  type: BOOKING_ACTIONS.SET_SELECTED_UPSELLS,
                  payload: selectedUpsells.filter(u => u.id !== upsell.id)
                });
              } else {
                // Add or update upsell in selection
                const existingIndex = selectedUpsells.findIndex(u => u.id === upsell.id);
                if (existingIndex >= 0) {
                  const updated = [...selectedUpsells];
                  updated[existingIndex] = { ...upsell, quantity };
                  dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_UPSELLS, payload: updated });
                } else {
                  dispatch({
                    type: BOOKING_ACTIONS.SET_SELECTED_UPSELLS,
                    payload: [...selectedUpsells, { ...upsell, quantity }]
                  });
                }
              }
            }}
            onUpsellItemAdd={async (upsellItems, formattedQuestions = []) => {


              try {
                // Replace previous items for this upsell with the new configuration
                const parentUpsellId = upsellItems[0]?.parentUpsellId || upsellItems[0]?.pageId;
                const ticketsWithoutThisUpsell = selectedTickets.filter(t => {
                  // keep main tickets; remove upsell tickets belonging to this upsell id
                  if (!(t.isUpsellItem || t.pageType === 'EventTemplate')) return true;
                  const id = t.parentUpsellId || t.pageId;
                  return id !== parentUpsellId;
                });

                const newTickets = [...ticketsWithoutThisUpsell];

                upsellItems.forEach(upsellItem => {
                  // Always add each person as their own entry (never merge).
                  // Merging by modifier would collapse different allergen notes into a single
                  // ticket and lose per-person data — each person must keep their own row.
                  newTickets.push({
                    webSaleItemId: upsellItem.webSaleItemId,
                    webSaleItemTypeID: upsellItem.webSaleItemTypeID,
                    name: upsellItem.name,
                    description: upsellItem.description,
                    price: upsellItem.price,
                    maxPurchaseQty: upsellItem.maxPurchaseQty,
                    quantity: 1, // always 1 — each entry represents one person
                    pageType: upsellItem.pageType || 'EventTemplate',
                    pageId: upsellItem.pageId || upsellItem.parentUpsellId,
                    eventId: upsellItem.eventId,
                    eventCapacityUsageId: upsellItem.eventCapacityUsageId,
                    parentWebSaleItemId: upsellItem.parentWebSaleItemId,
                    selectedDate: upsellItem.selectedDate || upsellItem.selectedEventTime?.startDateTime,
                    reservationExpiryDateTime: upsellItem.reservationExpiryDateTime,
                    isUpsellItem: true,
                    parentUpsellId: upsellItem.parentUpsellId,
                    parentUpsellName: upsellItem.parentUpsellName,
                    selectedEventTime: upsellItem.selectedEventTime,
                    questionAnswers: upsellItem.questionAnswers,
                    eventQuestions: upsellItem.eventQuestions,
                    modifierSelection: upsellItem.modifierSelection,
                    allergenNotes: upsellItem.allergenNotes,
                    participantDetails: upsellItem.participantDetails,
                    participantAnswers: upsellItem.participantAnswers
                  });
                });

                dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_TICKETS, payload: newTickets });

                // Compute new total quantity for this upsell and update the summary list
                const totalForThisUpsell = newTickets
                  .filter(t => (t.isUpsellItem || t.pageType === 'EventTemplate') && (t.parentUpsellId || t.pageId) === parentUpsellId)
                  .reduce((sum, t) => sum + (t.quantity || 0), 0);

                const mappedUpsells = selectedUpsells.map(u => (
                  u.id === parentUpsellId ? { ...u, quantity: totalForThisUpsell } : u
                ));
                const exists = mappedUpsells.some(u => u.id === parentUpsellId);
                const finalUpsells = exists ? mappedUpsells : [...mappedUpsells, { id: parentUpsellId, name: upsellItems[0]?.parentUpsellName, quantity: totalForThisUpsell }];
                const cleanedUpsells = finalUpsells.filter(u => (u.quantity || 0) > 0);

                dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_UPSELLS, payload: cleanedUpsells });

                // If we have a basket, add items to the VenPos basket with proper event capacity reservation
                if (basketId) {

                  // Get current discounts to pass to the upsell addition
                  const saverCode = getFamilySaverCode();
                  const circusCode = circusDiscountCodeState; // Use existing circus code if available
                  const currentDiscounts = [saverCode, circusCode].filter(Boolean);
                  
                  // CRITICAL FIX: Expand main tickets before building basket items
                  const expandedExistingTickets = [];
                  ticketsWithoutThisUpsell
                    .filter(ticket => ticket.quantity > 0)
                    .forEach(ticket => {
                      const isUpsell = ticket.isUpsellItem || ticket.pageType === 'EventTemplate';
                      const ticketQty = ticket.quantity || 1;
                      
                      // Don't expand upsell items
                      if (isUpsell || ticketQty === 1) {
                        expandedExistingTickets.push({ ...ticket, quantity: ticketQty });
                      } else {
                        // Expand main tickets into individual items
                        for (let i = 0; i < ticketQty; i++) {
                          expandedExistingTickets.push({
                            ...ticket,
                            quantity: 1,
                            personIndex: i
                          });
                        }
                      }
                    });

                  // Prepare existing items excluding previous items for this upsell; basket will be: existing (without old upsell) + new upsell
                  const existingBasketItems = expandedExistingTickets
                    .map(ticket => {
                      // Check if this is an upsell item or main ticket
                      const isUpsell = ticket.isUpsellItem || ticket.pageType === 'EventTemplate';
                      
                      return {
                        pageType: ticket.pageType || (isUpsell ? 'EventTemplate' : 'WebPage'),
                        pageId: ticket.pageId || (isUpsell ? ticket.parentUpsellId : pageId),
                        webSaleItemId: ticket.webSaleItemId,
                        webSaleItemTypeID: ticket.webSaleItemTypeID || 2,
                        quantity: ticket.quantity,
                        // Use ticket's own selectedDate if available (upsells may have different times)
                        selectedDate: ticket.selectedDate || 
                                     ticket.selectedEventTime?.startDateTime || 
                                     (formatDateLocal(selectedDate) + 'T' + (selectedTimeSlot?.time || '10:00') + ':00'),
                      fixedDonationAmount: 0,
                        name: ticket.name || '',
                        giftAidEligible: ticket.giftAidEligible || false,
                      isSysGift: false,
                      isSysMemberCard: false,
                        reservationExpiryDateTime: ticket.reservationExpiryDateTime || new Date(Date.now() + 30 * 60 * 1000).toISOString(),
                        // CRITICAL: Preserve each item's own event metadata (upsells have their own events!)
                        eventId: ticket.eventId || ticket.selectedEventTime?.eventId || (isUpsell ? null : selectedTimeSlot?.eventId),
                        eventCapacityUsageId: ticket.eventCapacityUsageId || ticket.selectedEventTime?.eventCapacityUsageId || (isUpsell ? null : selectedTimeSlot?.eventCapacityUsageId),
                        // Include parentWebSaleItemId for upsells
                        ...(ticket.parentWebSaleItemId && { parentWebSaleItemId: ticket.parentWebSaleItemId }),
                        // Preserve question answers and modifiers if present
                        questionAnswers: ticket.questionAnswers || {},
                        modifierSelection: ticket.modifierSelection || null
                      };
                    });


                  existingBasketItems.forEach((item, idx) => {

                  });

                  // Use the new addUpsellItemsToBasket method which handles event capacity reservation
                  const result = await venposService.addUpsellItemsToBasket(
                    locationId,
                    pageId,
                    upsellItems,
                    basketId,
                    registeredCustomerId || basketCustomerId || null,
                    currentDiscounts,
                    existingBasketItems, // Pass existing items so they're included in basket refresh
                    formattedQuestions // Pass formatted event questions
                  );
                  
                  if (result.isError) {

                    // Show error to user but don't block the flow
                    dispatch({ 
                      type: BOOKING_ACTIONS.SET_ERROR, 
                      payload: 'Unable to add some items. Please try again or continue without them.' 
                    });
                  } else {


                    // Store formatted questions if they were returned
                    if (result.data?.formattedQuestions && result.data.formattedQuestions.length > 0) {

                      // Store in state for access in confirmBooking
                      setFormattedEventQuestions(result.data.formattedQuestions);
                    }
                    
                    // Update basket details if we got them back
                    if (result.data?.basketDetails) {
                      setVenposBasket({ data: result.data.basketDetails });
                      
                      // CRITICAL: Update selectedTickets with the returned basket data to preserve eventCapacityUsageId
                      // basketDetails is the data object directly (not wrapped in another data property)
                      const basketGroups = result.data.basketDetails.groups || [];
                      const allBasketItems = basketGroups.flatMap(g => g.items || []);


                      if (allBasketItems.length > 0) {


                        // Log the full basket item structure to see what Venpos returned

                        allBasketItems.forEach((bi, idx) => {

                          if (bi.instances && bi.instances.length > 0) {
                            bi.instances.forEach((inst, instIdx) => {

                            });
                          }
                        });
                        
                        // CRITICAL: For items with modifiers that share the same webSaleItemId,
                        // we need to match them by index to preserve their unique eventCapacityUsageIds
                        // Build a list of indices for each webSaleItemId that has modifiers
                        const modifierItemIndices = new Map(); // webSaleItemId -> next available index in basket
                        
                        const updatedTickets = newTickets.map(ticket => {
                          // Find this ticket in the basket response
                          let basketItem;
                          
                          if (ticket.modifierSelection) {
                            // For modifier items, use index-based matching within the same webSaleItemId
                            const itemsWithSameId = allBasketItems.filter(bi => bi.webSaleItemId === ticket.webSaleItemId);
                            const currentIndex = modifierItemIndices.get(ticket.webSaleItemId) || 0;
                            basketItem = itemsWithSameId[currentIndex];
                            modifierItemIndices.set(ticket.webSaleItemId, currentIndex + 1);

                          } else {
                            // Match by webSaleItemId only for items without modifiers
                            basketItem = allBasketItems.find(bi => bi.webSaleItemId === ticket.webSaleItemId);
                          }
                          
                          if (basketItem) {
                            const isUpsell = ticket.isUpsellItem || ticket.pageType === 'EventTemplate';

                            // Update with basket's event metadata (especially eventCapacityUsageId)
                            // CRITICAL: Preserve participant details, questions, and answers from original ticket
                            return {
                              ...ticket,
                              eventId: basketItem.eventId || ticket.eventId,
                              eventCapacityUsageId: basketItem.eventCapacityUsageId || ticket.eventCapacityUsageId,
                              selectedDate: basketItem.selectedDate || ticket.selectedDate,
                              reservationExpiryDateTime: basketItem.reservationExpiryDateTime || ticket.reservationExpiryDateTime,
                              // Also preserve pageType and pageId from basket if available
                              pageType: basketItem.pageType || ticket.pageType,
                              pageId: basketItem.pageId || ticket.pageId,
                              // CRITICAL: Preserve questions, participant details and answers (basket response won't have these)
                              questionAnswers: ticket.questionAnswers,
                              eventQuestions: ticket.eventQuestions,
                              participantDetails: ticket.participantDetails,
                              participantAnswers: ticket.participantAnswers
                            };
                          }

                          return ticket;
                        });

                        dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_TICKETS, payload: updatedTickets });
                      }
                    }
                  }
                } else {

                }
                
              } catch (error) {

                // Show error to user but don't block the flow
                dispatch({ 
                  type: BOOKING_ACTIONS.SET_ERROR, 
                  payload: 'Unable to add some items. Please try again or continue without them.' 
                });
              }
            }}
            selectedDate={selectedDate}
            loading={upsellsLoading}
            openAnimalParkUpsellNonce={openAnimalParkUpsellNonce}
            openCircusUpsellNonce={openCircusUpsellNonce}
            onChangeDateClick={() => {
              // Go back to date selection step (step 2)
              dispatch({ type: BOOKING_ACTIONS.SET_CURRENT_STEP, payload: 2 });
            }}
          />
        );
      case 4:
        return (
          <CustomerDetails
            customerDetails={customerDetails}
            registeredCustomerId={registeredCustomerId}
            membershipDetails={membershipDetails}
            vineyardGuestCount={vineyardGuestCount}
            onUpdate={(details) => {
              dispatch({ type: BOOKING_ACTIONS.UPDATE_CUSTOMER_DETAILS, payload: details });
            }}
            onAddressUpdate={(address) => {
              dispatch({ type: BOOKING_ACTIONS.UPDATE_CUSTOMER_ADDRESS, payload: address });
            }}
            onMemberDetailsUpdate={(updatedMembershipDetails) => {
              setMembershipDetails(updatedMembershipDetails);
            }}
            onClearRegistration={() => {

              setRegisteredCustomerId(null);
              setBasketCustomerId(null);
              // Clear basket to force fresh registration on next submit
              setBasketId(null);
              setVenposBasket(null);
              setPreflightDone(false);
            }}
          />
        );
      case 5:
        return (
          <PaymentDetails
            paymentDetails={paymentDetails}
            upgradeCreditAmount={upgradeCreditAmount}
            sortedUpgradeItems={sortedUpgradeItems}
            usedUpgradeItems={usedUpgradeItems}
            originalBookingData={originalBookingData}
            upgradeFromBooking={upgradeFromBooking}
            upgradeDiscountApplied={upgradeDiscountApplied}
            upgradeDiscountCode={upgradeDiscountCodeState}
            upgradeDiscountMessage={upgradeDiscountMessage}
            totalAmount={calculateTotal()}
            customerDetails={customerDetails}
            basketId={basketId}
            locationId={locationId}
            venposBasket={venposBasket}
            appliedSaverCode={appliedSaverCode}
            appliedCircusCode={circusDiscountCodeState}
            autoApplyDiscountCode={choirFreeAdultAllowed ? choirDiscountCode : null}
            registeredCustomerId={registeredCustomerId} // Pass registered customer ID
            membershipDetails={membershipDetails} // Pass membership details for membership journeys
            onUndoDiscounts={undoAllDiscountCodes}
            isUndoingDiscounts={isUndoingDiscounts}
            createdDiscountCodes={createdDiscountCodes}
            onUpdate={(details) => {
              dispatch({ type: BOOKING_ACTIONS.UPDATE_PAYMENT_DETAILS, payload: details });
            }}
            onCardUpdate={(cardDetails) => {
              dispatch({ type: BOOKING_ACTIONS.UPDATE_CARD_DETAILS, payload: cardDetails });
            }}
            onBasketUpdate={(updatedBasket) => {

              // Ensure the basket data is properly wrapped for consistency
              const wrappedBasket = updatedBasket?.data ? updatedBasket : { data: updatedBasket };
              setVenposBasket(wrappedBasket);

            }}
            // In review step, proceed to dedicated payment step if amount > 0
            onProceedToPayment={() => {

              dispatch({ type: BOOKING_ACTIONS.SET_CURRENT_STEP, payload: 6 });
            }}
            onPaymentSuccess={async (paymentResult) => {
              console.log('✅ [PAYMENT] onPaymentSuccess called in BookingJourney');
              console.log('✅ [PAYMENT] Payment result received:', paymentResult);
              console.log('✅ [PAYMENT] Payment result type:', typeof paymentResult);
              console.log('✅ [PAYMENT] Payment result keys:', paymentResult ? Object.keys(paymentResult) : 'null');
              console.log('✅ [PAYMENT] Is test payment?', paymentResult?.isTest);

              // Store payment result and complete booking process
              console.log('✅ [PAYMENT] Storing payment result in state');
              setPaymentResult(paymentResult);
              
              try {
                console.log('✅ [PAYMENT] Calling processBooking with payment result');
                // Complete the booking in Venpos system - pass paymentResult directly
                await processBooking(paymentResult);
                console.log('✅ [PAYMENT] processBooking completed successfully');
              } catch (error) {
                console.error('❌ [PAYMENT] Error in processBooking:', error);
                console.error('❌ [PAYMENT] Error stack:', error.stack);

                // Handle booking completion failure
                dispatch({ 
                  type: BOOKING_ACTIONS.SET_ERROR, 
                  payload: 'Payment successful but booking completion failed. Please contact support.' 
                });
              }
            }}
            onPaymentFailure={(error) => {

              // Handle payment failure (stay on payment page)
            }}
          />
        );
      case 6:
        // CRITICAL: Check if this is actually a free booking (amount = 0)
        // If so, process it immediately via CreateFreeOrder instead of showing payment page
        const paymentPageFinalAmount = venposBasket?.data?.finalPrice ?? 
                                      venposBasket?.finalPrice ?? 
                                      calculateTotal();
        
        // If amount is 0 or less, this is a free booking - process it immediately
        if (paymentPageFinalAmount <= 0) {
          console.log('💰 [BookingJourney] Free booking detected on payment page, processing immediately via CreateFreeOrder');
          // Process free booking immediately
          processBooking().catch(err => {
            console.error('❌ [BookingJourney] Error processing free booking:', err);
            dispatch({ 
              type: BOOKING_ACTIONS.SET_ERROR, 
              payload: 'Failed to process free booking. Please try again.' 
            });
          });
          // Return loading state while processing
          return (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <div style={{ fontSize: '1.2rem', marginBottom: '1rem' }}>Processing your free booking...</div>
              <div style={{ color: '#666' }}>No payment required.</div>
            </div>
          );
        }
        
        return (
          <PaymentPage
            basketId={basketId}
            locationId={locationId}
            customerDetails={customerDetails}
            venposBasket={venposBasket}
            selectedTickets={selectedTickets}
            selectedDate={selectedDate}
            pageId={pageId}
            modifierSelections={modifierSelections}
            registeredCustomerId={registeredCustomerId}
            upgradeCreditAmount={upgradeCreditAmount}
            sortedUpgradeItems={sortedUpgradeItems}
            upgradeDiscountApplied={upgradeDiscountApplied}
            upgradeDiscountCode={upgradeDiscountCodeState}
            upgradeDiscountMessage={upgradeDiscountMessage}
            upgradeDiscountValue={upgradeDiscountValue}
            membershipDetails={membershipDetails}
            onUndoDiscounts={undoAllDiscountCodes}
            isUndoingDiscounts={isUndoingDiscounts}
            createdDiscountCodes={createdDiscountCodes}
            onPaymentProcessing={(isProcessing) => {

              setIsProcessingPayment(isProcessing);
            }}
            onPaymentSuccess={async (paymentResult) => {

              setPaymentResult(paymentResult);
              setIsProcessingPayment(true); // Keep overlay while creating order
              try {
                await processBooking(paymentResult);
              } catch (err) {

                dispatch({ type: BOOKING_ACTIONS.SET_ERROR, payload: 'Payment succeeded but order creation failed. Please contact support.' });
              }
            }}
            onPaymentFailure={(error) => {

            }}
            onCancel={() => {
              // Navigate back: membership journeys restart to step 1; others back to review
              if (isMembershipJourney) {
                handleRestartJourney({ preserveCustomerDetails: true, preserveMembershipDetails: true });
                dispatch({ type: BOOKING_ACTIONS.SET_CURRENT_STEP, payload: 1 });
              } else {
              dispatch({ type: BOOKING_ACTIONS.SET_CURRENT_STEP, payload: 5 });
              }
            }}
          />
        );
      case 7:
        // Use MembershipConfirmation for membership journeys, BookingConfirmation for regular bookings
        if (isMembershipJourney) {
          return (
            <MembershipConfirmation
              bookingReference={bookingReference}
              selectedTickets={selectedTickets}
              customerDetails={customerDetails}
              totalAmount={bookingSummary?.totalAmount || calculateTotal()}
              venposBasket={venposBasket}
              bookingSummary={bookingSummary}
            />
          );
        }
        return (
          <BookingConfirmation
            bookingReference={bookingReference}
            selectedTickets={selectedTickets}
            selectedDate={selectedDate}
            selectedTimeSlot={selectedTimeSlot}
            customerDetails={customerDetails}
            totalAmount={bookingSummary?.totalAmount || calculateTotal()}
            venposBasket={venposBasket}
            bookingSummary={bookingSummary}
            isFamilySaver={!!getFamilySaverCode()}
            pageId={pageId}
          />
        );
      default:
        return null;
    }
  };

  const getStepTitle = () => {
    if (isMembershipJourney) {
      const titles = {
        1: 'Select Your Membership',
        4: 'Your Details',
        5: 'Review & Discounts',
        6: 'Payment',
        7: 'Membership Confirmation'
      };
      return titles[currentStep] || '';
    }
    const titles = {
      1: 'Select Your Tickets',
      2: 'Choose Date & Time',
      3: 'Enhance Your Visit',
      4: 'Your Details',
      5: 'Review & Discounts',
      6: 'Payment',
      7: 'Booking Confirmation'
    };
    return titles[currentStep] || '';
  };

  const getStepDescription = () => {
    if (isMembershipJourney) {
      const descriptions = {
        1: 'Choose your membership',
        4: 'Enter your contact information',
        5: 'Review your membership and apply discount codes',
        6: 'Complete your secure payment',
        7: 'Your membership has been confirmed'
      };
      return descriptions[currentStep] || '';
    }
    const descriptions = {
      1: 'Choose the tickets you\'d like to purchase',
      2: 'Select your preferred visit date and time',
      3: 'Add special experiences to make your visit memorable',
      4: 'Enter your contact information',
      5: 'Review your booking and apply discount codes',
      6: 'Complete your secure payment',
      7: 'Your booking has been confirmed'
    };
    return descriptions[currentStep] || '';
  };

  // 🔒 Check maintenance mode status (checked on mount via useEffect)
  if (maintenanceMode.enabled) {
    return (
      <>
        <TechnicalDifficultiesOverlay>
          <TechnicalDifficultiesBox>
            <TechnicalDifficultiesIcon>⚠️</TechnicalDifficultiesIcon>
            <TechnicalDifficultiesTitle>
              We're Currently Having Technical Difficulties
            </TechnicalDifficultiesTitle>
            <TechnicalDifficultiesMessage>
              {maintenanceMode.reason || 'We apologize for the inconvenience. Our booking system is temporarily unavailable while we perform essential maintenance.'}
            </TechnicalDifficultiesMessage>
            <TechnicalDifficultiesMessage>
              Please try again later. We appreciate your patience and understanding.
            </TechnicalDifficultiesMessage>
            {maintenanceMode.enabled && (
              <TechnicalDifficultiesMessage style={{ fontSize: '0.9rem', color: '#666', fontStyle: 'italic' }}>
                Maintenance mode was automatically enabled due to system errors. Our team has been notified.
              </TechnicalDifficultiesMessage>
            )}
          </TechnicalDifficultiesBox>
        </TechnicalDifficultiesOverlay>
      </>
    );
  }

  if (upgradeLinkExpired) {
    return (
      <BookingContainer>
        <LoadingScreen>
          <div style={{ textAlign: 'center', maxWidth: '720px', margin: '0 auto' }}>
            <h2 style={{ marginBottom: '1rem' }}>Your Link Has Expired</h2>
            <p style={{ marginBottom: '1rem', lineHeight: 1.6, fontSize: '1.05rem' }}>
              The 24-hour window to access your discounted membership has passed.
            </p>
            <p style={{ marginBottom: '1.25rem', lineHeight: 1.6 }}>
              Don't worry — simply drop us an email at{' '}
              <a href="mailto:techsupport@belvoircastle.com" style={{ color: 'inherit', fontWeight: 600 }}>
                techsupport@belvoircastle.com
              </a>{' '}
              and we'll be happy to extend the link for you.
            </p>
          </div>
        </LoadingScreen>
      </BookingContainer>
    );
  }

  if (loading && currentStep === 1) {
    return withWellnessTheme(
      <BookingContainer>
        <LoadingScreen>
          <LoadingLogoContainer>
            <LoadingLogo src={navyLogo} alt="Belvoir Castle" />
          </LoadingLogoContainer>
          <LoadingText>Preparing your booking</LoadingText>
          <LoadingStrapline>
            {isWellnessBooking
              ? 'Belvoir Wellness — please wait a moment'
              : 'Belvoir Castle — please wait a moment'}
          </LoadingStrapline>
          <LoadingTrack>
            <LoadingBar />
          </LoadingTrack>
        </LoadingScreen>
      </BookingContainer>
    );
  }

  return withWellnessTheme(
    <BookingContainer>
      {/* Queue Screen — high-demand urgency overlay for Pirate/Circus event */}
      {ENABLE_PIRATE_CIRCUS_QUEUE_SCREEN && showQueue && (
        <QueueScreen onComplete={() => setShowQueue(false)} />
      )}

      {/* Processing Overlay - shows on top of everything when processing payment */}
      {isProcessingPayment && (
        <LoadingScreen style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, minHeight: '100vh' }}>
          <LoadingLogoContainer>
            <LoadingLogo src={navyLogo} alt="Belvoir Castle" />
          </LoadingLogoContainer>
          <LoadingText>Confirming your booking</LoadingText>
          <LoadingStrapline>Securing your visit — this won't take long</LoadingStrapline>
          <LoadingTrack>
            <LoadingBar />
          </LoadingTrack>
        </LoadingScreen>
      )}
      
      <ParallaxBanner
        welcomeText={
          isWellnessBooking
            ? 'Sunday 20 September 2026'
            : isHalloweenBooking
            ? 'October School Holidays 2026'
            : isSafariBooking
              ? '13 July – 1 September 2026'
              : 'Book online'
        }
        title={
          isWellnessTrailBooking
            ? 'Sensory Tree Trail Tours'
            : isWellnessBooking
              ? 'Belvoir Wellness'
              : isHalloweenBooking
                ? 'Book Halloween'
                : isSafariBooking
                  ? 'Belvoir Summer Safari'
                  : isDogParkBooking
                    ? 'Book the Dog Park'
                    : 'Book your visit'
        }
        subtitle={
          isWellnessTrailBooking
            ? 'A DAY OF DISCOVERY · WOODLAND WALK · BOOKING ESSENTIAL'
            : isWellnessBooking
              ? 'A DAY OF DISCOVERY · CASTLE · STUDIO · GARDENS'
              : isHalloweenBooking
                ? 'GARDENS & PLAYGROUND · HALLOWEEN TRAIL · ANIMAL PARK INCLUDED'
                : isSafariBooking
                  ? (isSafariCastle ? 'CASTLE ENTRY · ANIMAL PARK INCLUDED' : 'GARDENS & PLAYGROUND · ANIMAL PARK INCLUDED')
                  : isDogParkBooking
                    ? 'OFF-LEAD SPACE · BOOK YOUR SLOT'
                    : 'YOUR CASTLE, YOUR CROWD, YOUR SAVING'
        }
        backgroundImage={
          isWellnessTrailBooking
            ? WELLNESS_TRAIL_BANNER_CDN
            : isWellnessBooking
              ? WELLNESS_BANNER_CDN
              : isHalloweenBooking
                ? HALLOWEEN_BANNER_CDN
                : isSafariBooking
                  ? safariHeaderImg
                  : isDogParkBooking
                    ? dogParkBannerImg
                    : BOOKING_JOURNEY_BANNER_CDN
        }
        imagePosition={isDogParkBooking ? 'center 18%' : undefined}
      />

      <BookingIntroSection $wellness={isWellnessBooking}>
        <BookingIntroInner>
          <BookingIntroEyebrow $wellness={isWellnessBooking}>
            {isWellnessBooking
              ? 'Mind, Body, Belvoir'
              : isHalloweenBooking || isSafariBooking || isDogParkBooking
                ? 'Secure booking'
                : 'Secure checkout'}
          </BookingIntroEyebrow>
          <BookingIntroSubtitle $wellness={isWellnessBooking}>
            {isWellnessTrailBooking
              ? 'Booking Sensory Tree Trail Tours — Sunday 20 September, 11am or 2pm. Free, booking essential. Sensible footwear recommended. Please be aware that due to steps, inclines and uneven terrain, wheelchair and buggy access is not possible.'
              : isWellnessBooking
                ? 'Booking Belvoir Wellness — A Day of Discovery. Castle sessions at regular admission; wellness activities are free. Complete your details below.'
                : isHalloweenBooking
                  ? 'Booking Gardens & Playground Entry — Halloween adventure trail, Animal Park & Curse Breaker treat included. Complete your details below.'
                  : isSafariBooking
                    ? `Booking ${isSafariCastle ? 'Castle Entry' : 'Gardens & Playground Entry'} — Safari Trail, Animal Park & ice cream reward included. Complete your details below.`
                    : isDogParkBooking
                      ? 'Book a Dog Park slot, then add an extra dog if you need to. Your selection and payment are processed securely.'
                      : 'Complete your booking in a few steps — your selection and payment are processed securely.'}
          </BookingIntroSubtitle>
        </BookingIntroInner>
      </BookingIntroSection>

      <BookingContent ref={topRef}>
        <ProgressContainer $wellness={isWellnessBooking}>
          <ProgressHeader $wellness={isWellnessBooking}>
            <h1>{getStepTitle()}</h1>
            <p>{getStepDescription()}</p>
          </ProgressHeader>

          {/* Mobile compact progress: bar + step count */}
          {(() => {
            const allSteps = isDogParkBooking
              ? [2, 3, 4, 5]
              : (isMembershipJourney || NO_UPSELL_PAGE_IDS.includes(pageId))
              ? [1, 4, 5]
              : [1, 2, 3, 4, 5];
            const totalSteps = allSteps.length;
            const activeIndex = allSteps.indexOf(currentStep);
            const displayIndex = activeIndex >= 0 ? activeIndex : 0;
            const pct = totalSteps > 1
              ? Math.round((displayIndex / (totalSteps - 1)) * 100)
              : 100;
            return (
              <ProgressTrack>
                <ProgressBarRail>
                  <ProgressBarFill $pct={pct} $safari={isSafariBooking} $wellness={isWellnessBooking} />
                </ProgressBarRail>
                <ProgressMobileLabel>
                  <ProgressMobileStep>{getStepTitle()}</ProgressMobileStep>
                  <ProgressMobileCount>
                    {displayIndex + 1} of {totalSteps}
                  </ProgressMobileCount>
                </ProgressMobileLabel>
              </ProgressTrack>
            );
          })()}

          {/* Desktop segmented stepper */}
          <ProgressSteps>
            {(isDogParkBooking
              ? [
                  { step: 2, label: 'Date & Time' },
                  { step: 3, label: 'Extras' },
                  { step: 4, label: 'Details' },
                  { step: 5, label: 'Review' },
                ]
              : (isMembershipJourney || NO_UPSELL_PAGE_IDS.includes(pageId))
              ? [
                  { step: 1, label: isMembershipJourney ? 'Membership' : 'Tickets' },
                  { step: 4, label: 'Details' },
                  { step: 5, label: 'Review' },
                ]
              : [
                  { step: 1, label: 'Tickets' },
                  { step: 2, label: 'Date & Time' },
                  { step: 3, label: 'Extras' },
                  { step: 4, label: 'Details' },
                  { step: 5, label: 'Review' },
                ]
            ).map(({ step, label }) => (
              <ProgressStepNode key={step} $isCompleted={currentStep > step}>
                <ProgressStep key={step} $isActive={currentStep === step}>
                  <StepIcon
                    $isActive={currentStep === step}
                    $isCompleted={currentStep > step}
                  >
                    {currentStep > step ? '✓' : step}
                  </StepIcon>
                  <StepLabel $isActive={currentStep === step} $isCompleted={currentStep > step}>
                    {label}
                  </StepLabel>
                </ProgressStep>
              </ProgressStepNode>
            ))}
          </ProgressSteps>
        </ProgressContainer>

        {error && (
          <ErrorMessage>
            {error}
          </ErrorMessage>
        )}

        <StepContent>
          {renderStepContent()}
        </StepContent>

        {currentStep < 7 && currentStep !== 6 && (
          <NavigationContainer>
            <BackButton 
              onClick={handleBack}
              disabled={isDogParkBooking ? currentStep < 2 : currentStep === 1}
              style={(!isDogParkBooking && currentStep === 2) ? { 
                background: 'rgba(255, 243, 205, 0.85)', 
                color: '#856404',
                borderColor: 'rgba(255, 193, 7, 0.6)',
                fontWeight: '600'
              } : {}}
            >
              {(!isDogParkBooking && currentStep === 2) ? '↺ Restart' : '← Back'}
            </BackButton>
            
            <NextButton 
              onClick={handleNext}
              disabled={!canProceedToNextStep() || loading}
            >
              {loading ? (
                <>⟳ Processing…</>
              ) : (
                <>
                  {currentStep === 5 ? 
                    (() => {
                      const basketFinalPrice = venposBasket?.data?.finalPrice ?? venposBasket?.finalPrice;
                      const calculatedTotal = calculateTotal();
                      const basketDiscountAmount = venposBasket?.data?.discounts?.reduce((sum, discount) => {
                        return sum + (discount.discountedAmount || 0);
                      }, 0) || 0;
                      const basketSubtotal = venposBasket?.data?.totalPrice;
                      const calculatedFromBasket = basketSubtotal ? Math.max(0, basketSubtotal - basketDiscountAmount) : null;
                      const amountNow = (basketFinalPrice !== null && basketFinalPrice !== undefined)
                        ? basketFinalPrice
                        : (calculatedFromBasket !== null && calculatedFromBasket !== undefined)
                          ? Math.max(0, calculatedFromBasket)
                          : Math.max(0, calculatedTotal);
                      return amountNow <= 0 ? 'Confirm Free Booking' : 'Proceed to Payment';
                    })() : 
                    'Continue'
                  } →
                </>
              )}
            </NextButton>
          </NavigationContainer>
        )}

        {currentStep < 7 && currentStep !== 6 && <NavSpacer />}
      </BookingContent>
      
      <PartnersFooter>
        <p>
          Powered by <strong>Vennersys API</strong> • Built by <strong>Belvoir Castle</strong> and <strong>Winters Web</strong>
        </p>
        <p className="strapline">
          Building Tomorrow's Experiences, Today.
        </p>
      </PartnersFooter>
      
      <Footer />
      
      {showRestartModal && (
        <ModalOverlay onClick={() => setShowRestartModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalIcon>🔄</ModalIcon>
            <ModalTitle>Start Fresh?</ModalTitle>
            <ModalText>
              Going back will restart your booking from the beginning to ensure everything works perfectly.
            </ModalText>
            <ModalText style={{ fontSize: '0.95rem', color: '#856404', background: '#fff3cd', padding: '1rem', borderRadius: '8px' }}>
              <strong>What happens:</strong><br/>
              Your ticket selections will be cleared so you can choose again with the best pricing.
            </ModalText>
            <ModalButtons>
              <CancelModalButton onClick={() => setShowRestartModal(false)}>
                Stay Here
              </CancelModalButton>
              <ConfirmModalButton onClick={handleRestartJourney}>
                Restart Booking
              </ConfirmModalButton>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}
      
      {showFreeAnimalParkReminderModal && (
        <ModalOverlay onClick={() => setShowFreeAnimalParkReminderModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            {(() => {
              const visitDate = selectedDate ? new Date(selectedDate) : null;
              const isInSafariPeriod = visitDate && visitDate >= new Date('2026-07-10') && visitDate <= new Date('2026-09-01');
              const showSafariCopy = isSafariBooking || isInSafariPeriod;
              return (
                <>
                  <ModalIcon style={{ background: showSafariCopy ? 'linear-gradient(135deg, #0C2C1B 0%, #1B5E3B 55%, #C8861A 100%)' : 'linear-gradient(135deg, #1a3a52 0%, #2c5282 55%, #9d6d49 100%)' }}>
                    🦙
                  </ModalIcon>
                  <ModalTitle>
                    {showSafariCopy ? "Don't forget your free Animal Park" : 'Free Animal Park is still available'}
                  </ModalTitle>
                  <ModalText>
                    {showSafariCopy
                      ? <>Your visit includes <strong>free Animal Park</strong> entry as part of Belvoir Summer Safari — add it now so it's on your booking.</>
                      : <>You have added Castle &amp; Gardens entry but have not added <strong>free Animal Park</strong> entry to your booking yet.</>}
                  </ModalText>
                </>
              );
            })()}
            {(() => {
              const _vd = selectedDate ? new Date(selectedDate) : null;
              const _safari = isSafariBooking || (_vd && _vd >= new Date('2026-07-10') && _vd <= new Date('2026-09-01'));
              return (
                <ModalText style={{
                  fontSize: '0.95rem',
                  color: '#101d40',
                  background: _safari ? 'rgba(12,44,27,0.06)' : '#f8f7f4',
                  border: _safari ? '2px solid rgba(200,134,26,0.4)' : '2px solid rgba(157, 109, 73, 0.45)',
                  padding: '1rem 1.15rem',
                  borderRadius: '12px',
                  marginBottom: '1.25rem'
                }}>
                  Stay on this step and open the <strong>Animal Park</strong> card to add it for free, or continue without it if you prefer.
                </ModalText>
              );
            })()}
            <ModalButtons>
              <CancelModalButton
                type="button"
                onClick={() => {
                  setShowFreeAnimalParkReminderModal(false);
                  setOpenAnimalParkUpsellNonce(n => n + 1);
                }}
              >
                Stay — add Animal Park
              </CancelModalButton>
              <ConfirmModalButton
                type="button"
                onClick={() => {
                  setShowFreeAnimalParkReminderModal(false);
                  handleNext({ skipAnimalParkReminder: true });
                }}
              >
                Continue without Animal Park
              </ConfirmModalButton>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}

      {showCircusSkipReminderModal && (
        <ModalOverlay onClick={() => setShowCircusSkipReminderModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalIcon style={{ background: 'linear-gradient(135deg, #6b21a8 0%, #9d6d49 55%, #c084fc 100%)' }}>
              🎪
            </ModalIcon>
            <ModalTitle>Are you sure?</ModalTitle>
            <ModalText>
              Are you sure you don&apos;t want to add the <strong>Circus</strong> to your visit? You can still add it now from this page.
            </ModalText>
            <ModalText style={{
              fontSize: '0.95rem',
              color: '#101d40',
              background: '#f8f7f4',
              border: '2px solid rgba(157, 109, 73, 0.45)',
              padding: '1rem 1.15rem',
              borderRadius: '12px',
              marginBottom: '1.25rem'
            }}>
              Stay on this step and open the <strong>Circus</strong> card to add tickets, or continue without the Circus if you prefer.
            </ModalText>
            <ModalButtons>
              <CancelModalButton
                type="button"
                onClick={() => {
                  setShowCircusSkipReminderModal(false);
                  setOpenCircusUpsellNonce(n => n + 1);
                }}
              >
                Stay — add Circus
              </CancelModalButton>
              <ConfirmModalButton
                type="button"
                onClick={() => {
                  setShowCircusSkipReminderModal(false);
                  handleNext({
                    skipCircusReminder: true,
                    skipAnimalParkReminder: true,
                  });
                }}
              >
                Continue without Circus
              </ConfirmModalButton>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}

      {showDateTimeChangeModal && (
        <ModalOverlay onClick={() => setShowDateTimeChangeModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalIcon style={{ background: 'linear-gradient(135deg, #fff3cd, #ffeaa7)' }}>⚠️</ModalIcon>
            <ModalTitle>Change Your {pendingDateTimeChange?.type === 'date' ? 'Visit Date' : 'Entry Time'}?</ModalTitle>
            <ModalText>
              Changing your {pendingDateTimeChange?.type === 'date' ? 'visit date' : 'entry time'} will remove the following activities from your basket:
            </ModalText>
            <div style={{ 
              background: 'white',
              border: '2px solid #9d6d49',
              borderRadius: '12px', 
              padding: '1.25rem', 
              marginBottom: '1.5rem'
            }}>
              <ul style={{ 
                margin: '0', 
                paddingLeft: '1.5rem',
                color: '#101d40',
                fontSize: '1rem',
                lineHeight: '2'
              }}>
                {selectedTickets
                  .filter(t => t.isUpsellItem || t.pageType === 'EventTemplate')
                  .map((ticket, index) => (
                    <li key={index}>
                      <strong>{ticket.name}</strong>
                      {ticket.selectedEventTime?.time && (
                        <span style={{ color: '#666', fontWeight: 'normal' }}> at {ticket.selectedEventTime.time}</span>
                      )}
                    </li>
                  ))
                }
              </ul>
            </div>
            <ModalText style={{ fontSize: '1rem', color: '#666', lineHeight: '1.6' }}>
              Don't worry - you can add activities again once you've selected your new {pendingDateTimeChange?.type === 'date' ? 'date' : 'entry time'}.
            </ModalText>
            <ModalButtons>
              <CancelModalButton onClick={() => {
                setShowDateTimeChangeModal(false);
                setPendingDateTimeChange(null);
              }}>
                Keep Current {pendingDateTimeChange?.type === 'date' ? 'Date' : 'Time'}
              </CancelModalButton>
              <ConfirmModalButton onClick={handleConfirmDateTimeChange}>
                Confirm Change
              </ConfirmModalButton>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}
    </BookingContainer>
  );
};

export default BookingJourney;